/**
 * Chat widget -- public entry point.
 *
 * Renders a floating launcher button + slide-in chat drawer inside Shadow DOM
 * for CSS isolation. Handles streaming NDJSON from the backend.
 */

import type {
  ActionPayload,
  AddToCartParams,
  ChatOpenActionOptions,
  ChatOpenDetail,
  ChatOpenOptions,
  ChatOpenSource,
  PageContext,
  StreamEvent,
  StreamEventAction,
  UIElement,
  UISpec,
} from '../common/types.js';
import type { ChatTransportConfig } from '../common/api-paths.js';
import type { ActionRouterOptions } from '../common/action-router.js';
import type { UISpecRenderHelpers } from '../common/renderer/index.js';
import type { BridgeMessage, CommunicationBridgeOptions } from '../common/communication-bridge.js';
import type { BackendRequestMeta } from './api.js';
import { mergeUISpecRegistry } from '../common/renderer/index.js';
import { BaseWidget } from '../common/widget-base.js';
import { dispatch } from '../common/events.js';
import { uuidv7 } from '../common/uuidv7.js';
import { CommunicationBridge } from '../common/communication-bridge.js';
import { buildCommittedHostEffectReceipt, normalizeAddToCartHostResult } from '../common/addToCartOutcome.js';
import { routeStreamAction } from '../common/action-router.js';
import { sanitizeHtml, isSafeUrl } from '../common/safe-html.js';
import { navigateToUrl } from '../common/navigation.js';
import { resolveLocaleTag } from '../common/locale.js';
import { debugLog } from '../common/debug.js';
import { escapeCssIdentifier } from '../common/css-escape.js';
import { validateImageFile } from './attachment-utils.js';
import { sendChatMessage, enrichActionPayload } from './api.js';
import { ChatDrawer } from './components/ChatDrawer.js';
import { AssistantModeController } from './features/beauty-consulting/mode-controller.js';
import {
  createBeautyStreamState,
  handleBeautyUISpec,
  isPhotoAnalysisMessage,
  flushBeautyStreamComplete,
  flushBeautyStreamError,
} from './features/beauty-consulting/stream-handler.js';
import {
  detectConsultingGrid,
  isConsultingGridReady,
  patchConsultingGridDom,
} from './features/beauty-consulting/consulting-grid.js';
import { createLauncher } from './components/Launcher.js';
import type { LauncherElements } from './components/Launcher.js';
import {
  CHAT_CONVERSATION_RESET_EVENT,
  type ChatConversationResetDetail,
  type ChatConversationResetReason,
} from './conversation-lifecycle.js';
import { createFloatingLauncher } from './components/FloatingLauncher.js';
import type { FloatingLauncherKit } from './components/FloatingLauncher.js';
import { createInlineLauncher } from './components/InlineLauncher.js';
import type { InlineLauncherKit } from './components/InlineLauncher.js';
import { playTtsAudio } from '../common/tts-player.js';
import type { AudioHandle } from '../common/tts-player.js';
import {
  renderUISpec,
  createDefaultChatUISpecRegistry,
  defaultChatUnknownUISpecRenderer,
} from './components/renderUISpec.js';
import { renderFloatingComparisonButton } from './components/FloatingComparisonButton.js';
import { FRONTEND_PANEL_RESTORE_ACTION } from './components/PanelRestoreCard.js';
import type { TypewriterHandle } from './components/typewriter.js';
import { typewriteHtml } from './components/typewriter.js';
import { linkProductMentions } from './components/productMentionLinker.js';
import type { ProductMention } from './components/productMentionLinker.js';
import {
  clearChoicePrompterDismissState,
  createChoicePrompter,
  isChoicePrompterDismissed,
  recordChoicePrompterDismissedForThread,
} from './components/ChoicePrompter.js';
import type {
  ChatActionChip,
  OpeningContextKey,
  ChatWidgetConfig,
  ChatMessage,
  ChatI18n,
  ChatUISpecRenderContext,
  ChatUISpecRegistry,
  ProductSortState,
} from './types.js';
import { GengageIndexedDB } from '../common/indexed-db.js';
import { CHAT_I18N_TR, resolveChatLocale } from './locales/index.js';
import { ExtendedModeManager } from './extendedModeManager.js';
import { PanelManager, determinePanelUpdateAction, type PanelSource, type PanelUpdateAction } from './panel-manager.js';
import { ChatHistoryStorage, type ChatHistoryIndexEntry, type ChatHistorySessionRecord } from './history-storage.js';
import { SessionPersistence } from './session-persistence.js';
import { ChatPresentationState, getLatestUnreadAssistantThreadId } from './chat-presentation-state.js';
import { invalidateChatScrollCache } from './utils/get-chat-scroll-element.js';
import { getGlobalErrorMessage, isLikelyConnectivityIssue } from '../common/global-error-toast.js';
import { makePillLauncher } from '../common/pill-launcher.js';
import { detectFastIntent } from '../common/fastIntent.js';
import { shouldShowStreamErrorAsRedStrip } from './stream-error-display.js';
import {
  containsKvkk,
  isKvkkShown,
  markKvkkShown,
  stripKvkkBlock,
  extractKvkkBlock,
  localeToOutputLanguage,
} from './kvkk.js';

import chatStyles from './components/chat.css?inline';
import * as ga from '../common/ga-datalayer.js';

const CONTEXT_LAUNCH_TYPES = new Set(['launchSingleProduct', 'launchProductList', 'launchHomepage']);
const MESSAGE_UI_SPEC_COMPONENTS = new Set(['PanelRestoreCard']);
const PRIMARY_ANSWER_UI_SPEC_COMPONENTS = new Set(['ComparisonTable', 'ProductDetailsPanel']);

function getPrimaryAnswerComponentType(rootElement: UIElement | undefined): string | undefined {
  const componentType = rootElement?.type;
  if (!componentType) return undefined;
  if (PRIMARY_ANSWER_UI_SPEC_COMPONENTS.has(componentType)) return componentType;
  if (componentType !== 'ProductGrid') return undefined;
  return rootElement.props?.['similarsAppend'] === true ? undefined : componentType;
}

function hasRenderableText(html: string): boolean {
  if (!html.trim()) return false;
  const template = document.createElement('template');
  template.innerHTML = sanitizeHtml(html);
  return (template.content.textContent ?? '').trim().length > 0;
}

type SendActionOptions = {
  silent?: boolean;
  attachment?: File;
  preservePanel?: boolean;
  isContextPrime?: boolean;
  preservePills?: boolean;
  skipFullSheet?: boolean;
};

type StoredThreadPill = {
  label: string;
  action: ActionPayload;
  icon?: string;
  image?: string;
  description?: string;
};

/** Validate that a string is a safe CSS color value (no url(), expression(), etc.). */
function isSafeCSSColor(value: string): boolean {
  if (value.length > 120) return false;
  // Only allow characters found in valid CSS colors: hex digits, letters, parens,
  // commas, dots, spaces, percent, slash (modern color syntax), and hyphens.
  return /^[a-zA-Z0-9#(),.\s%/\-]+$/.test(value);
}

/** Lightweight runtime check: value looks like an ActionPayload (has a string `type`). */
function isActionLike(value: unknown): value is ActionPayload {
  return typeof value === 'object' && value !== null && typeof (value as Record<string, unknown>).type === 'string';
}

function dispatchConversationReset(reason: ChatConversationResetReason): void {
  // otokoc-assistants is the initial downstream listener, but this remains an
  // account-agnostic SDK event for both always-latest CSH and SSH integrations.
  window.dispatchEvent(
    new CustomEvent<ChatConversationResetDetail>(CHAT_CONVERSATION_RESET_EVENT, {
      detail: { reason },
      bubbles: false,
    }),
  );
}

import type { AssistantMode } from './assistant-mode.js';
import { asRecord } from './assistant-mode.js';

/**
 * Floating AI chat widget with streaming NDJSON responses, product cards, and comparison tables.
 *
 * @example
 * ```ts
 * import { GengageChat } from './frontend/chatfe/chat/index.js';
 * import { bootstrapSession } from './frontend/chatfe/common/context.js';
 *
 * const chat = new GengageChat();
 * await chat.init({
 *   accountId: 'mystore',
 *   middlewareUrl: '<backend service location provided for your Gengage account>',
 *   session: { sessionId: bootstrapSession() },
 * });
 * chat.open(); // Programmatically open the drawer
 * ```
 */
export function isSimilarsAppendGrid(element: UIElement | undefined): boolean {
  return element?.type === 'ProductGrid' && element.props?.['similarsAppend'] === true;
}

export class GengageChat extends BaseWidget<ChatWidgetConfig> {
  private _pendingOpenSource: ChatOpenSource = 'programmatic';
  private static readonly _MAX_COMPARISON_SELECTION = 5;
  private _shadow: ShadowRoot | null = null;
  private _rootEl: HTMLElement | null = null;
  /** Full-viewport scrim when drawer is open (floating/overlay); not used for inline. */
  private _backdropEl: HTMLElement | null = null;
  private _launcher: LauncherElements | null = null;
  private _floatingLauncher: FloatingLauncherKit | null = null;
  private _inlineLauncher: InlineLauncherKit | null = null;
  private _inlineLauncherBootstrapPending = false;
  private _drawer: ChatDrawer | null = null;
  private _bridge: CommunicationBridge | null = null;
  private _drawerVisible = false;
  /** Set when `config.pillLauncher` is used — `apply()` runs at end of `onInit`. */
  private _pillLauncherApply: (() => Promise<void>) | null = null;
  /**
   * Host scroll blocked via capture-phase `touchmove`/`wheel` + preventDefault on `window` and
   * `document`, `scroll` pin for the viewport, temporary `overflow` / `overscroll-behavior` on
   * `html`/`body` (restored on release).
   */
  private _hostScrollLockActive = false;
  private _hostOverflowRestore: {
    htmlOverflow: string;
    bodyOverflow: string;
    htmlOverscroll: string;
    bodyOverscroll: string;
  } | null = null;
  private _hostOverlayRestore: Array<{ name: string; value: string; priority: string }> | null = null;
  /** Viewport scroll position captured when the lock starts (Windows / nested layouts). */
  private _hostScrollLockViewport: { x: number; y: number } | null = null;
  private _hostScrollPinning = false;
  private readonly _preventHostDocumentTouchMove = (e: TouchEvent): void => {
    if (!this._hostScrollLockActive) return;
    if (this._hostScrollEventShouldReachChatScroller(e)) return;
    e.preventDefault();
  };
  private readonly _preventHostDocumentWheel = (e: WheelEvent): void => {
    if (!this._hostScrollLockActive) return;
    const path = e.composedPath();
    if (!path.length || !path.includes(this.root)) {
      e.preventDefault();
      return;
    }
    const target = this._chatScrollTargetFromPath(path);
    if (!target) {
      e.preventDefault();
      return;
    }
    const { el, vertical, horizontal } = target;
    const canScrollY = vertical && e.deltaY !== 0;
    const canScrollX = horizontal && e.deltaX !== 0;
    if (!canScrollY && !canScrollX) {
      e.preventDefault();
      return;
    }
    // Pure vertical wheel on a vertically scrollable chat scroller: native scroll
    // (manual scrollTop + scroll-behavior:smooth felt ~5× slower under scroll lock).
    if (canScrollY && !canScrollX) {
      return;
    }
    e.preventDefault();
    if (canScrollY) {
      const maxScroll = el.scrollHeight - el.clientHeight;
      const delta = this._normalizeWheelDeltaY(e, el);
      el.scrollTop = Math.max(0, Math.min(maxScroll, el.scrollTop + delta));
    }
    if (canScrollX) {
      const maxScroll = el.scrollWidth - el.clientWidth;
      const delta = this._normalizeWheelDeltaX(e, el);
      el.scrollLeft = Math.max(0, Math.min(maxScroll, el.scrollLeft + delta));
    }
  };
  private readonly _pinHostViewportScroll = (): void => {
    if (!this._hostScrollLockActive || !this._hostScrollLockViewport || this._hostScrollPinning) return;
    // On mobile, iOS scrolls the visual viewport when the keyboard opens to bring the focused
    // input into view. Calling scrollTo() here to fight that scroll creates an oscillation loop
    // (wobble). The visualViewport handler compensates the drawer position instead, so we skip
    // the pin whenever the keyboard appears to be open (visualViewport shorter than innerHeight).
    if (this._isMobileViewport && window.visualViewport && window.visualViewport.height < window.innerHeight) {
      return;
    }
    const t = this._hostScrollLockViewport;
    if (window.scrollX === t.x && window.scrollY === t.y) return;
    this._hostScrollPinning = true;
    window.scrollTo(t.x, t.y);
    this._hostScrollPinning = false;
  };
  private _messages: ChatMessage[] = [];
  private _ephemeralWelcomeMessageId: string | null = null;
  // Bot text accumulation is now closure-local inside _sendAction to prevent
  // corruption when concurrent preservePanel streams write simultaneously.
  private _currentMessageId = 0;
  private _abortControllers = new Set<AbortController>();
  /** Current thread cursor — only messages with threadId <= this are visible. */
  private _currentThreadId: string | null = null;
  /** Most recent threadId ever created — used to detect branch points. */
  private _lastThreadId: string | null = null;
  /** Timestamp when the chat session was created (ISO 8601). */
  private _chatCreatedAt = '';
  /** Last backend-streamed context object — sent back with every request. */
  private _lastBackendContext: import('../common/types.js').BackendContext | null = null;
  private _productSort: ProductSortState = { type: 'related' };
  private _lastSku: string | undefined;
  private _lastPageType: string | undefined;
  private _lastSkuListKey: string | undefined;
  private _lastPageRouteKey: string | undefined;
  private _lastCategoryLeaf: string | undefined;
  private _comparisonSelectMode = false;
  private _comparisonSelectedSkus: string[] = [];
  private _comparisonSelectionWarning: string | null = null;
  private _comparisonRefreshRafId: number | null = null;
  private _openContextPrimeRafId: number | null = null;
  private _openContextPrimeTimerId: number | null = null;
  private _openActionRafId: number | null = null;
  private _openActionTimerId: number | null = null;
  private _pendingOpenActions: Array<{ action: ActionPayload; options: SendActionOptions }> = [];
  private _lastAppliedKeyboardInset = 0;
  private _syncVisualVh: (() => void) | null = null;
  /** SKUs of products the user has viewed across panel product grids. */
  private _viewedProductSkus = new Set<string>();
  /** Latest AI analysis UISpec (AITopPicks/AIGroupingCards) per thread for panel restore. */
  private _panelAiSpecByThread = new Map<string, UISpec>();
  /** Suggestion pills captured per thread so rollback can restore them. */
  private _threadPillsByThreadId = new Map<string, StoredThreadPill[]>();
  private _choicePrompterEl: HTMLElement | null = null;
  private _openState: 'full' | 'half' = 'full';
  private _mobileBreakpoint = 768;
  private _isMobileViewport = false;
  /** GA: previous MainPane expanded state for `gengage-chatbot-maximized` edge detection. */
  private _gaPrevMainPaneExpanded = false;
  private _pdpLaunched = false;
  private _plpLaunched = false;
  private _homepageLaunched = false;
  /**
   * True ONLY when this widget actually restored a non-empty thread that now owns the
   * surface. When set, the generic page-context auto-launch (homepage/PLP greeting) is
   * suppressed so the restored thread — and its in-flight continuation — are not
   * clobbered by a "Merhaba!" welcome. See R1 (yataş store-finder nav clobber).
   *
   * INVARIANT (regression #1 fix): this flag is tied to ACTUAL restored on-screen
   * content, never to mere restore-KEY presence. If a restore no-ops (stale/missing
   * session, cross-SKU guard, IDB absent, empty thread, rejected freshness token) the
   * flag stays/returns false so the NORMAL greeting/auto-launch fires — the surface is
   * never left blank.
   */
  private _restorePending = false;
  /** One-shot gate for an assistant-driven soft navigation update in the same document. */
  private _assistantSoftNavigationPending = false;
  private _assistantSoftNavigationTimer: number | null = null;
  private _entryContextPrimed = false;
  /** True while a silent context-prime launch (PDP/PLP/homepage) is in flight. */
  private _contextPrimingInFlight = false;
  /** User messages queued until context priming completes. */
  private _queuedUserMessages: Array<{ text: string; attachment?: File }> = [];
  private _productContextUnavailableSku: string | null = null;
  private _i18n: ChatI18n = CHAT_I18N_TR;
  private _extendedModeManager: ExtendedModeManager | null = null;
  /** Active typewriter animation handle — cancelled on new action or drawer close. */
  private _activeTypewriter: TypewriterHandle | null = null;
  /** Active TTS audio handle — stopped on new stream start or drawer close. */
  private _activeTtsHandle: AudioHandle | null = null;
  /** Active request thread ID — guards against stale stream events from cancelled requests. */
  private _activeRequestThreadId: string | null = null;
  /** Accumulated SKU → product item map from outputText events. */
  private _skuToProductItem: Record<string, Record<string, unknown>> = {};
  /** Whether initialization (including IDB restore) has completed. */
  private _initComplete = false;
  /** Queue of actions received before init completes. Max 10, FIFO discard. */
  private _pendingActions: Array<{
    action: ActionPayload;
    options?: SendActionOptions | undefined;
  }> = [];
  /** Supplemental context received from host via bridge (e.g. PDP detail context). */
  private _bridgeContext: Record<string, unknown> | null = null;
  private _threadsWithFirstBot: Set<string> = new Set();
  /** Panel state manager (snapshots, topbar, navigation). */
  private _panel: PanelManager | null = null;
  /** Client-side panel navigation stack for local drilldowns (e.g. card → detail). Max 10 entries.
   *  Stores rebuild info (UISpec or kind) instead of DOM clones so back navigation
   *  produces fresh elements with live event listeners. */
  private _localPanelHistory: Array<{
    source: PanelSource;
    title: string;
  }> = [];
  private static readonly _MAX_PANEL_HISTORY = 10;
  /** Tracks how the current panel content was produced, for history/error-recovery rebuild. */
  private _currentPanelSource: PanelSource | null = null;
  /** Latest rendered single-product record shown inline on PDP, reused when the panel has no active PDP detail. */
  private _currentInlineProductSummaryRecord: Record<string, unknown> | null = null;
  /** IndexedDB session persistence manager. */
  private _session: SessionPersistence | null = null;
  /** LocalStorage-backed chat history manager. */
  private _historyStorage: ChatHistoryStorage | null = null;
  /** Active local history session ID for the current conversation. */
  private _activeHistorySessionId: string | null = null;
  /** Transcript focus, pin-to-bottom, and scroll request coordination. */
  private readonly _presentation = new ChatPresentationState();
  private readonly _presentationScrollTimerIds = new Set<ReturnType<typeof setTimeout>>();
  /** Registered event callbacks (GA4 event hooks). Key = event name, value = set of callbacks. */
  private _eventCallbacks = new Map<string, Set<(detail: Record<string, unknown>) => boolean | Promise<boolean>>>();
  private _pendingAddToCartKeys = new Set<string>();
  /** Consecutive identical error counter for account-inactive detection. */
  private _consecutiveErrorCount = 0;
  /** Last error message text for deduplication. */
  private _lastErrorMessage = '';
  private _launcherAnchorCleanup: (() => void) | null = null;
  private _modeController = new AssistantModeController();
  /** @deprecated Alias for backward compat in tests. Use _modeController.mode. */
  get _assistantMode(): AssistantMode {
    return this._modeController.mode;
  }
  set _assistantMode(value: AssistantMode) {
    this._modeController.mode = value;
  }
  /** @deprecated Alias for backward compat in tests. Use _modeController.uiHints. */
  get _uiHints(): Record<string, unknown> | null {
    return this._modeController.uiHints;
  }
  set _uiHints(value: Record<string, unknown> | null) {
    this._modeController.uiHints = value;
  }

  protected async onInit(config: ChatWidgetConfig): Promise<void> {
    this._i18n = this._resolveI18n(config);
    this._chatCreatedAt = new Date().toISOString();

    if (config.pillLauncher) {
      const kit = makePillLauncher(config.pillLauncher);
      config.launcherImageUrl = kit.launcherImageUrl;
      if (config.headerAvatarUrl === undefined) {
        config.headerAvatarUrl = config.pillLauncher.avatarUrl;
      }
      this._pillLauncherApply = () => kit.apply(this._shadow ?? undefined);
    }

    // Create Shadow DOM for CSS isolation
    this._shadow = this.root.attachShadow({ mode: 'open' });

    // Inject styles
    const style = document.createElement('style');
    style.textContent = chatStyles;
    this._shadow.appendChild(style);

    // Apply theme CSS custom properties on the shadow host so they cascade into the shadow tree.
    // Priority: explicit config fields > theme object entries > CSS defaults.
    const host = this.root as HTMLElement;
    const applyVar = (cssVar: string, value: string | undefined): void => {
      if (value && isSafeCSSColor(value)) host.style.setProperty(cssVar, value);
    };
    // Explicit header color shortcuts
    applyVar('--gengage-chat-header-bg', config.headerBg);
    applyVar('--gengage-chat-header-foreground', config.headerForeground);
    // Arbitrary theme tokens (e.g. theme['--gengage-primary-color'])
    if (config.theme) {
      for (const [key, value] of Object.entries(config.theme)) {
        if (key.startsWith('--') && typeof value === 'string') {
          applyVar(key, value);
        }
      }
    }

    this._applyDiscountedPriceColorVar(config.productPriceUi?.discountedPriceColor);

    // Create root container
    const rootEl = document.createElement('div');
    rootEl.className = 'gengage-chat-root';
    rootEl.lang = resolveLocaleTag(config.locale);
    this._rootEl = rootEl;
    this._shadow.appendChild(rootEl);

    const variant = config.variant ?? 'floating';
    if (variant === 'inline') {
      rootEl.classList.add('gengage-chat--inline');
    }

    // Dim page + click outside to close (not for inline — drawer is embedded)
    if (variant !== 'inline') {
      const backdropEl = document.createElement('div');
      backdropEl.className = 'gengage-chat-backdrop';
      backdropEl.setAttribute('aria-hidden', 'true');
      backdropEl.setAttribute('role', 'button');
      backdropEl.setAttribute('tabindex', '-1');
      backdropEl.setAttribute('aria-label', this._i18n.closeAriaLabel);
      backdropEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
      this._backdropEl = backdropEl;
      rootEl.prepend(backdropEl);
    }

    // Create launcher (floating variant only — inline/overlay are triggered programmatically)
    if (variant === 'floating') {
      if (config.floatingLauncher) {
        this._floatingLauncher = createFloatingLauncher(
          config.floatingLauncher,
          {
            onSend: (text) => {
              this.open({ initialMessage: text, source: 'launcher' });
              this._floatingLauncher?.showOpenChatButton();
            },
            onOpenChat: () => this.open({ source: 'launcher' }),
          },
          this._shadow,
        );
        rootEl.appendChild(this._floatingLauncher.container);
      } else {
        const launcherOpts: import('./components/Launcher.js').LauncherOptions = {
          onClick: () => this.open({ source: 'launcher' }),
          ariaLabel: this._i18n.openButton,
        };
        if (config.launcherImageUrl !== undefined) launcherOpts.imageUrl = config.launcherImageUrl;
        else if (config.launcherSvg !== undefined) launcherOpts.svgMarkup = config.launcherSvg;
        if (config.mobileLauncherImageUrl !== undefined) launcherOpts.mobileImageUrl = config.mobileLauncherImageUrl;
        if (config.hideMobileLauncher !== undefined) launcherOpts.hideMobile = config.hideMobileLauncher;
        if (config.mobileBreakpoint !== undefined) launcherOpts.mobileBreakpoint = config.mobileBreakpoint;
        if (config.launcherTooltip !== undefined) launcherOpts.tooltip = config.launcherTooltip;
        this._launcher = createLauncher(launcherOpts);
        rootEl.appendChild(this._launcher.container);
      }

      const deferFloatingLauncher =
        config.inlineLauncher !== undefined && config.inlineLauncher.hideFloatingLauncher !== false;
      if (deferFloatingLauncher) {
        this._setInlineBootstrapFloatingLauncherHidden(true);
      }
      this._setupLauncherAnchor(config);
      if (config.inlineLauncher) {
        this._setupInlineLauncher(config, { releaseFloatingLauncherOnFirstSync: deferFloatingLauncher });
      }
    }

    // Overlay variant wraps drawer in a backdrop for full-screen modal
    if (variant === 'overlay') {
      rootEl.classList.add('gengage-chat--overlay');
    }

    // Create drawer (hidden initially for floating/overlay, visible for inline)
    const drawerContainer = document.createElement('div');
    rootEl.appendChild(drawerContainer);

    this._drawer = new ChatDrawer(drawerContainer, {
      i18n: this._i18n,
      onSend: (text, attachment) => this._sendMessage(text, attachment),
      onClose: () => this.close(),
      onAttachment: (file) => this._handleAttachment(file),
      visualSearch: config.visualSearch,
      onPanelToggle: () => {
        this._drawer?.persistPanelState(config.accountId);
      },
      onHostShellSync: () => {
        this._applyOpenStateClasses();
      },
      onRollback: (messageId) => this._handleRollback(messageId),
      onPanelBack: () => this._navigatePanelBack(),
      onPanelForward: () => this._panel?.navigateForward(),
      onPanelClose: () => {
        // Mobile ✕: panel is only hidden (content kept); header reopen stays meaningful.
        if (this._isMobileViewport) {
          this._comparisonSelectMode = false;
          this._comparisonSelectedSkus = [];
          return;
        }
        this._localPanelHistory = [];
        this._comparisonSelectMode = false;
        this._comparisonSelectedSkus = [];
        this._currentPanelSource = null;
      },
      headerTitle: config.headerTitle,
      headerAvatarUrl: config.headerAvatarUrl,
      launcherImageUrl: config.launcherImageUrl,
      headerBadge: config.headerBadge,
      headerCartUrl: config.headerCartUrl,
      footerAttribution: config.footerAttribution,
      showHeaderFavorites: typeof config.onFavoritesClick === 'function' || config.headerFavoritesToggle === true,
      ...(config.headerCartUrl || config.onCartClick
        ? {
            onCartClick: () => {
              if (config.headerCartUrl) {
                this._saveSessionAndOpenURL(config.headerCartUrl);
              } else {
                config.onCartClick?.();
              }
            },
          }
        : {}),
      onFavoritesClick: () => {
        ga.trackLikeList();
        if (typeof config.onFavoritesClick === 'function') {
          config.onFavoritesClick();
          return;
        }
        this._openFavoritesPanel();
      },
      onHistoryClick: () => {
        if (this._currentPanelSource?.kind === 'history') {
          this._navigatePanelBack();
          return;
        }
        this._openHistoryPanel();
      },
      getMobileState: () => this._openState ?? 'full',
      getMobileViewport: () => this._isMobileViewport,
      onMobileSnap: (state) => {
        if (state === 'close') {
          this.close();
        } else {
          this._openState = state;
          this._applyOpenStateClasses();
        }
      },
      onLinkClick: (url) => {
        this._saveSessionAndOpenURL(url);
      },
      renderMessageUISpec: (message) => this._renderMessageUISpec(message),
      voiceEnabled: config.voiceEnabled,
      voiceLang: config.locale
        ? `${config.locale.split('-')[0] ?? 'tr'}-${(config.locale.split('-')[1] ?? config.locale.split('-')[0] ?? 'TR').toUpperCase()}`
        : undefined,
      onNewChat: () => this._startNewChat(),
      presentation: {
        onPinnedToBottomChange: (pinned) => {
          this._presentation.pinnedToBottom = pinned;
        },
        onUserInteractingChange: (interacting) => {
          this._presentation.userInteracting = interacting;
        },
        onFormerMessagesHint: () => {
          if (this._presentation.focusedThreadId && this._hasMultipleThreadIds()) {
            this._drawer?.setFormerMessagesButtonVisible(true);
          }
        },
        shouldBlockSoftAutoScroll: () => this._presentation.shouldBlockStreamAutoScroll(),
        onReleasePresentationFocus: () => this._releasePresentationFocus(),
      },
    });
    this._applyUiHints();

    // Extended mode manager for host PDP maximize/minimize
    this._extendedModeManager = new ExtendedModeManager({
      onChange: (extended) => this._panel?.notifyExtension(extended),
      productDetailsInPanel: (config.isDemoWebsite ?? false) && (config.productDetailsExtended ?? false),
    });

    // Panel manager for snapshot/topbar/navigation state
    this._panel = new PanelManager({
      drawer: () => this._drawer,
      shadow: () => this._shadow,
      currentThreadId: () => this._currentThreadId,
      bridge: () => this._bridge,
      extendedModeManager: () => this._extendedModeManager,
      i18n: () => this._i18n,
      rollbackToThread: (tid) => this._rollbackToThread(tid),
    });

    // Hide drawer initially for floating/overlay variants
    if (variant !== 'inline') {
      this._drawer.getElement().classList.add('gengage-chat-drawer--hidden');
    }

    // Restore panel state from session
    const restoredCollapsedPanel = this._drawer.restorePanelState(config.accountId);

    // Panel mode: 'collapsed' starts hidden, 'expanded' starts open, 'auto' (default) expands when content arrives
    const panelMode = config.panelMode ?? 'auto';
    if (panelMode === 'collapsed') {
      this._drawer.setPanelCollapsed(true);
    } else if (panelMode === 'expanded') {
      this._drawer.setForceExpanded();
    } else if (!restoredCollapsedPanel) {
      // 'auto': panel starts collapsed — will expand when content arrives via stream
      // (prevents empty panel being visible on page load/session restore)
    }

    // Restore session if an explicit handoff signal exists. Two shapes:
    //   - SimRel product navigation: session id + SKU (rich product handoff).
    //   - Assistant-driven page navigation (R1, e.g. store-finder): session id only,
    //     no SKU because the destination is not a product page. The presence of the
    //     restore session id is the durable "assistant initiated this navigation —
    //     keep the thread" marker across the SPA re-mount boundary.
    //
    // Regression #2 fix — freshness guard: a marker only counts as a handoff when it
    // was produced by the IMMEDIATELY-PRECEDING navigation (its freshness token matches
    // the current same-document nav seq). A stale/stranded marker left by an unrelated
    // earlier navigation is REJECTED (and cleared) so it cannot trigger a spurious
    // restore onto an unrelated page. Either way the marker is consumed-or-rejected
    // here so it has exactly one lifecycle and never survives to a later navigation.
    const restoreSessionId = sessionStorage.getItem('gengage_restore_session_id');
    const restoreHistoryId = sessionStorage.getItem('gengage_restore_history_id');
    const markerIsFresh = this._isRestoreMarkerFresh();
    const hasHandoff = !!restoreSessionId && markerIsFresh;
    // Consume/reject the marker NOW — single guaranteed lifecycle. Whether it was a
    // valid handoff or a rejected stale key, it must not survive this mount.
    this._clearRestoreMarker();

    // NOTE: _restorePending is intentionally NOT set here. It is tied to ACTUAL
    // restored content (see invariant on the field). It is set true only after a
    // restore replays a non-empty thread; on every no-op/early-return/failure path it
    // stays false so the normal greeting/auto-launch fires (regression #1 fix).
    this._restorePending = false;

    // IndexedDB persistence (best-effort, non-fatal). It also backs the chat
    // history archive, so it is opened BEFORE the history store is hydrated.
    let idb: GengageIndexedDB | null = null;
    try {
      idb = new GengageIndexedDB();
      await idb.open();
      this._session = new SessionPersistence(idb);
    } catch {
      // IndexedDB unavailable — continue without persistence. Neither an IDB
      // session nor the history archive can be restored; _restorePending stays
      // false so the normal greeting/auto-launch fires (regression #1: the
      // IDB-absent path must never blank the surface).
      idb = null;
      this._session = new SessionPersistence(null);
    }

    // History archive lives in IndexedDB (migrated once from any legacy
    // localStorage on hydrate). With no IDB it degrades to no history.
    this._historyStorage = new ChatHistoryStorage(this.config.accountId, this.config.session?.userId ?? '', idb);
    await this._historyStorage.hydrate();

    // The explicit history-record reopen (gengage_restore_history_id, e.g. a
    // PanelRestoreCard click) is a distinct, user/card-driven handoff and is honored on
    // its own — it does NOT depend on the session-id auto-restore freshness gate. It is
    // still consumed-and-cleared above (_clearRestoreMarker), so it cannot strand.
    const restoredHistory = restoreHistoryId ? await this._historyStorage.loadSession(restoreHistoryId) : null;

    if (restoredHistory) {
      this._activeHistorySessionId = restoredHistory.id;
      this._restorePending = await this._restoreFromHistoryRecord(restoredHistory);
    } else if (idb) {
      // Session-id auto-restore: gated on a FRESH handoff marker (regression #2).
      this._restorePending = await this._restoreFromIndexedDB(hasHandoff);
    }

    // Register public API on window
    this._registerPublicAPI();

    // Apply mobileInitialState if configured
    if (config.mobileInitialState !== undefined) {
      this._openState = config.mobileInitialState;
    }
    this._mobileBreakpoint = config.mobileBreakpoint ?? 768;

    this._syncViewportState();
    const onResize = () => this._syncViewportState();
    window.addEventListener('resize', onResize, { passive: true });
    this.addCleanup(() => window.removeEventListener('resize', onResize));

    // iOS visualViewport keyboard handling:
    // keep drawer height stable and move the whole drawer up by the keyboard overlap so that
    // chat bottom stays attached to keyboard top with no background peek/flicker.
    // Also syncs --gengage-chat-visual-vh so the full-screen drawer matches the actual visual
    // viewport height even when the iOS Chrome bottom toolbar has hidden on scroll.
    if (window.visualViewport) {
      const KEYBOARD_OPEN_THRESHOLD_PX = 90;
      const onViewportChange = () => {
        if (!this._drawerVisible || !this._isMobileViewport) return;
        const el = this._drawer?.getElement();
        if (!el) return;
        const vv = window.visualViewport!;
        // iOS can shift visual viewport down while keyboard is open.
        // Effective bottom overlap = layout viewport height - visual viewport bottom edge.
        const keyboardInset = Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
        const keyboardOpen = keyboardInset > KEYBOARD_OPEN_THRESHOLD_PX;
        // Apply in BOTH mobile states. The full drawer is sized to the visual
        // viewport via --visual-vh/--visual-top (below); the half sheet is
        // bottom-anchored and lifts above the keyboard via --keyboard-inset.
        // Without compensating the half state, the sheet stays pinned to the
        // layout-viewport bottom (which iOS does NOT shrink for the keyboard), so
        // the input/footer hide behind the keyboard and the host page bleeds
        // through the band between the sheet and the keyboard.
        const nextInset = keyboardOpen ? keyboardInset : 0;
        if (nextInset !== this._lastAppliedKeyboardInset) {
          this._lastAppliedKeyboardInset = nextInset;
          el.style.setProperty('--gengage-chat-keyboard-inset', `${nextInset}px`);
        }
        // Sync visual viewport geometry so the drawer tracks the actual visible
        // area: the full drawer uses vv.height/vv.offsetTop for top+height; the
        // half sheet caps its max-height to vv.height so the lifted sheet never
        // runs off the top of the visible area.
        el.style.setProperty('--gengage-chat-visual-vh', `${Math.round(vv.height)}px`);
        el.style.setProperty('--gengage-chat-visual-top', `${Math.round(vv.offsetTop)}px`);
      };
      this._syncVisualVh = () => {
        if (!this._isMobileViewport) return;
        const el = this._drawer?.getElement();
        if (!el || !window.visualViewport) return;
        const vv = window.visualViewport;
        el.style.setProperty('--gengage-chat-visual-vh', `${Math.round(vv.height)}px`);
        el.style.setProperty('--gengage-chat-visual-top', `${Math.round(vv.offsetTop)}px`);
      };
      // Apply synchronously to avoid one-frame stale geometry gaps during keyboard motion.
      window.visualViewport.addEventListener('resize', onViewportChange);
      window.visualViewport.addEventListener('scroll', onViewportChange);
      this.addCleanup(() => {
        window.visualViewport?.removeEventListener('resize', onViewportChange);
        window.visualViewport?.removeEventListener('scroll', onViewportChange);
      });
    }

    // Inline variant starts visible (onShow() is not invoked — mirror presentation state here)
    if (variant === 'inline') {
      this._drawerVisible = true;
      this.isVisible = true;
      this._applyOpenStateClasses();
      this._presentation.setShown(true);
      setTimeout(() => this._maybeAutoAnchorUnreadAssistant(), 60);
    }

    // Communication bridge for host ↔ widget messaging
    const bridgeOpts: CommunicationBridgeOptions = {
      namespace: 'chat',
      onMessage: (msg) => this._handleBridgeMessage(msg),
    };
    if (config.allowedOrigins !== undefined) bridgeOpts.allowedOrigins = config.allowedOrigins;
    this._bridge = new CommunicationBridge(bridgeOpts);

    // Track initial page context for SPA navigation detection
    this._lastSku = this.config.pageContext?.sku;
    this._lastPageType = this.config.pageContext?.pageType;
    this._lastSkuListKey = this.config.pageContext?.skuList?.slice(0, 48).join(',');
    this._lastPageRouteKey = this._resolvePageRouteKey(this.config.pageContext?.url);
    this._lastCategoryLeaf = this._categoryLeaf(this.config.pageContext?.categoryTree);

    // Mark init complete and drain pending actions queue
    this._initComplete = true;
    for (const pending of this._pendingActions) {
      this._sendAction(pending.action, pending.options);
    }
    this._pendingActions = [];

    if (this._pillLauncherApply && variant === 'floating') {
      await this._pillLauncherApply();
      this._pillLauncherApply = null;
    }

    dispatch('gengage:chat:ready', {});
    ga.trackInit('chat');
    config.onReady?.();
  }

  protected onUpdate(context: Partial<PageContext>): void {
    // BaseWidget.update() spread-merges context over old pageContext, which
    // re-introduces stale fields that mergePageContext already deleted.
    // Clean them so request metadata never sends e.g. a PDP sku on a homepage action.
    if (this.config.pageContext && context.pageType !== undefined) {
      const { sku, skuList, ...rest } = this.config.pageContext;
      const cleaned: typeof this.config.pageContext = { ...rest };
      if (cleaned.pageType === 'pdp' && sku !== undefined) cleaned.sku = sku;
      if (cleaned.pageType === 'plp' && skuList !== undefined) cleaned.skuList = skuList;
      this.config = { ...this.config, pageContext: cleaned };
    }

    const previousPageType = this._lastPageType;
    const nextPageType = context.pageType ?? this.config.pageContext?.pageType;
    const nextPageRouteKey = this._resolvePageRouteKey(context.url);
    const pageRouteChanged =
      this._lastPageRouteKey !== undefined &&
      nextPageRouteKey !== undefined &&
      this._lastPageRouteKey !== nextPageRouteKey;
    const plpRouteUrlChanged = previousPageType === 'plp' && nextPageType === 'plp' && pageRouteChanged;
    const nextCategoryLeaf = this._categoryLeaf(context.categoryTree ?? this.config.pageContext?.categoryTree);
    const categoryRouteChanged =
      previousPageType === 'plp' &&
      nextPageType === 'plp' &&
      Boolean(this._lastCategoryLeaf) &&
      Boolean(nextCategoryLeaf) &&
      this._lastCategoryLeaf !== nextCategoryLeaf;
    let shouldReset = plpRouteUrlChanged || categoryRouteChanged;
    let skuListChanged = false;

    if (context.sku !== undefined && context.sku !== this._lastSku) {
      this._lastSku = context.sku;
      shouldReset = true;
    }
    if (context.pageType !== undefined && context.pageType !== this._lastPageType) {
      this._lastPageType = context.pageType;
      shouldReset = true;
      this.syncInlineLauncher();
    }
    if (context.skuList !== undefined) {
      const key = context.skuList.slice(0, 48).join(',');
      if (key !== this._lastSkuListKey) {
        this._lastSkuListKey = key;
        skuListChanged = true;
        const samePlpRoute =
          previousPageType === 'plp' && nextPageType === 'plp' && !plpRouteUrlChanged && !categoryRouteChanged;
        if (!samePlpRoute) shouldReset = true;
      }
    }
    if (nextPageRouteKey !== undefined) this._lastPageRouteKey = nextPageRouteKey;
    if (nextCategoryLeaf !== undefined) this._lastCategoryLeaf = nextCategoryLeaf;

    // Soft-SPA host (intercepts assistant navigation, no widget re-mount): the assistant
    // navigation's context update may arrive via onUpdate. Only treat a fresh marker as
    // assistant-driven while the navigation dispatch is actively pending. A stale marker
    // alone is not enough; otherwise a later user/site navigation can be misclassified.
    if (this._assistantSoftNavigationPending && this._isRestoreMarkerFresh() && this.config.session?.sessionId) {
      // Assistant-driven soft navigation: the thread the assistant just answered into is
      // STILL MOUNTED (no re-mount happened), so "continue in place" is correct — no restore
      // and no reset+greet over it (that was the original clobber). Consume the marker so it
      // can't strand and fire a spurious restore on a later hard reload, and keep the launch
      // flags handled so onShow won't greet.
      this._clearRestoreMarker();
      this._restorePending = false;
      this._markCurrentContextLaunchHandled();
      this._pdpLaunched = true;
      this._plpLaunched = true;
      this._homepageLaunched = true;
      return;
    }

    if (shouldReset) {
      // User-driven soft navigation (no fresh marker): today's fresh-session behavior.
      // _resetForNewPage also clears any (stale) marker so it can't strand.
      this._resetForNewPage();
    }
    const samePlpHydration =
      !shouldReset &&
      skuListChanged &&
      previousPageType === 'plp' &&
      nextPageType === 'plp' &&
      !this._usesGenericNonPdpLaunch();
    if (samePlpHydration && this._shopperTurnOwnsSurface()) {
      // A listing can hydrate after the shopper has already started a turn. The
      // new SKU snapshot still belongs in PageContext, but its automatic greeting
      // must not abort, clear, or follow the shopper's request later on reopen.
      this._markCurrentContextLaunchHandled();
      return;
    }

    // A genuine route reset needs a fresh native context launch while the drawer
    // stays open. Same-route PLP hydration also needs one chance to prime after
    // its first SKU list arrives; FE's private launch flags make later snapshots
    // and close/reopen cycles no-ops.
    if (this._drawerVisible && (shouldReset || skuListChanged) && this._hasDedicatedContextLaunch()) {
      this._primeOpenPageContext();
    }
  }

  protected onShow(): void {
    const detail: ChatOpenDetail = {
      state: this._openState,
      source: this._pendingOpenSource,
    };
    this._inlineLauncher?.setActive(true);
    this._showDrawer();
    this.emit('open', detail);
    dispatch('gengage:chat:open', detail);
    ga.trackShow('chat');
    this.config.onOpen?.(detail);

    this._showWelcomeIfNeeded();
    this._scheduleOpenPageContextPrime();
  }

  private _scheduleOpenPageContextPrime(): void {
    this._cancelScheduledOpenPageContextPrime();
    // The host adapter may inspect a large merchant DOM synchronously. Let the
    // newly visible drawer paint before automatic context collection begins.
    this._openContextPrimeRafId = requestAnimationFrame(() => {
      this._openContextPrimeRafId = null;
      this._openContextPrimeTimerId = window.setTimeout(() => {
        this._openContextPrimeTimerId = null;
        if (this._drawerVisible) {
          this._primeOpenPageContext();
          this._flushQueuedUserMessages();
        }
      }, 0);
    });
  }

  private _cancelScheduledOpenPageContextPrime(): void {
    if (this._openContextPrimeRafId !== null) {
      cancelAnimationFrame(this._openContextPrimeRafId);
      this._openContextPrimeRafId = null;
    }
    if (this._openContextPrimeTimerId !== null) {
      clearTimeout(this._openContextPrimeTimerId);
      this._openContextPrimeTimerId = null;
    }
  }

  private _isOpenPageContextPrimeScheduled(): boolean {
    return this._openContextPrimeRafId !== null || this._openContextPrimeTimerId !== null;
  }

  private _scheduleOpenActionAfterPaint(action: ActionPayload, options: SendActionOptions): void {
    this._cancelScheduledOpenPageContextPrime();
    this._pendingOpenActions.push({ action, options });
    if (this._openActionRafId !== null || this._openActionTimerId !== null) return;

    // QNA opens can enter an expensive host adapter synchronously. Give the
    // newly visible drawer one paint before starting that integration work.
    this._openActionRafId = requestAnimationFrame(() => {
      this._openActionRafId = null;
      this._openActionTimerId = window.setTimeout(() => {
        this._openActionTimerId = null;
        const pending = this._pendingOpenActions.splice(0);
        for (const item of pending) {
          this._sendAction(item.action, item.options);
        }
      }, 0);
    });
  }

  private _cancelScheduledOpenActions(): void {
    if (this._openActionRafId !== null) {
      cancelAnimationFrame(this._openActionRafId);
      this._openActionRafId = null;
    }
    if (this._openActionTimerId !== null) {
      clearTimeout(this._openActionTimerId);
      this._openActionTimerId = null;
    }
    this._pendingOpenActions = [];
  }

  private _primeOpenPageContext(): void {
    // R1: suppress the page-context auto-launch ONLY when a restore actually replayed a
    // non-empty thread that now owns the surface (_restorePending is true ONLY then,
    // see the field invariant). In that case the generic welcome + homepage/PLP/PDP
    // launch would clobber the restored answer with a "Merhaba!" greeting + curated home
    // chips (the yataş store-finder bug), so skip them and mark the launches handled.
    //
    // Regression #1: if the restore no-opped (stale/missing session, cross-SKU guard,
    // IDB absent, empty thread, rejected freshness token) _restorePending is false and
    // we FALL THROUGH to NORMAL launch below — the surface is never left blank.
    if (this._restorePending) {
      this._markCurrentContextLaunchHandled();
      this._pdpLaunched = true;
      this._plpLaunched = true;
      this._homepageLaunched = true;
      return;
    }

    // Show welcome message on first open with empty history.
    // Dedicated context launches (PDP/PLP/homepage) provide their own greeting,
    // so skip the generic welcome bubble for those page types.
    this._showWelcomeIfNeeded();

    // Prime a contextual opening for blank / homepage / listing states when
    // the merchant configured startup journeys in the SaaS.
    this._maybePrimeEntryContextOpening();

    // Auto-launch PDP context on first open when SKU is available
    if (!this._pdpLaunched && this.config.pageContext?.pageType === 'pdp' && this.config.pageContext?.sku) {
      this._pdpLaunched = true;
      this._contextPrimingInFlight = true;
      this._sendAction(
        {
          title: '',
          type: 'launchSingleProduct',
          payload: {
            sku: this.config.pageContext.sku,
            ...(this._resolveContextualOpeningMessage('product')
              ? { opening_message: this._resolveContextualOpeningMessage('product') }
              : {}),
            ...(this._resolveContextualOpeningGuidance('product')
              ? { opening_guidance: this._resolveContextualOpeningGuidance('product') }
              : {}),
          },
        },
        { silent: true, isContextPrime: true, preservePills: true },
      );
    }

    // Auto-launch PLP context on first open when skuList is available
    if (
      !this._usesGenericNonPdpLaunch() &&
      !this._plpLaunched &&
      this.config.pageContext?.pageType === 'plp' &&
      this.config.pageContext.skuList?.length
    ) {
      this._plpLaunched = true;
      this._contextPrimingInFlight = true;
      this._sendAction(
        {
          title: '',
          type: 'launchProductList',
          payload: { sku_list: this.config.pageContext.skuList.slice(0, 48) },
        },
        { silent: true, isContextPrime: true, preservePills: true },
      );
    }

    // Auto-launch homepage context on first open
    if (
      !this._homepageLaunched &&
      (this._usesGenericNonPdpLaunch() ||
        this.config.pageContext?.pageType === 'home' ||
        this.config.pageContext?.pageType === 'other')
    ) {
      this._homepageLaunched = true;
      this._contextPrimingInFlight = true;
      this._sendAction(
        {
          title: '',
          type: 'launchHomepage',
          payload: {},
        },
        { silent: true, isContextPrime: true, preservePills: true },
      );
    }
  }

  protected onHide(): void {
    this._cancelScheduledOpenPageContextPrime();
    this._inlineLauncher?.setActive(false);
    // Floating mode should only hide the drawer, not the launcher button.
    // BaseWidget.hide() toggles `root.style.display = 'none'`, so undo it here.
    if ((this.config.variant ?? 'floating') === 'floating') {
      this.root.style.display = '';
    }
    this._hideDrawer();
    this.emit('close');
    dispatch('gengage:chat:close', {});
    this.config.onClose?.();
  }

  private _setupLauncherAnchor(config: ChatWidgetConfig): void {
    const anchor = config.launcherAnchor;
    if (!anchor || (!anchor.mobile && !anchor.desktop)) return;

    const host = this.root as HTMLElement;
    const breakpoint = config.mobileBreakpoint ?? 768;
    const activeViewport = () => (window.innerWidth <= breakpoint ? anchor.mobile : anchor.desktop);
    const isSmoothViewport = () => {
      const viewport = activeViewport();
      return !!viewport?.dynamicRelocate && !!viewport.selector;
    };

    // Two dedicated override vars. Setting them overrides theme positioning;
    // removing them (removeProperty) naturally falls back to theme values — no
    // save/restore bookkeeping needed.
    const ANCHOR_BOTTOM = '--gengage-chat-launcher-anchor-bottom';
    const ANCHOR_RIGHT = '--gengage-chat-launcher-anchor-right';

    if (anchor.mobile?.dynamicRelocate || anchor.desktop?.dynamicRelocate) {
      host.style.setProperty('--gengage-chat-launcher-anchor-transition', '0.35s');
    }

    const applyAnchor = (): void => {
      const isMobile = window.innerWidth <= breakpoint;
      const viewport = isMobile ? anchor.mobile : anchor.desktop;
      if (!viewport) return;

      const selector = viewport.selector;
      const hasSelector = !!selector;
      const el = hasSelector ? document.querySelector(selector) : null;
      const rect = el?.getBoundingClientRect();

      if (viewport.bottom !== undefined) {
        const { offset, type } = viewport.bottom;
        if (!hasSelector) {
          host.style.setProperty(ANCHOR_BOTTOM, `${Math.max(0, offset)}px`);
        } else if (rect) {
          // root: ref = element's bottom edge (offset moves launcher upward from there)
          // gap:  offset=0 → same edge; offset>0 → N px outside the element's top edge
          const ref = type === 'root' ? rect.bottom : offset === 0 ? rect.bottom : rect.top;
          const px = Math.max(0, window.innerHeight - ref + offset);
          host.style.setProperty(ANCHOR_BOTTOM, `${px}px`);
        }
      }

      if (viewport.right !== undefined) {
        const { offset, type } = viewport.right;
        if (!hasSelector) {
          host.style.setProperty(ANCHOR_RIGHT, `${Math.max(0, offset)}px`);
        } else if (rect) {
          // root: ref = element's right edge (offset moves launcher inward from there)
          // gap:  offset=0 → same edge; offset>0 → N px outside the element's left edge
          const ref = type === 'root' ? rect.right : offset === 0 ? rect.right : rect.left;
          const px = Math.max(0, window.innerWidth - ref + offset);
          host.style.setProperty(ANCHOR_RIGHT, `${px}px`);
        }
      }
    };

    const clearAnchor = (): void => {
      host.style.removeProperty(ANCHOR_BOTTOM);
      host.style.removeProperty(ANCHOR_RIGHT);
    };

    // Tracks whether the anchor element is currently intersecting the viewport.
    // Prevents ResizeObserver from applying anchor positions for off-screen elements.
    let anchorIntersecting = false;
    let measureFrame = 0;
    const scheduleAnchorMeasure = (): void => {
      if (!isSmoothViewport() || !anchorIntersecting || measureFrame) return;
      measureFrame = requestAnimationFrame(() => {
        measureFrame = 0;
        if (isSmoothViewport() && anchorIntersecting) applyAnchor();
      });
    };
    const resizeObs = new ResizeObserver(() => {
      if (!isSmoothViewport() || anchorIntersecting) applyAnchor();
    });
    let intersectionObs: IntersectionObserver | null = null;
    let waitForElObs: MutationObserver | null = null;

    const activeFound = (): boolean => {
      const selector = activeViewport()?.selector;
      return !selector || !!document.querySelector(selector);
    };

    // Arms waitForElObs to re-wire observers when elements are replaced in the DOM
    // (e.g. SPA/framework removes and re-adds components on navigation or scroll).
    const armWaitForEl = (): void => {
      if (!isSmoothViewport() || activeFound()) return;
      waitForElObs?.disconnect();
      waitForElObs = new MutationObserver(() => {
        if (activeFound()) {
          waitForElObs?.disconnect();
          waitForElObs = null;
          observeAnchors();
        }
      });
      waitForElObs.observe(document.body, { childList: true, subtree: true });
    };

    const observeAnchors = (): void => {
      resizeObs.disconnect();
      intersectionObs?.disconnect();
      intersectionObs = null;
      anchorIntersecting = false;

      const viewport = activeViewport();
      if (!viewport) return;
      if (!viewport.selector) {
        applyAnchor();
        return;
      }
      const el = document.querySelector(viewport.selector);
      if (!el) return;

      resizeObs.observe(el);

      if (isSmoothViewport()) {
        intersectionObs = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                anchorIntersecting = true;
                applyAnchor();
              } else {
                anchorIntersecting = false;
                clearAnchor();
                // Re-arm in case the element was removed from the DOM (framework
                // re-renders) so the next appearance is handled correctly.
                armWaitForEl();
              }
            }
          },
          { threshold: 0 },
        );
        intersectionObs.observe(el);
      } else {
        applyAnchor();
      }
    };
    observeAnchors();

    let frame = 0;
    const onResize = (): void => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        anchorIntersecting = false;
        clearAnchor();
        waitForElObs?.disconnect();
        waitForElObs = null;
        observeAnchors();
        armWaitForEl();
        if (!isSmoothViewport()) applyAnchor();
      });
    };
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('scroll', scheduleAnchorMeasure, { passive: true });
    window.visualViewport?.addEventListener('scroll', scheduleAnchorMeasure, { passive: true });

    this._syncLauncherAnchor = (): void => {
      anchorIntersecting = false;
      clearAnchor();
      waitForElObs?.disconnect();
      waitForElObs = null;
      observeAnchors();
      armWaitForEl();
    };

    // Non-smooth: apply immediately and retry in case element loads late.
    if (!isSmoothViewport()) {
      applyAnchor();
      [100, 400, 1000].forEach((d) => window.setTimeout(applyAnchor, d));
    } else {
      // Smooth: element may not be in DOM yet (e.g. appears after scroll).
      armWaitForEl();
    }

    this._launcherAnchorCleanup = (): void => {
      resizeObs.disconnect();
      intersectionObs?.disconnect();
      waitForElObs?.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', scheduleAnchorMeasure);
      window.visualViewport?.removeEventListener('scroll', scheduleAnchorMeasure);
      if (frame) cancelAnimationFrame(frame);
      if (measureFrame) cancelAnimationFrame(measureFrame);
    };
  }

  syncLauncherAnchor(): void {
    this._syncLauncherAnchor?.();
  }

  syncInlineLauncher(): void {
    if (this._inlineLauncherBootstrapPending) return;
    void this._inlineLauncher?.sync(this.config.pageContext?.pageType);
  }

  private _syncLauncherAnchor: (() => void) | null = null;

  private _setupInlineLauncher(
    config: ChatWidgetConfig,
    { releaseFloatingLauncherOnFirstSync = false }: { releaseFloatingLauncherOnFirstSync?: boolean } = {},
  ): void {
    const inline = config.inlineLauncher;
    if (!inline) return;

    this._inlineLauncher = createInlineLauncher(
      inline,
      { onClick: () => this.open({ source: 'launcher' }) },
      {
        shadowRoot: this._shadow,
        getPageType: () => this.config.pageContext?.pageType,
      },
    );

    this._inlineLauncherBootstrapPending = true;

    const syncInlineLauncher = async (): Promise<void> => {
      await this._inlineLauncher?.sync(this.config.pageContext?.pageType);
    };

    const runInitialSync = async (): Promise<void> => {
      try {
        await syncInlineLauncher();
      } finally {
        this._inlineLauncherBootstrapPending = false;
        if (releaseFloatingLauncherOnFirstSync) {
          this._setInlineBootstrapFloatingLauncherHidden(false);
        }
      }
    };

    void runInitialSync();
    [100, 400, 1000, 2500].forEach((delay) => {
      window.setTimeout(() => {
        this.syncInlineLauncher();
      }, delay);
    });
  }

  private _floatingLauncherContainer(): HTMLElement | null {
    return this._floatingLauncher?.container ?? this._launcher?.container ?? null;
  }

  private _setInlineBootstrapFloatingLauncherHidden(hidden: boolean): void {
    const container = this._floatingLauncherContainer();
    if (!container) return;
    if (hidden) {
      container.dataset['gengageInlineBootstrapHidden'] = '1';
      container.style.setProperty('visibility', 'hidden');
      container.style.setProperty('opacity', '0');
      container.style.setProperty('pointer-events', 'none');
      return;
    }
    if (container.dataset['gengageInlineBootstrapHidden'] !== '1') return;
    delete container.dataset['gengageInlineBootstrapHidden'];
    container.style.removeProperty('visibility');
    container.style.removeProperty('opacity');
    container.style.removeProperty('pointer-events');
  }

  protected onDestroy(): void {
    this._cancelScheduledOpenPageContextPrime();
    this._cancelScheduledOpenActions();
    this._clearAssistantSoftNavigationPending();
    this._releaseHostDocumentScrollLock();
    this._restoreChatHostOverlayPriority();
    this._abortAllActiveRequests();
    this._activeTypewriter?.cancel();
    this._activeTypewriter = null;
    this._presentationScrollTimerIds.forEach((timerId) => clearTimeout(timerId));
    this._presentationScrollTimerIds.clear();
    this._activeTtsHandle?.stop();
    this._activeTtsHandle = null;
    if (this._comparisonRefreshRafId !== null) {
      cancelAnimationFrame(this._comparisonRefreshRafId);
      this._comparisonRefreshRafId = null;
    }
    this._launcherAnchorCleanup?.();
    this._launcherAnchorCleanup = null;
    this._syncLauncherAnchor = null;
    this._inlineLauncherBootstrapPending = false;
    this._floatingLauncher?.destroy();
    this._floatingLauncher = null;
    this._inlineLauncher?.destroy();
    this._inlineLauncher = null;
    this._drawer?.destroy();
    invalidateChatScrollCache();
    this._drawer = null;
    this._bridge?.destroy();
    this._bridge = null;
    this._extendedModeManager = null;
    this._panel?.destroy();
    this._panel = null;
    this._session?.close();
    this._session = null;
    this._historyStorage = null;
    this._activeHistorySessionId = null;
    this._localPanelHistory = [];
    this._currentPanelSource = null;
    if (window.gengage) {
      delete window.gengage.chat;
    }
    if (this._shadow) {
      this._shadow.innerHTML = '';
      this._shadow = null;
    }
    this._rootEl = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  open(options?: ChatOpenOptions): void {
    if (options?.state !== undefined) {
      this._openState = options.state;
      if (this._drawerVisible) {
        this._applyOpenStateClasses();
      }
    }
    this._showWithSource(options?.source);
    if (options?.initialMessage !== undefined) {
      this._sendMessage(options.initialMessage);
    }
  }

  openWithAction(action: ActionPayload, options?: ChatOpenActionOptions): void {
    const wasVisible = this._drawerVisible;
    if (options?.sku !== undefined) {
      this.update({ sku: options.sku });
    }
    // Skip the automatic page-prime for the current context since an explicit action is about to run.
    this._markCurrentContextLaunchHandled();
    // Action-triggered opens default to half-sheet on mobile
    if (options?.state !== undefined) {
      this._openState = options.state;
    } else if (this._isMobileViewport) {
      this._openState = 'half';
    }
    this._showWithSource(options?.source);
    if (this._drawerVisible) {
      this._applyOpenStateClasses();
    }
    const sendOptions: SendActionOptions = { skipFullSheet: true };
    if (options?.source === 'qna' && (!wasVisible || this._pendingOpenActions.length > 0)) {
      this._scheduleOpenActionAfterPaint(action, sendOptions);
    } else {
      this._sendAction(action, sendOptions);
    }
  }

  private _showWithSource(source: ChatOpenSource | undefined): void {
    this._pendingOpenSource = source ?? 'programmatic';
    try {
      this.show();
    } finally {
      // show() is a no-op while already visible; never leak that ignored source
      // into a later close/reopen transition.
      this._pendingOpenSource = 'programmatic';
    }
  }

  /** Send a user message programmatically (same flow as typing + submit). */
  sendMessage(text: string): void {
    this._sendMessage(text);
  }

  /** Send a backend action programmatically. */
  sendAction(action: ActionPayload, options?: { silent?: boolean }): void {
    this._sendAction(action, options);
  }

  close(): void {
    this.hide();
  }

  saveSession(sessionId: string, sku: string): void {
    sessionStorage.setItem('gengage_restore_session_id', sessionId);
    sessionStorage.setItem('gengage_restore_sku', sku);
    if (this._activeHistorySessionId) {
      sessionStorage.setItem('gengage_restore_history_id', this._activeHistorySessionId);
    } else {
      sessionStorage.removeItem('gengage_restore_history_id');
    }
    // MAJOR fix — empty-SKU gate: the relaxed onInit gate accepts a session-id-only
    // handoff (R1 store-finder), so an empty/whitespace-SKU saveSession would otherwise
    // silently flip fresh→restore+suppress. Preserve the old `sessionId && sku`
    // semantics for the SimRel product path: only stamp the freshness token (which is
    // what makes a marker count as a handoff) when there is a REAL product SKU. An
    // empty-SKU saveSession writes the legacy keys but no token, so onInit rejects it
    // as not-fresh and the page keeps its normal greeting. The deliberate no-SKU
    // store-page handoff goes through saveSessionForNavigation(), which always stamps.
    if (sku.trim()) {
      this._stampRestoreFreshnessToken();
    } else {
      sessionStorage.removeItem('gengage_restore_token');
    }
  }

  /**
   * Set the restore handoff marker for an assistant-driven PAGE navigation that has
   * no associated product (R1, e.g. the store-finder redirect). Mirrors saveSession()
   * but without a SKU — the destination is not a PDP, so there is nothing to gate the
   * restore on. The restore session id alone tells the re-mounted widget "the chat
   * navigated you here; restore the thread and continue, do not auto-launch a greeting".
   *
   * Only stamps the marker when there is a real session id to restore; otherwise it is
   * a no-op (a user/site navigation should stay a fresh session).
   */
  saveSessionForNavigation(): void {
    const sessionId = this.config.session?.sessionId;
    if (!sessionId) return;
    sessionStorage.setItem('gengage_restore_session_id', sessionId);
    // No SKU for a non-product page navigation — clear any stale SKU marker so the
    // restore gate (which skips a session saved for a different SKU) does not trip.
    sessionStorage.removeItem('gengage_restore_sku');
    if (this._activeHistorySessionId) {
      sessionStorage.setItem('gengage_restore_history_id', this._activeHistorySessionId);
    } else {
      sessionStorage.removeItem('gengage_restore_history_id');
    }
    // Stamp the freshness token: this no-SKU page handoff is the DELIBERATE store-page
    // case, so it must be honored on the immediately-following mount. The token binds it
    // to this navigation so a stranded copy can't fire a spurious restore later.
    this._stampRestoreFreshnessToken();
  }

  // ---------------------------------------------------------------------------
  // Restore-marker freshness token (regression #2 fix)
  //
  // The restore marker (gengage_restore_*) must have exactly ONE lifecycle: stamped on
  // an assistant-driven navigation, then consumed-or-rejected-and-cleared on the very
  // next mount/update — never surviving to a later, unrelated navigation. We bind the
  // marker to the navigation that produced it with a freshness token: a monotonically
  // increasing same-document nav sequence (persisted in sessionStorage so it survives
  // a hard re-mount) plus a timestamp. A marker is "fresh" only when its token's seq
  // equals the current nav seq — i.e. it was produced by the immediately-preceding
  // navigation. A stranded marker from an earlier navigation has a lower seq → rejected.
  // ---------------------------------------------------------------------------

  /** Read the persisted same-document nav sequence (0 when absent/corrupt). */
  private _readNavSeq(): number {
    try {
      const raw = sessionStorage.getItem('gengage_nav_seq');
      const n = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Stamp the freshness token onto the current restore marker: bump the nav seq and
   * record it (with a timestamp) so the immediately-following mount can recognize this
   * marker as fresh. Best-effort — non-fatal if sessionStorage is unavailable.
   */
  private _stampRestoreFreshnessToken(): void {
    const seq = this._readNavSeq() + 1;
    sessionStorage.setItem('gengage_nav_seq', String(seq));
    sessionStorage.setItem('gengage_restore_token', JSON.stringify({ seq, ts: Date.now() }));
  }

  /**
   * True when the current restore marker was produced by the immediately-preceding
   * navigation (its token's seq matches the current nav seq). A stale/stranded marker
   * — or one with no/corrupt token (e.g. an empty-SKU saveSession) — is NOT fresh.
   */
  private _isRestoreMarkerFresh(): boolean {
    try {
      const raw = sessionStorage.getItem('gengage_restore_token');
      if (!raw) return false;
      const token = JSON.parse(raw) as { seq?: unknown };
      const seq = typeof token.seq === 'number' ? token.seq : 0;
      return seq > 0 && seq === this._readNavSeq();
    } catch {
      return false;
    }
  }

  /**
   * Clear the restore marker and its freshness token in one place — the single
   * guaranteed-consume point so a marker never strands. Called on mount (onInit, after
   * the freshness decision) and on any state reset (_resetForNewPage / soft-nav).
   * Leaves gengage_nav_seq intact: it is the monotonic clock, not part of the marker.
   */
  private _clearRestoreMarker(): void {
    try {
      sessionStorage.removeItem('gengage_restore_session_id');
      sessionStorage.removeItem('gengage_restore_sku');
      sessionStorage.removeItem('gengage_restore_history_id');
      sessionStorage.removeItem('gengage_restore_token');
    } catch {
      // sessionStorage unavailable — nothing to clear, non-fatal.
    }
    this._clearAssistantSoftNavigationPending();
  }

  private _markAssistantSoftNavigationPending(): void {
    this._clearAssistantSoftNavigationPending();
    this._assistantSoftNavigationPending = true;
  }

  private _clearAssistantSoftNavigationPending(): void {
    this._assistantSoftNavigationPending = false;
    if (this._assistantSoftNavigationTimer !== null) {
      window.clearTimeout(this._assistantSoftNavigationTimer);
      this._assistantSoftNavigationTimer = null;
    }
  }

  private _expireAssistantSoftNavigationIfUnclaimed(): void {
    if (!this._assistantSoftNavigationPending) return;
    this._clearRestoreMarker();
  }

  private _scheduleAssistantSoftNavigationExpiry(): void {
    if (!this._assistantSoftNavigationPending || this._assistantSoftNavigationTimer !== null) return;
    this._assistantSoftNavigationTimer = window.setTimeout(() => {
      this._assistantSoftNavigationTimer = null;
      this._expireAssistantSoftNavigationIfUnclaimed();
    }, 250);
  }

  get isOpen(): boolean {
    return this._drawerVisible;
  }

  /**
   * Register a callback for integration events (e.g. 'gengage-cart-add', 'gengage-product-favorite').
   * The callback receives the event detail and should return true (success) or false (failure).
   * For add-to-cart, failure triggers an error message in the chat.
   * For product-favorite, failure reverts the heart on the card and shows an error message.
   * @returns unsubscribe function
   */
  addCallback(
    eventName: string,
    callback: (detail: Record<string, unknown>) => boolean | Promise<boolean>,
  ): () => void {
    let set = this._eventCallbacks.get(eventName);
    if (!set) {
      set = new Set();
      this._eventCallbacks.set(eventName, set);
    }
    set.add(callback);
    return () => {
      set!.delete(callback);
      if (set!.size === 0) this._eventCallbacks.delete(eventName);
    };
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private _applyDiscountedPriceColorVar(
    mode: NonNullable<ChatWidgetConfig['productPriceUi']>['discountedPriceColor'] | undefined,
  ): void {
    const host = this.root as HTMLElement;
    if (mode === 'client') {
      host.style.setProperty('--gengage-discounted-price-color', 'var(--client-primary)');
    } else {
      host.style.removeProperty('--gengage-discounted-price-color');
    }
  }

  private _abortAllActiveRequests(): void {
    for (const controller of this._abortControllers) {
      controller.abort();
    }
    this._abortControllers.clear();
  }

  /**
   * Drop assistant rows that are still "streaming" with no text when the user starts
   * a new turn. Otherwise each superseded request leaves an empty model message in
   * `_messages` — it is included in `chatHistory` and can confuse the backend and
   * delay or distort the next response.
   */
  private _pruneEmptyStreamingAssistantPlaceholders(): void {
    const next: ChatMessage[] = [];
    for (const m of this._messages) {
      // Also catch status === 'done': the Stop button handler transitions streaming → done
      // before _sendAction fires, so a stop-then-Enter sequence would otherwise leave an
      // empty assistant turn in chatHistory and confuse the backend.
      const drop =
        m.role === 'assistant' &&
        !m.frontendOnly &&
        (m.status === 'streaming' || m.status === 'done') &&
        (m.content == null || m.content.length === 0);
      if (drop) {
        if (m.threadId) {
          const keepThreadArtifacts =
            this._messages.some(
              (candidate) => candidate.id !== m.id && candidate.threadId === m.threadId && candidate.frontendOnly,
            ) || this._panel?.snapshotTypes.has(m.id) === true;
          this._threadsWithFirstBot.delete(m.threadId);
          this._presentation.finalizeAssistantGroup(m.threadId);
          // Remove inline UISpec nodes (data-thread-id but no data-message-id) that may
          // have arrived before any outputText — removeMessageBubble only targets the bubble.
          this._shadow
            ?.querySelectorAll(`[data-thread-id="${escapeCssIdentifier(m.threadId)}"]:not([data-message-id])`)
            .forEach((el) => el.remove());
          if (this._panel && !keepThreadArtifacts)
            this._panel.threads = this._panel.threads.filter((t) => t !== m.threadId);
        }
        this._drawer?.removeMessageBubble(m.id);
        this._panel?.snapshots.delete(m.id);
        this._panel?.snapshotTypes.delete(m.id);
        continue;
      }
      next.push(m);
    }
    this._messages.length = 0;
    this._messages.push(...next);
  }

  /** Clear all runtime conversation state while keeping the widget mounted and ready. */
  private _clearConversationState(resetPageLaunchFlags: boolean): void {
    // Invalidate any in-flight stream callbacks so they discard themselves
    // via the `threadId !== this._activeRequestThreadId` guard.
    this._activeRequestThreadId = null;
    // Abort in-flight streams
    this._abortAllActiveRequests();
    // Cancel active typewriter/TTS
    this._activeTypewriter?.cancel();
    this._activeTypewriter = null;
    this._activeTtsHandle?.stop();
    this._activeTtsHandle = null;
    // Clear messages
    this._messages.length = 0;
    this._ephemeralWelcomeMessageId = null;
    this._drawer?.clearMessages();
    // Clear panel
    this._drawer?.clearPanel();
    this._currentPanelSource = null;
    this._currentInlineProductSummaryRecord = null;
    this._localPanelHistory = [];
    if (this._panel) {
      this._panel.clearSnapshots();
      this._panel.threads = [];
      this._panel.activePanelMessageId = null;
      this._panel.currentType = null;
    }
    this._panelAiSpecByThread.clear();
    this._threadPillsByThreadId.clear();
    // Reset accumulated sku->product lookup so a new chat doesn't carry stale
    // products from the previous conversation (and the map can't grow unbounded).
    this._skuToProductItem = {};
    // Reset comparison state
    this._comparisonSelectMode = false;
    this._comparisonSelectedSkus = [];
    this._comparisonSelectionWarning = null;
    this._viewedProductSkus.clear();
    this._threadsWithFirstBot.clear();
    // Reset thread cursors
    this._currentThreadId = null;
    this._lastThreadId = null;
    this._lastBackendContext = null;
    this._chatCreatedAt = new Date().toISOString();
    // Allow PDP/PLP/homepage auto-launch for new context
    if (resetPageLaunchFlags) {
      this._pdpLaunched = false;
      this._plpLaunched = false;
      this._homepageLaunched = false;
      this._entryContextPrimed = false;
      // A genuine page-context change consumes the restore handoff — the next
      // context launch is appropriate again.
      this._restorePending = false;
    }
    this._contextPrimingInFlight = false;
    this._queuedUserMessages = [];
    this._productContextUnavailableSku = null;
    this._presentation.reset();
    this._presentation.setShown(this._drawerVisible);
    this._drawer?.setPresentationFocus(null);
    this._drawer?.setFormerMessagesButtonVisible(false);
    this._drawer?.setPills([]);
    clearChoicePrompterDismissState();
    if (this._modeController.reset()) {
      this._drawer?.setInputPlaceholder(this._i18n.inputPlaceholder);
      this._drawer?.setAttachmentControlsVisible(true);
      this._drawer?.setBeautyPhotoStepCard({ visible: false });
    }
  }

  /** Reset all chat state when navigating to a different SKU/page. */
  private _resetForNewPage(reason: ChatConversationResetReason = 'page_context_changed'): void {
    this._activeHistorySessionId = null;
    this._bridgeContext = null;
    // Regression #2a — no strand: a state reset (incl. a soft-nav reset on hosts that
    // don't re-mount the widget) must also drop the restore marker. Otherwise a marker
    // stamped for THIS navigation survives in sessionStorage and fires a spurious
    // restore on the next hard reload (old thread onto an unrelated page, suppressing
    // its greeting). _clearConversationState(true) already resets _restorePending.
    this._clearRestoreMarker();
    this._clearConversationState(true);
    dispatchConversationReset(reason);
  }

  /** Start a brand-new local conversation on the current page context. */
  private _startNewChat(): void {
    this._activeHistorySessionId = null;
    this._clearConversationState(true);
    dispatchConversationReset('new_chat');
    this._primeOpenPageContext();
    this._drawer?.focusInput();
  }

  /**
   * Same side effects as stream UISpec `clearPanel`: hide/clear the assistant panel without
   * `restoreOrClearPanel()`. Used for `clearPanel` chunks and PDP chat layout (`productDetailsExtended` off).
   */
  private _clearAssistantPanelLikeStreamClearPanel(): void {
    this._clearChoicePrompter();
    this._drawer?.clearPanel();
    this._currentPanelSource = null;
    this._panelAiSpecByThread.clear();
    this._comparisonSelectMode = false;
    this._comparisonSelectedSkus = [];
    this._comparisonSelectionWarning = null;
    if (this._panel) {
      this._panel.currentType = null;
      this._panel.updateExtendedMode('');
      this._panel.updateTopBar('');
    }
  }

  private _flushPresentationScroll(): void {
    const req = this._presentation.scrollRequest;
    if (!req || !this._drawer) return;
    let handled = false;
    if (req.type === 'thread' && req.threadId) {
      handled = this._drawer.scrollThreadIntoView(req.threadId, req.behavior);
      if (!handled) {
        this._drawer.scrollToBottomPresentation(req.behavior);
        handled = true;
      }
    } else if (req.type === 'bottom') {
      this._drawer.scrollToBottomPresentation(req.behavior);
      handled = true;
    }
    if (handled) {
      this._presentation.consumeScrollRequest(req.id);
    }
  }

  private _schedulePresentationScrollFlush(): void {
    const timerId = setTimeout(() => {
      this._presentationScrollTimerIds.delete(timerId);
      this._flushPresentationScroll();
    }, 40);
    this._presentationScrollTimerIds.add(timerId);
  }

  private _focusPresentationThread(
    threadId: string,
    behavior: ScrollBehavior = 'smooth',
    syncDrawerFocus = false,
  ): void {
    this._presentation.requestThreadFocus(threadId, behavior);
    if (syncDrawerFocus) {
      this._drawer?.setPresentationFocus(threadId);
    }
    this._drawer?.setFormerMessagesButtonVisible(false);
    this._schedulePresentationScrollFlush();
  }

  /** Align inline UISpec render so the thread’s first visible node stays at the top. */
  private _scrollInlineIntoView(inline: HTMLElement, threadId: string | null | undefined): void {
    if (threadId) {
      this._focusPresentationThread(threadId, 'auto');
      return;
    }
    if (typeof inline.scrollIntoView === 'function') {
      inline.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  private _releasePresentationFocus(): void {
    this._presentation.releaseFocusedThread();
    this._drawer?.setPresentationFocus(null);
    this._drawer?.setFormerMessagesButtonVisible(false);
  }

  private _hasMultipleThreadIds(): boolean {
    const ids = new Set<string>();
    for (const m of this._messages) {
      if (m.threadId) ids.add(m.threadId);
    }
    return ids.size > 1;
  }

  private _orderedThreadIds(): string[] {
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const m of this._messages) {
      if (m.threadId && !seen.has(m.threadId)) {
        seen.add(m.threadId);
        ordered.push(m.threadId);
      }
    }
    return ordered;
  }

  private _maybeAutoAnchorUnreadAssistant(): void {
    if (!this._drawer || !this._presentation.shown) return;
    const threadIds = this._orderedThreadIds();
    if (threadIds.length === 0) return;
    const latestUnread = getLatestUnreadAssistantThreadId(threadIds, this._presentation);
    if (!latestUnread) return;
    const gid = `${latestUnread}:assistant`;
    if (this._presentation.lastAutoAnchoredGroupId === gid) return;
    if (this._presentation.userInteracting && !this._presentation.pinnedToBottom) return;
    if (this._drawer.scrollThreadIntoView(latestUnread, 'smooth')) {
      this._presentation.markGroupAutoAnchored(gid);
    }
  }

  private _handleBridgeMessage(msg: BridgeMessage): void {
    switch (msg.type) {
      case 'openChat':
        this.open();
        break;
      case 'closeChat':
        this.close();
        break;
      case 'startNewChatWithLauncherAction': {
        // Start a new chat with a preset launcher action.
        // Legacy sends payload as the action directly: { type, title, requestDetails }
        // Clean-room also accepts nested: { action: { type, title, ... } }
        const payload = msg.payload as Record<string, unknown> | undefined;
        const nested = payload?.action;
        // Prefer nested shape, fall back to payload-is-action (legacy compat)
        const action = isActionLike(nested) ? nested : isActionLike(payload) ? payload : null;
        if (action) {
          this._sendAction(action, { silent: true });
        }
        this.open();
        break;
      }
      case 'startNewChatWithDetailContext': {
        // Start chat with product detail context (e.g., from PDP page).
        // Legacy shape: { action: {...}, sku: "..." }
        const ctx = msg.payload as Record<string, unknown> | undefined;
        if (ctx && typeof ctx === 'object') {
          this._bridgeContext = ctx;
          // Extract SKU and update page context so auto-PDP launch uses it
          const sku = ctx.sku as string | undefined;
          if (sku) {
            this.update({ sku });
          }
          // Extract and send the action (legacy sent action + premade context)
          if (isActionLike(ctx.action)) {
            this._markCurrentContextLaunchHandled(); // Skip auto-launch, explicit action takes priority
            this.open();
            this._sendAction(ctx.action);
            break;
          }
        }
        this.open();
        break;
      }
      case 'launcherAction': {
        // Send action to active chat from external trigger
        const payload = msg.payload as Record<string, unknown> | undefined;
        const action = payload?.action;
        if (action && typeof action === 'object' && 'type' in (action as Record<string, unknown>)) {
          this._sendAction(action as ActionPayload);
        }
        break;
      }
      case 'scrollToBottom':
        this._presentation.requestScrollToBottom('smooth');
        this._schedulePresentationScrollFlush();
        break;
      case 'addToCardHandler': {
        // Host notifies widget of add-to-cart result
        this._bridge?.send('addToCardResult', msg.payload);
        break;
      }
      case 'cartQuantityHandler':
        break;
      case 'favoritesCountHandler': {
        const payload = msg.payload as Record<string, unknown> | undefined;
        const count = payload?.count;
        if (typeof count === 'number' && count >= 0) {
          this._drawer?.updateFavoritesBadge(count);
        }
        break;
      }
      case 'minimizeRequestedByUser':
        this._extendedModeManager?.setHiddenByUser(true);
        break;
      case 'bgColorChange': {
        const payload = msg.payload as Record<string, unknown> | undefined;
        const color = payload?.color;
        if (typeof color === 'string' && isSafeCSSColor(color) && this._shadow) {
          (this._shadow.host as HTMLElement).style.setProperty('--gengage-chat-bg', color);
        }
        break;
      }
      default:
        break;
    }
  }

  private _registerPublicAPI(): void {
    if (!window.gengage) window.gengage = {};
    window.gengage.chat = {
      open: (opts) => this.open(opts),
      openWithAction: (action, opts) => this.openWithAction(action, opts),
      sendMessage: (text) => this.sendMessage(text),
      sendAction: (action, opts) => this.sendAction(action, opts),
      close: () => this.close(),
      saveSession: (sid, sku) => this.saveSession(sid, sku),
      get isOpen() {
        return false;
      }, // Placeholder, overridden below
      on: (event, handler) => this.on(event, handler),
      addCallback: (eventName, callback) => this.addCallback(eventName, callback),
    };
    // Fix isOpen getter to reflect actual state
    Object.defineProperty(window.gengage.chat, 'isOpen', {
      get: () => this._drawerVisible,
    });
  }

  private _showDrawer(): void {
    if (this._drawerVisible) return;
    this._drawerVisible = true;
    const el = this._drawer?.getElement();
    if (el) {
      el.classList.remove('gengage-chat-drawer--hidden');
    }
    this._applyOpenStateClasses();
    this._syncVisualVh?.();
    this._drawer?.trapFocus();
    if (!this._isMobileViewport) {
      this._drawer?.focusInput();
    }
    this._extendedModeManager?.setChatShown(true);
    this._presentation.setShown(true);
    setTimeout(() => this._maybeAutoAnchorUnreadAssistant(), 60);
  }

  /** Show welcome message and starter pills on first open with empty history. */
  private _showWelcomeIfNeeded(): void {
    if (this._messages.length !== 0) return;

    const contextKey = this._resolveOpeningContextKey();
    const openingActions = this._resolveContextualOpeningActions(contextKey);
    if (openingActions.length > 0) {
      // TODO(CSH): To be moved to CSH as FE MUST NOT CONTAIN default business logic;
      // all activities should be streamed by backend/CSH agent.
      this._drawer?.setPills(
        openingActions.map((action) => ({
          label: action.title,
          onAction: () => this._sendAction(this._resolveContextualOpeningAction(action, contextKey)),
        })),
      );
    }

    // Dedicated context launches provide their own backend-generated greeting —
    // skip the local generic welcome bubble so the shopper doesn't see two greetings.
    if (this._hasDedicatedContextLaunch()) return;

    if (this._shouldPrimeContextualOpening()) {
      return;
    }

    const ephemeralWelcomeMessage = this.config.ephemeralWelcomeMessage?.trim();
    if (ephemeralWelcomeMessage) {
      if (this._ephemeralWelcomeMessageId) return;
      const welcomeMsg: ChatMessage = {
        id: uuidv7(),
        role: 'assistant',
        content: ephemeralWelcomeMessage,
        timestamp: Date.now(),
        status: 'done',
        frontendOnly: true,
      };
      this._ephemeralWelcomeMessageId = welcomeMsg.id;
      this._drawer?.addMessage(welcomeMsg);
      return;
    }

    const welcomeMessage = this._resolveContextualOpeningMessage(contextKey);
    if (!welcomeMessage) return;

    const welcomeMsg: ChatMessage = {
      id: uuidv7(),
      role: 'assistant',
      content: welcomeMessage,
      timestamp: Date.now(),
      status: 'done',
    };
    this._messages.push(welcomeMsg);
    this._drawer?.addMessage(welcomeMsg);
  }

  private _resolveOpeningContextKey(): OpeningContextKey {
    switch (this.config.pageContext?.pageType) {
      case 'home':
        return 'home';
      case 'search':
      case 'plp':
        return 'listing';
      case 'pdp':
        return 'product';
      default:
        return 'default';
    }
  }

  private _resolveContextualOpeningMessage(contextKey = this._resolveOpeningContextKey()): string | undefined {
    return (
      this.config.openingMessagesByContext?.[contextKey] ??
      (contextKey !== 'default' ? this.config.openingMessagesByContext?.default : undefined) ??
      this.config.welcomeMessage
    );
  }

  private _resolveContextualOpeningGuidance(contextKey = this._resolveOpeningContextKey()): string | undefined {
    return (
      this.config.openingGuidanceByContext?.[contextKey] ??
      (contextKey !== 'default' ? this.config.openingGuidanceByContext?.default : undefined)
    );
  }

  private _resolveContextualOpeningActions(contextKey = this._resolveOpeningContextKey()): ChatActionChip[] {
    // TODO(CSH): To be moved to CSH as FE MUST NOT CONTAIN default business logic;
    // all activities should be streamed by backend/CSH agent.
    const contextualActions =
      this.config.welcomeActionsByContext?.[contextKey] ??
      (contextKey !== 'default' ? this.config.welcomeActionsByContext?.default : undefined);

    if (contextualActions?.length) {
      return contextualActions.filter((action) => typeof action?.title === 'string' && action.title.trim().length > 0);
    }

    return (this.config.welcomeActions ?? [])
      .filter((title) => typeof title === 'string' && title.trim().length > 0)
      .map((title) => ({ title }));
  }

  private _resolveContextualOpeningAction(
    action: ChatActionChip,
    contextKey = this._resolveOpeningContextKey(),
  ): ActionPayload {
    // TODO(CSH): To be moved to CSH as FE MUST NOT CONTAIN default business logic;
    // all activities should be streamed by backend/CSH agent.
    const title = action.title.trim();
    const primarySku = this.config.pageContext?.sku ?? this._readContextStringField('sku');
    const visibleSkus = this._readContextStringListField('visible_skus') ?? [];

    if (action.icon === 'review' && primarySku) {
      return {
        title,
        type: 'reviewSummary',
        payload: { sku: primarySku },
      };
    }

    if (action.icon === 'similar' && primarySku) {
      return {
        title,
        type: 'findSimilar',
        payload: { sku: primarySku },
      };
    }

    if (action.icon === 'compare' && contextKey === 'listing' && visibleSkus.length >= 2) {
      return {
        title,
        type: 'getComparisonTable',
        payload: { sku_list: visibleSkus.slice(0, 2) },
      };
    }

    return {
      title,
      type: 'user_message',
      payload: title,
    };
  }

  private _readContextStringField(key: string): string | undefined {
    const pageContext = this.config.pageContext as Record<string, unknown> | undefined;
    const extra =
      pageContext?.extra && typeof pageContext.extra === 'object' && !Array.isArray(pageContext.extra)
        ? (pageContext.extra as Record<string, unknown>)
        : undefined;
    const altCamel = key.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());
    const candidate = pageContext?.[key] ?? extra?.[key] ?? pageContext?.[altCamel] ?? extra?.[altCamel];
    return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate.trim() : undefined;
  }

  private _readContextStringListField(key: string): string[] | undefined {
    const pageContext = this.config.pageContext as Record<string, unknown> | undefined;
    const extra =
      pageContext?.extra && typeof pageContext.extra === 'object' && !Array.isArray(pageContext.extra)
        ? (pageContext.extra as Record<string, unknown>)
        : undefined;
    const altCamel = key.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());
    const candidate = pageContext?.[key] ?? extra?.[key] ?? pageContext?.[altCamel] ?? extra?.[altCamel];
    if (!Array.isArray(candidate)) return undefined;
    const values = candidate.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return values.length > 0 ? values : undefined;
  }

  private _usesGenericNonPdpLaunch(): boolean {
    const pageType = this.config.pageContext?.pageType;
    return this.config.genericNonPdpLaunch === true && pageType !== undefined && pageType !== 'pdp';
  }

  /**
   * Stable SPA route identity. Query/hash updates commonly represent filters,
   * sorting, pagination, or anchor state rather than a new category session.
   */
  private _resolvePageRouteKey(url: string | undefined): string | undefined {
    const value = url?.trim() || window.location.href;
    if (!value) return undefined;
    try {
      const parsed = new URL(value, window.location.href);
      const pathname = parsed.pathname.replace(/\/+$/u, '') || '/';
      return `${parsed.origin}${pathname}`;
    } catch {
      return value.split(/[?#]/u, 1)[0]?.replace(/\/+$/u, '') || '/';
    }
  }

  /**
   * Category trees can hydrate from a leaf into a full breadcrumb. The leaf is
   * the stable route signal; URL pathname remains the primary discriminator.
   */
  private _categoryLeaf(categoryTree: string[] | undefined): string | undefined {
    if (categoryTree === undefined) return undefined;
    return categoryTree[categoryTree.length - 1]?.trim() ?? '';
  }

  private _markCurrentContextLaunchHandled(): void {
    const ctx = this.config.pageContext;
    if (!ctx) return;
    if (ctx.pageType === 'pdp' && ctx.sku) {
      this._pdpLaunched = true;
      return;
    }
    if (this._usesGenericNonPdpLaunch()) {
      this._homepageLaunched = true;
      return;
    }
    if (ctx.pageType === 'plp' && ctx.skuList?.length) {
      this._plpLaunched = true;
      return;
    }
    if (ctx.pageType === 'home' || ctx.pageType === 'other') {
      this._homepageLaunched = true;
    }
  }

  private _shopperTurnOwnsSurface(): boolean {
    if (this._queuedUserMessages.length > 0) return true;
    return this._messages.some(
      (message) =>
        message.role === 'user' ||
        (message.role === 'assistant' && message.status === 'streaming' && message.silent !== true),
    );
  }

  private _buildEntryOpeningPageDetails(): Record<string, unknown> | undefined {
    const pageDetails: Record<string, unknown> = {};

    pageDetails.url = this.config.pageContext?.url ?? window.location.href;

    const pageTitle = this._readContextStringField('page_title');
    if (pageTitle) pageDetails.page_title = pageTitle;

    const pageDescription = this._readContextStringField('page_description');
    if (pageDescription) pageDetails.page_description = pageDescription;

    const searchQuery = this._readContextStringField('search_query');
    if (searchQuery) pageDetails.search_query = searchQuery;

    const visibleSkus = this._readContextStringListField('visible_skus');
    if (visibleSkus?.length) pageDetails.visible_skus = visibleSkus;

    const popularSearches = this._readContextStringListField('popular_searches');
    if (popularSearches?.length) pageDetails.popular_searches = popularSearches;

    const categoryPath = this.config.pageContext?.categoryTree ?? this._readContextStringListField('category_path');
    if (categoryPath?.length) pageDetails.category_path = categoryPath;

    return Object.keys(pageDetails).length > 0 ? pageDetails : undefined;
  }

  /** True when the current page type has a dedicated silent auto-launch that provides its own greeting. */
  private _hasDedicatedContextLaunch(): boolean {
    const ctx = this.config.pageContext;
    if (!ctx) return false;
    if (ctx.pageType === 'pdp' && ctx.sku) return true;
    if (this._usesGenericNonPdpLaunch()) return true;
    if (ctx.pageType === 'plp' && ctx.skuList?.length) return true;
    if (ctx.pageType === 'home') return true;
    return false;
  }

  private _shouldPrimeContextualOpening(): boolean {
    if (this._messages.length !== 0 || this._entryContextPrimed || this._contextPrimingInFlight) return false;
    // Dedicated launches handle context priming — skip generic contextual opening
    if (this._hasDedicatedContextLaunch()) return false;
    return Boolean(
      this.config.openingMessagesByContext ||
      this.config.openingGuidanceByContext ||
      this.config.welcomeActionsByContext,
    );
  }

  private _maybePrimeEntryContextOpening(): void {
    const contextKey = this._resolveOpeningContextKey();
    if (!this._shouldPrimeContextualOpening()) return;

    this._entryContextPrimed = true;

    const payload: Record<string, unknown> = {
      text: '',
      is_entry_context_opening: 1,
      opening_context_key: contextKey,
    };

    const pageDetails = this._buildEntryOpeningPageDetails();
    if (pageDetails) payload.page_details = pageDetails;

    const openingMessage = this._resolveContextualOpeningMessage(contextKey);
    if (openingMessage) payload.opening_message = openingMessage;

    const openingGuidance = this._resolveContextualOpeningGuidance(contextKey);
    if (openingGuidance) payload.opening_guidance = openingGuidance;

    this._sendAction(
      {
        title: '',
        type: 'user_message',
        payload,
      },
      { silent: true, preservePills: true },
    );
  }

  private _hideDrawer(): void {
    if (!this._drawerVisible) return;
    this._drawer?.releaseFocus();
    this._activeTypewriter?.cancel();
    this._activeTypewriter = null;
    this._activeTtsHandle?.stop();
    this._activeTtsHandle = null;
    this._drawerVisible = false;
    // Reset mobile open state so next launcher open defaults to full-screen
    this._openState = 'full';
    // Don't clear panel — preserve it so reopening the drawer shows the same
    // panel state. clearPanel() is reserved for SKU/page resets and stale
    // loading skeleton cleanup after streams.
    const el = this._drawer?.getElement();
    if (el) {
      el.classList.add('gengage-chat-drawer--hidden');
      // Reset keyboard inset override so the next open starts from resting position.
      el.style.removeProperty('--gengage-chat-keyboard-inset');
    }
    this._lastAppliedKeyboardInset = 0;
    this._applyOpenStateClasses();
    this._extendedModeManager?.setChatShown(false);
    this._presentation.setShown(false);
    this._drawer?.setPresentationFocus(null);
    this._drawer?.setFormerMessagesButtonVisible(false);
  }

  private _syncViewportState(): void {
    if (!this._rootEl) return;
    this._isMobileViewport = window.innerWidth <= this._mobileBreakpoint;
    this._rootEl.classList.toggle('gengage-chat-root--mobile', this._isMobileViewport);
    this._drawer?.syncPanelShellState();

    if (this._launcher) {
      const hideLauncher = this._isMobileViewport && this.config.hideMobileLauncher === true;
      this._launcher.container.classList.toggle('gengage-chat-launcher--hidden-mobile', hideLauncher);
    }

    this._applyOpenStateClasses();
  }

  /**
   * Dimming backdrop + click-through scrim only in this state:
   * - overlay: drawer open (full-screen modal)
   * - floating: drawer open and side panel visible (split / “maximized” layout)
   */
  private _isMaximizedForHostChrome(): boolean {
    if (!this._drawerVisible) return false;
    const variant = this.config.variant ?? 'floating';
    if (variant === 'inline') return false;
    if (variant === 'overlay') return true;
    if (!(this._drawer?.isPanelVisible() ?? false)) return false;
    // Desktop split view: scrim only while MainPane is expanded; collapsed = chat-only column.
    if (!this._isMobileViewport && (this._drawer?.isPanelCollapsed() ?? false)) return false;
    return true;
  }

  /** Host page scroll blocked when host chrome is maximized (overlay/mobile sheet, or expanded desktop MainPane). */
  private _shouldLockHostDocumentScroll(): boolean {
    if (!this._drawerVisible) return false;
    const variant = this.config.variant ?? 'floating';
    if (variant === 'inline') return false;
    if (variant === 'overlay') return true;
    if (this._isMobileViewport) return true;
    return this._isMaximizedForHostChrome();
  }

  private _applyOpenStateClasses(): void {
    if (!this._rootEl) return;
    const mobileHalf = this._drawerVisible && this._isMobileViewport && this._openState === 'half';
    const mobileFull = this._drawerVisible && this._isMobileViewport && this._openState === 'full';
    const maximizedHost = this._isMaximizedForHostChrome();
    this._rootEl.classList.toggle('gengage-chat-root--open', this._drawerVisible);
    this._rootEl.classList.toggle('gengage-chat-root--mobile-half', mobileHalf);
    this._rootEl.classList.toggle('gengage-chat-root--mobile-full', mobileFull);
    this._rootEl.classList.toggle('gengage-chat-root--maximized-host-chrome', maximizedHost);
    this._drawer?.syncPanelShellState();
    this._syncChatHostOverlayPriority(
      this._drawerVisible && this._isMobileViewport && (this.config.variant ?? 'floating') !== 'inline',
    );
    if (mobileFull) {
      this._syncVisualVh?.();
    }
    if (this._backdropEl) {
      const backdropIsScrim = maximizedHost && (this.config.variant ?? 'floating') !== 'inline';
      this._backdropEl.setAttribute('aria-hidden', backdropIsScrim ? 'false' : 'true');
    }
    this._syncHostDocumentScrollLock();
    this._maybeTrackChatbotMainPaneGa();
  }

  /** MainPane = assistant left panel; mobile counts full overlay panel as expanded. */
  private _mainPaneExpandedForAnalytics(): boolean {
    const d = this._drawer;
    if (!d?.isPanelVisible()) return false;
    if (this._isMobileViewport) return true;
    return !d.isPanelCollapsed();
  }

  private _maybeTrackChatbotMainPaneGa(): void {
    const expanded = this._mainPaneExpandedForAnalytics();
    if (expanded && !this._gaPrevMainPaneExpanded) {
      ga.trackChatbotMaximized();
    }
    this._gaPrevMainPaneExpanded = expanded;
  }

  private _syncHostDocumentScrollLock(): void {
    if (typeof document === 'undefined') return;
    const variant = this.config.variant ?? 'floating';
    if (variant === 'inline') {
      this._releaseHostDocumentScrollLock();
      return;
    }
    if (this._shouldLockHostDocumentScroll()) {
      this._applyHostDocumentScrollLock();
    } else {
      this._releaseHostDocumentScrollLock();
    }
  }

  /**
   * When true, we do not preventDefault — the event may drive chat-internal scrolling.
   * Full-viewport backdrop sits inside the shadow tree, so `composedPath()` always hits
   * the host; we must not treat "inside widget" as "allow scroll" for that case.
   */
  private _hostScrollEventShouldReachChatScroller(ev: Event): boolean {
    try {
      const path = ev.composedPath();
      if (path.length === 0) return true;
      if (!path.includes(this.root)) return false;
      return this._chatScrollTargetFromPath(path) !== null;
    } catch {
      return false;
    }
  }

  private _chatScrollTargetFromPath(path: EventTarget[]): {
    el: HTMLElement;
    vertical: boolean;
    horizontal: boolean;
  } | null {
    for (const n of path) {
      if (n === this.root) break;
      if (!(n instanceof HTMLElement)) continue;
      if (this._backdropEl && (n === this._backdropEl || this._backdropEl.contains(n))) continue;
      const vertical = this._isVerticallyScrollable(n);
      const horizontal = this._isHorizontallyScrollable(n);
      if (vertical || horizontal) return { el: n, vertical, horizontal };
    }
    return null;
  }

  private _isVerticallyScrollable(el: HTMLElement): boolean {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false;
    return el.scrollHeight > el.clientHeight + 1;
  }

  private _isHorizontallyScrollable(el: HTMLElement): boolean {
    const overflowX = window.getComputedStyle(el).overflowX;
    if (overflowX !== 'auto' && overflowX !== 'scroll' && overflowX !== 'overlay') return false;
    return el.scrollWidth > el.clientWidth + 1;
  }

  private _normalizeWheelDeltaY(e: WheelEvent, scroller: HTMLElement): number {
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) return e.deltaY * 16;
    if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) return e.deltaY * scroller.clientHeight;
    return e.deltaY;
  }

  private _normalizeWheelDeltaX(e: WheelEvent, scroller: HTMLElement): number {
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) return e.deltaX * 16;
    if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) return e.deltaX * scroller.clientWidth;
    return e.deltaX;
  }

  private _applyHostDocumentScrollLock(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const html = document.documentElement;
    if (!this._hostOverflowRestore) {
      const body = document.body;
      this._hostOverflowRestore = {
        htmlOverflow: html.style.overflow,
        bodyOverflow: body?.style.overflow ?? '',
        htmlOverscroll: html.style.overscrollBehavior,
        bodyOverscroll: body?.style.overscrollBehavior ?? '',
      };
      html.dataset['gengageHostScrollLock'] = '1';
      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      if (body) {
        body.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'none';
      }
      this._hostScrollLockViewport = { x: window.scrollX, y: window.scrollY };
    }
    if (this._hostScrollLockActive) return;
    window.addEventListener('touchmove', this._preventHostDocumentTouchMove, { capture: true, passive: false });
    window.addEventListener('wheel', this._preventHostDocumentWheel, { capture: true, passive: false });
    window.addEventListener('scroll', this._pinHostViewportScroll, { capture: true, passive: true });
    this._hostScrollLockActive = true;
  }

  private _syncChatHostOverlayPriority(active: boolean): void {
    const host = this.root as HTMLElement | undefined;
    if (!host?.style) return;
    if (active) {
      if (!this._hostOverlayRestore) {
        this._hostOverlayRestore = ['position', 'z-index', 'isolation'].map((name) => ({
          name,
          value: host.style.getPropertyValue(name),
          priority: host.style.getPropertyPriority(name),
        }));
      }
      host.dataset['gengageChatOverlayPriority'] = '1';
      const computedPosition =
        typeof window !== 'undefined' ? window.getComputedStyle(host).position : host.style.position;
      if (!computedPosition || computedPosition === 'static') {
        host.style.setProperty('position', 'relative', 'important');
      }
      host.style.setProperty('z-index', '2147483647', 'important');
      host.style.setProperty('isolation', 'isolate', 'important');
      return;
    }
    this._restoreChatHostOverlayPriority();
  }

  private _restoreChatHostOverlayPriority(): void {
    const host = this.root as HTMLElement | undefined;
    if (!host?.style) {
      this._hostOverlayRestore = null;
      return;
    }
    if (!this._hostOverlayRestore && host.dataset['gengageChatOverlayPriority'] !== '1') return;
    delete host.dataset['gengageChatOverlayPriority'];
    for (const entry of this._hostOverlayRestore ?? []) {
      if (entry.value) {
        host.style.setProperty(entry.name, entry.value, entry.priority);
      } else {
        host.style.removeProperty(entry.name);
      }
    }
    this._hostOverlayRestore = null;
  }

  private _releaseHostDocumentScrollLock(): void {
    if (typeof document === 'undefined') return;
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this._pinHostViewportScroll, { capture: true });
      window.removeEventListener('touchmove', this._preventHostDocumentTouchMove, { capture: true });
      window.removeEventListener('wheel', this._preventHostDocumentWheel, { capture: true });
    }
    this._hostScrollLockActive = false;
    this._hostScrollLockViewport = null;
    this._hostScrollPinning = false;
    const html = document.documentElement;
    const body = document.body;
    if (this._hostOverflowRestore) {
      html.style.overflow = this._hostOverflowRestore.htmlOverflow;
      html.style.overscrollBehavior = this._hostOverflowRestore.htmlOverscroll;
      if (body) {
        body.style.overflow = this._hostOverflowRestore.bodyOverflow;
        body.style.overscrollBehavior = this._hostOverflowRestore.bodyOverscroll;
      }
      this._hostOverflowRestore = null;
    } else if (html.dataset['gengageHostScrollLock'] === '1') {
      html.style.removeProperty('overflow');
      html.style.removeProperty('overscroll-behavior');
      body?.style.removeProperty('overflow');
      body?.style.removeProperty('overscroll-behavior');
    }
    delete html.dataset['gengageHostScrollLock'];
  }

  private _handleAttachment(file: File): void {
    const result = validateImageFile(file);
    if (!result.ok) {
      const message = result.reason === 'invalid_type' ? this._i18n.invalidFileType : this._i18n.fileTooLarge;
      dispatch('gengage:global:error', {
        message,
        source: 'chat' as const,
      });
      return;
    }
    this._drawer?.stageAttachment(file);
  }

  private _sendMessage(text: string, attachment?: File): void {
    this._dismissEphemeralWelcomeMessage();
    if (attachment === undefined) {
      if (
        (this._contextPrimingInFlight || this._isOpenPageContextPrimeScheduled()) &&
        detectFastIntent(text) === 'directAddToCart'
      ) {
        this._queuedUserMessages.push({ text });
        return;
      }
      if (this._maybeHandleDirectAddToCartCommand(text)) {
        return;
      }
    }
    if (this._contextPrimingInFlight) {
      this._abortAllActiveRequests();
      this._contextPrimingInFlight = false;
      this._queuedUserMessages = [];
    }

    this._ensureMobileFullSheetForPanel();
    ga.trackMessageSent();
    // Track conversation start on first user message in a new thread
    const hasUserMessages = this._messages.some((m) => m.role === 'user');
    if (!hasUserMessages) {
      ga.trackConversationStart();
    }
    debugLog('chat', 'sendMessage', {
      mode: this._assistantMode,
      hasAttachment: attachment !== undefined,
      textLength: text.length,
    });
    const action: ActionPayload =
      attachment !== undefined
        ? { title: text, type: this._modeController.resolveAttachmentActionType(), payload: text ? { text } : {} }
        : { title: text, type: 'user_message', payload: text };
    if (attachment !== undefined) {
      this._sendAction(action, { attachment });
    } else {
      this._sendAction(action);
    }
  }

  private _dismissEphemeralWelcomeMessage(): void {
    if (!this._ephemeralWelcomeMessageId) return;
    this._drawer?.removeMessageBubble(this._ephemeralWelcomeMessageId);
    this._ephemeralWelcomeMessageId = null;
  }

  private _flushQueuedUserMessages(): void {
    if (this._contextPrimingInFlight || this._queuedUserMessages.length === 0) return;
    const queued = [...this._queuedUserMessages];
    this._queuedUserMessages = [];
    for (const item of queued) {
      this._sendMessage(item.text, item.attachment);
    }
  }

  /** Apply ui_hints from the backend CONTEXT event — delegates to mode controller. */
  private _applyUiHints(): void {
    this._modeController.applyUiHints(this._drawer, this._i18n.inputPlaceholder, () => {
      this._clearChoicePrompter();
    });
  }

  /** Returns true if the redirect actually switched the assistant mode. */
  private _handleRedirectMetadata(redirectPayload: unknown): boolean {
    return this._modeController.handleRedirect(redirectPayload);
  }

  private _sendAction(action: ActionPayload, options?: SendActionOptions): void {
    if (!options?.isContextPrime && this._isOpenPageContextPrimeScheduled()) {
      this._cancelScheduledOpenPageContextPrime();
      this._markCurrentContextLaunchHandled();
    }
    if (!options?.skipFullSheet) {
      this._ensureMobileFullSheetForPanel();
    }
    // Cancel any running typewriter animation and TTS playback
    this._activeTypewriter?.cancel();
    this._activeTypewriter = null;
    this._activeTtsHandle?.stop();
    this._activeTtsHandle = null;

    // Defer actions until init completes
    if (!this._initComplete) {
      if (this._pendingActions.length < 10) {
        this._pendingActions.push({ action, options });
      }
      return;
    }

    if (action.type === 'navigate') {
      const payload = typeof action.payload === 'object' && action.payload ? action.payload : null;
      const url = payload && 'url' in payload ? String((payload as { url?: unknown }).url || '') : '';
      if (url && isSafeUrl(url)) {
        const newTab = payload && 'newTab' in payload ? (payload as { newTab?: unknown }).newTab : undefined;
        const params: { url: string; newTab?: boolean } = { url };
        if (typeof newTab === 'boolean') params.newTab = newTab;
        this._navigateFromAction(params);
        return;
      }
    }

    // Typed messages already dismiss this before intent handling. Keep the
    // common user-visible action path covered as well for starter pills,
    // openWithAction(), and the public sendAction() API. Silent context-prime
    // actions are not shopper turns and must not remove the presentation.
    if (!options?.silent) {
      this._dismissEphemeralWelcomeMessage();
    }

    // Remove ChoicePrompter on new action
    this._clearChoicePrompter();
    // Fresh user request: reset ChoicePrompter dismiss so it can show again on the next grid
    if (!options?.preservePanel) {
      clearChoicePrompterDismissState();
    }

    // Clear comparison mode when starting a new request (unless preservePanel).
    // Exception: getComparisonTable actions keep comparison state visible during
    // the request so the floating button and checkboxes stay as loading feedback.
    // State is cleared once the ComparisonTable UISpec actually renders in the panel.
    if (!options?.preservePanel && this._comparisonSelectMode && action.type !== 'getComparisonTable') {
      this._comparisonSelectMode = false;
      this._comparisonSelectedSkus = [];
    }

    // Clear local panel history on new requests (fresh panel context)
    if (!options?.preservePanel) {
      this._localPanelHistory = [];
    }

    // Branch deletion: if user is sending from a rewound position, prune future messages
    if (this._currentThreadId && this._lastThreadId && this._lastThreadId > this._currentThreadId) {
      const cutoff = this._currentThreadId;
      // Remove future messages from array
      const removed = this._messages.filter((m) => m.threadId !== undefined && m.threadId > cutoff);
      this._messages = this._messages.filter((m) => !m.threadId || m.threadId <= cutoff);
      // Remove their DOM nodes
      for (const msg of removed) {
        this._shadow?.querySelector(`[data-message-id="${escapeCssIdentifier(msg.id)}"]`)?.remove();
        this._panel?.deleteSnapshotForMessage(msg.id);
      }
      this._panel?.pruneThreadsAfter(cutoff);
      this._pruneThreadPillsAfter(cutoff);
      // Remove orphaned inline UISpec elements
      const orphanedUISpecs = this._shadow?.querySelectorAll(`[data-thread-id]`);
      orphanedUISpecs?.forEach((el) => {
        if (el instanceof HTMLElement && el.dataset['threadId'] && el.dataset['threadId'] > cutoff) {
          el.remove();
        }
      });
    }

    // Clear previous suggestion pills and input chips
    if (!options?.preservePills) {
      this._drawer?.setPills([]);
    }
    this._drawer?.clearInputAreaChips();

    let hostResponding = false;
    const setHostResponding = (next: boolean): void => {
      if (hostResponding === next) return;
      hostResponding = next;
      this._bridge?.send('isResponding', next);
    };

    // Notify host that assistant is responding
    setHostResponding(true);

    // Generate thread ID for this request-response cycle
    const threadId = uuidv7();
    this._currentThreadId = threadId;
    this._lastThreadId = threadId;
    // Preserve the active grid intent during product drilldowns. A product click
    // should not relabel an existing search-result panel as "similar products".
    if (this._panel && !CONTEXT_LAUNCH_TYPES.has(action.type)) {
      this._panel.lastActionType = action.type;
    }
    // For preservePanel actions (like/addToCart), don't overwrite _activeRequestThreadId
    // to avoid silencing concurrent streams. Instead, track validity locally.
    const isPreservePanel = options?.preservePanel === true;
    const isContextAutoLaunch = options?.silent === true && options?.isContextPrime === true;
    const isPdpAutoLaunch = isContextAutoLaunch && action.type === 'launchSingleProduct';
    if (!isPreservePanel) {
      this._activeRequestThreadId = threadId;
    }

    // Align presentation focus with this thread *before* appending the user bubble.
    // Otherwise addMessage()'s collapse pass uses the previous focus and marks the
    // new bubble as presentation-collapsed until a later setPresentationFocus — which
    // can yield wrong scroll targets (offsetTop on display:none) and “empty” transcript.
    if (!options?.silent && !isPreservePanel) {
      this._drawer?.setPresentationFocus(threadId);
    }

    // Add user message to UI (skip for silent/auto-launch actions)
    if (!options?.silent) {
      const userText =
        typeof action.payload === 'string'
          ? action.payload
          : typeof (action.payload as Record<string, unknown>)?.['text'] === 'string'
            ? ((action.payload as Record<string, unknown>)['text'] as string)
            : action.title;
      // Retry deduplication: skip adding a duplicate user bubble when retrying
      const lastMsg = this._messages.length > 0 ? this._messages[this._messages.length - 1] : undefined;
      const isDuplicate = lastMsg !== undefined && lastMsg.role === 'user' && lastMsg.content === userText;
      if (!isDuplicate) {
        // Dismiss any visible KVKK notice on the first user-visible action.
        // We also mark the account as approved here as a fallback for older
        // flows that may not have set the flag during initial notice display.
        if (!this._messages.some((m) => m.role === 'user') || this._drawer?.isKvkkBannerVisible()) {
          markKvkkShown(this.config.accountId);
          this._drawer?.hideKvkkBanner();
        }
        const userMsg = this._createMessage('user', userText);
        userMsg.threadId = threadId;
        if (options?.attachment !== undefined) {
          userMsg.attachment = options.attachment;
        }
        this._drawer?.addMessage(userMsg);
        if (options?.attachment !== undefined && this._modeController.isBeautyConsulting) {
          this._drawer?.setBeautyPhotoStepCard({ visible: false });
        }
        this._messages.push(userMsg);
        this._persistHistory();
      }
    }

    const shouldDetachUnavailableProductContext =
      !options?.silent &&
      this._assistantMode === 'shopping' &&
      this._hasUnavailableProductContext() &&
      (action.type === 'user_message' || action.type === 'inputText');

    // Preserve panel during the request — don't clear or show loading skeleton
    // until the backend explicitly signals new panel content (panelLoading event).
    // Exception: getComparisonTable shows the panel skeleton immediately (desktop + mobile)
    // so users see progress while the table streams in.
    // Captures the panel source (UISpec/kind) so it can be re-rendered with fresh
    // event listeners if a non-text action fails to deliver new panel content.
    let prePanelSource = this._currentPanelSource;
    let prePanelSourceCaptured = false;
    const shouldClearStalePanelOnEmptyResponse = action.type === 'user_message' || action.type === 'inputText';
    const capturePanelSourceIfNeeded = (): void => {
      if (prePanelSourceCaptured || options?.preservePanel) return;
      prePanelSource = this._currentPanelSource;
      prePanelSourceCaptured = true;
    };
    const restoreOrClearPanel = (): void => {
      if (!this._drawer?.isPanelLoading()) return;
      if (!shouldClearStalePanelOnEmptyResponse && prePanelSource) {
        const ctx = this._buildRenderContext();
        const el = this._renderPanelFromSource(prePanelSource, ctx);
        this._drawer.setPanelContent(el);
        this._currentPanelSource = prePanelSource;
      } else {
        this._drawer.clearPanel();
        this._currentPanelSource = null;
      }
      prePanelSource = null;
    };

    if (action.type === 'getComparisonTable') {
      this._drawer?.showPanelLoading('comparisonTable');
      this._panel?.updateTopBarForLoading('comparisonTable');
      if (this._isMobileViewport) {
        this._ensureMobilePanelOverlayVisible();
      }
    }

    if (!options?.silent && !isPreservePanel) {
      this._pruneEmptyStreamingAssistantPlaceholders();
    }

    // Show typing indicator
    this._drawer?.showTypingIndicator();
    // Use a local text accumulator per stream invocation to prevent corruption
    // when multiple concurrent preservePanel streams write to the same state.
    let localBotText = '';

    // Create bot message placeholder
    const botMsg = this._createMessage('assistant', '');
    botMsg.threadId = threadId;
    botMsg.status = 'streaming';
    if (options?.silent) botMsg.silent = true;
    // Note: silent flag only skips the USER message above — bot response is always rendered
    this._messages.push(botMsg);

    this._presentation.registerAssistantActivity(threadId);
    // User-visible non-preservePanel: focus was already set before the user bubble.
    this._focusPresentationThread(threadId, 'smooth', options?.silent || isPreservePanel);

    // Abort previous request(s) — skip for preservePanel to avoid killing concurrent streams
    if (!options?.preservePanel) {
      if (this._contextPrimingInFlight && !isContextAutoLaunch) {
        this._contextPrimingInFlight = false;
        this._queuedUserMessages = [];
      }
      this._abortAllActiveRequests();
    }

    const transport: ChatTransportConfig = {
      middlewareUrl: this.config.middlewareUrl,
      ...(this.config.accountId ? { accountId: this.config.accountId } : {}),
      // AGENTIC_ADAPTER_SEAM: include only when mounted so legacy clients keep the fetch path.
      ...(this.config.injectorAdapter ? { injectorAdapter: this.config.injectorAdapter } : {}),
    };
    if (options?.attachment !== undefined) {
      transport.attachment = options.attachment;
    }

    const visibleMessages = this._getVisibleMessages();
    const chatHistory = visibleMessages
      // Keep assistant messages even when empty (panel-only responses) so the
      // backend sees the proper alternating user/model turn structure.  Exclude
      // the current bot placeholder (just created, not yet populated).
      .filter((m) => !m.frontendOnly && m !== botMsg && (m.content || m.role === 'assistant'))
      .slice(-50)
      .map((m) => ({
        role: m.role === 'user' ? 'user' : 'model',
        content: m.content ?? '',
      }));

    // Build meta object for backend
    const meta: BackendRequestMeta = {
      outputLanguage: localeToOutputLanguage(this.config.locale),
      parentUrl: window.location.href,
      windowWidth: String(window.innerWidth),
      windowHeight: String(window.innerHeight),
      selfUrl: '',
      id: this.config.session?.sessionId ?? '',
      userId: this.config.session?.userId ?? '',
      appId: this.config.accountId,
      threads: [],
      createdAt: this._chatCreatedAt,
      kvkkApproved: isKvkkShown(this.config.accountId),
      voiceEnabled: this.config.voiceEnabled ?? false,
      threadId,
      isControlGroup: this.config.session?.abTestVariant === 'control',
      isMobile: this._isMobileViewport,
    };
    // If this is a demo website, set openPdpSimilars to true
    if (this.config.isDemoWebsite === true) {
      meta.openPdpSimilars = true;
    }
    if (!this._modeController.isShopping) {
      meta.assistantMode = this._modeController.mode;
    }
    if (this.config.session?.viewId !== undefined) {
      meta.viewId = this.config.session.viewId;
    }

    // Enrich action payload with fields the backend expects
    const enrichedAction = enrichActionPayload(action, {
      pageContext: this.config.pageContext,
      backendContext: this._lastBackendContext,
      isMobile: this._isMobileViewport,
      genericNonPdpLaunch: this.config.genericNonPdpLaunch,
    });

    const request: import('./api.js').ProcessActionRequest = {
      account_id: this.config.accountId,
      session_id: this.config.session?.sessionId ?? '',
      correlation_id: this.config.session?.sessionId ?? '',
      type: enrichedAction.type,
      locale: resolveLocaleTag(this.config.locale),
      meta,
      context: {
        // Spread backend context (panel, message_id, etc.) but preserve FE's
        // authoritative chatHistory — the backend's stale `messages` must not
        // overwrite the up-to-date conversation history built by the widget.
        ...(this._lastBackendContext ?? {}),
        messages: chatHistory,
        // Backend reads session_id from context.
        session_id: this.config.session?.sessionId ?? '',
      },
    };

    // Only set optional fields when values exist (exactOptionalPropertyTypes)
    if (this.config.session?.userId !== undefined) {
      request.user_id = this.config.session.userId;
    }
    if (this.config.session?.viewId !== undefined) {
      request.view_id = this.config.session.viewId;
    }
    if (enrichedAction.payload !== undefined) {
      request.payload = enrichedAction.payload;
    }
    if (!shouldDetachUnavailableProductContext && this.config.pageContext?.sku !== undefined) {
      request.sku = this.config.pageContext.sku;
    }
    if (!shouldDetachUnavailableProductContext && this.config.pageContext?.pageType !== undefined) {
      request.page_type = this.config.pageContext.pageType;
    }

    let panelLoadingSeen = false;
    let panelContentReceived = false;
    /** Desktop: panel shows ProductGrid / Categories — AI picks can render above instead of in chat. */
    let panelListEligibleForAiZone = false;
    let aiAnalysisUiReceivedForPanel = false;
    let streamDone = false;
    let primaryAnswerReady = false;
    const beautyStreamState = createBeautyStreamState();
    /** AITopPicks / AIGroupingCards often arrive before product_list; flush when grid mounts. */
    let pendingPanelAiSpec: UISpec | null = null;
    /**
     * Consulting style-picker grids may stream twice: once with some variations
     * still `loading`, then a final replace with everything `ready`. Rendering
     * the partial produces a visible skeleton→partial→final flash, so we hold
     * the partial here and only flush it if the stream ends without a fully
     * ready replacement (fallback so the shopper never sees an empty panel).
     */
    let pendingConsultingSpec: UISpec | null = null;

    const syncPanelAiAnalysisZone = (): void => {
      if (!this._drawer) return;
      // Keep rendered AITopPicks / AIGroupingCards in the panel AI zone; do not clear
      // them just because eligibility toggled mid-stream.
      if (aiAnalysisUiReceivedForPanel) return;
      if (!panelListEligibleForAiZone) {
        this._drawer.setPanelAiZoneState('hidden');
        return;
      }
      if (!streamDone) {
        this._drawer.setPanelAiZoneState('analyzing', { analyzingLabel: this._i18n.aiAnalysisAnalyzingLabel });
      } else {
        this._drawer.setPanelAiZoneState('hidden');
      }
    };

    const flushPendingPanelAiSpecToZone = (isStreaming: boolean): void => {
      if (!pendingPanelAiSpec || !this._drawer) return;
      const flushCtx = this._buildRenderContext();
      flushCtx.isStreaming = isStreaming;
      const aiEl = this._renderUISpec(pendingPanelAiSpec, flushCtx);
      aiAnalysisUiReceivedForPanel = true;
      this._drawer.setPanelAiZoneState('results', { resultEl: aiEl });
      pendingPanelAiSpec = null;
    };

    const syncPanelAiZoneAfterPanelUpdate = (
      componentType: string,
      panelAction: PanelUpdateAction,
      isStreaming: boolean,
    ): void => {
      if (componentType === 'ProductGrid' || componentType === 'CategoriesContainer') {
        panelListEligibleForAiZone = true;
        flushPendingPanelAiSpecToZone(isStreaming);
        syncPanelAiAnalysisZone();
        return;
      }
      if (panelAction !== 'appendSimilars' && panelAction !== 'append') {
        panelListEligibleForAiZone = false;
        aiAnalysisUiReceivedForPanel = false;
        pendingPanelAiSpec = null;
        this._drawer?.setPanelAiZoneState('hidden');
      }
    };

    const shouldPreserveAiZoneForPanelReplace = (componentType: string): boolean =>
      (componentType === 'ProductGrid' || componentType === 'CategoriesContainer') &&
      (panelListEligibleForAiZone || aiAnalysisUiReceivedForPanel || pendingPanelAiSpec !== null);

    const markPrimaryAnswerReady = (): void => {
      if (primaryAnswerReady) return;
      primaryAnswerReady = true;
      this._drawer?.removeTypingIndicator();
      setHostResponding(false);
      this._bridge?.send('loadingMessage', { text: null });
    };

    const replacePanelSpec = (
      panelSpec: UISpec,
      renderContext: ChatUISpecRenderContext,
      componentType: string,
    ): void => {
      if (!this._drawer || !this._panel) return;
      const rootForPatch = panelSpec.elements[panelSpec.root];
      if (componentType === 'ProductGrid' && rootForPatch) {
        const consultingPatch = detectConsultingGrid(rootForPatch);
        if (consultingPatch.isConsulting) {
          const existingPanel = this._drawer.getPanelContentElement();
          if (existingPanel && patchConsultingGridDom(existingPanel, consultingPatch, renderContext)) {
            this._comparisonSelectMode = false;
            this._comparisonSelectedSkus = [];
            this._comparisonSelectionWarning = null;
            this._drawer.setComparisonDockContent(null);
            this._currentPanelSource = { kind: 'spec', spec: panelSpec };
            this._panel.currentType = componentType;
            this._drawer.resyncPanelTopBarFromCurrentContent();
            return;
          }
        }
      }
      this._comparisonSelectMode = false;
      this._comparisonSelectedSkus = [];
      this._comparisonSelectionWarning = null;
      this._drawer.setComparisonDockContent(null);
      this._drawer.setPanelContent(this._renderUISpec(panelSpec, renderContext), {
        preserveAiZone: shouldPreserveAiZoneForPanelReplace(componentType),
      });
      this._currentPanelSource = { kind: 'spec', spec: panelSpec };
      this._panel.currentType = componentType;
    };

    const finalizePanelUpdate = (
      componentType: string,
      rootElement: UIElement | undefined,
      panelAction: PanelUpdateAction,
      isStreaming: boolean,
    ): void => {
      if (!this._panel) return;

      if (componentType === 'ProductDetailsPanel' && action.type === 'launchSingleProduct') {
        this._clearUnavailableProductContext();
      }

      if (botMsg.threadId && this._panel.threads[this._panel.threads.length - 1] !== botMsg.threadId) {
        this._panel.threads.push(botMsg.threadId);
      }
      if (this._isMobileViewport && (componentType === 'ProductGrid' || componentType === 'ComparisonTable')) {
        this._ensureMobilePanelOverlayVisible();
      }
      const titleType = this._panel.currentType ?? componentType;
      const backendTitle = rootElement?.props?.['panelTitle'] as string | undefined;
      this._panel.updateTopBar(titleType, backendTitle);
      this._panel.updateExtendedMode(componentType);
      if (this._isMobileViewport && isPdpAutoLaunch) {
        this._drawer?.hideMobilePanel();
      }
      syncPanelAiZoneAfterPanelUpdate(componentType, panelAction, isStreaming);
    };

    let streamController: AbortController | null = null;
    streamController = sendChatMessage(
      request,
      {
        onTextChunk: (content, isFinal, extra) => {
          if (!isPreservePanel && threadId !== this._activeRequestThreadId) return;
          localBotText += content;
          // Store enrichment data from outputText for downstream rendering
          if (extra?.skuToProductItem) {
            this._skuToProductItem = { ...this._skuToProductItem, ...extra.skuToProductItem };
          }
          if (extra?.renderHint) {
            botMsg.renderHint = extra.renderHint;
          }

          if (!this._drawer) return;

          // KVKK filtering: strip KVKK block from display text and show banner.
          // Backend explicitly flags KVKK content via `extra.kvkk` — always respect that.
          // Keyword fallback covers backends that omit the flag.
          let displayText = localBotText;
          const isExplicitKvkkContent = extra?.kvkk === true;
          const kvkkBlockOptions = isExplicitKvkkContent ? { fallbackToFirstBlock: true } : undefined;
          const isKvkkContent = isExplicitKvkkContent || containsKvkk(displayText);
          if (isKvkkContent) {
            // Show banner on the final chunk so the full extracted block is available.
            if (isFinal) {
              const acctId = this.config.accountId;
              if (!isKvkkShown(acctId)) {
                const kvkkHtml = extractKvkkBlock(displayText, kvkkBlockOptions);
                if (kvkkHtml) {
                  this._drawer?.showKvkkBanner(kvkkHtml);
                  markKvkkShown(acctId);
                } else {
                  // No KVKK block found — mark as shown so we don't re-check
                  markKvkkShown(acctId);
                }
              }
            }
            // Strip KVKK from every chunk (not just the final one) so it never
            // appears in the message bubble, even during streaming.
            displayText = stripKvkkBlock(displayText, kvkkBlockOptions);
            // Persist the stripped text, not the raw chunk, so a consent-only
            // event cannot leak back into later assistant bubbles.
            localBotText = displayText;
          }

          if (hasRenderableText(displayText)) {
            markPrimaryAnswerReady();
          }

          // Check if we already have a bot bubble in the DOM (query by message ID, not :last-child)
          const existingBubble = this._shadow?.querySelector(
            `[data-message-id="${botMsg.id}"] .gengage-chat-bubble-text`,
          );
          if (existingBubble) {
            existingBubble.innerHTML = sanitizeHtml(displayText);
          } else {
            botMsg.content = displayText;
            if (botMsg.role === 'assistant' && botMsg.threadId && !this._threadsWithFirstBot.has(botMsg.threadId)) {
              this._threadsWithFirstBot.add(botMsg.threadId);
              this._drawer.markFirstBotMessage(botMsg.id);
            }
            this._drawer.addMessage(botMsg);
          }

          if (isFinal) {
            botMsg.content = displayText;
            botMsg.status = 'done';
            ga.trackMessageReceived();

            // Photo analysis messages render a structured card — skip typewriter.
            if (isPhotoAnalysisMessage(botMsg)) {
              this._drawer?.updateBotMessage(botMsg.id, displayText, 'photo_analysis', botMsg.photoAnalysis);
              if (botMsg.threadId) {
                this._focusPresentationThread(botMsg.threadId, 'auto');
              }
            } else {
              // Apply typewriter animation to the final bot text
              const bubbleTextEl = this._shadow?.querySelector(
                `[data-message-id="${botMsg.id}"] .gengage-chat-bubble-text`,
              ) as HTMLElement | null;
              if (bubbleTextEl) {
                this._activeTypewriter?.cancel();
                const mentions = extra?.productMentions
                  ? this._enrichProductMentionsWithUrls(extra.productMentions)
                  : undefined;
                this._activeTypewriter = typewriteHtml({
                  container: bubbleTextEl,
                  html: sanitizeHtml(displayText),
                  onTick: () => this._drawer?.scrollToBottomIfNeeded(),
                  onComplete: () => {
                    this._activeTypewriter = null;
                    // Link product mentions after typewriter finishes
                    // AGENTIC_ADAPTER_SEAM: adapter events can provide the same mention metadata as legacy NDJSON.
                    if (mentions && mentions.length > 0 && bubbleTextEl) {
                      linkProductMentions({
                        container: bubbleTextEl,
                        mentions,
                        onProductClick: (mention) => {
                          const url = mention.url?.trim() ?? '';
                          if (url && isSafeUrl(url)) {
                            this._handleProductNavClick({
                              sku: mention.sku,
                              url,
                              name: mention.short_name,
                            });
                            return;
                          }
                          this._sendAction({
                            title: mention.short_name || mention.sku,
                            type: 'launchSingleProduct',
                            payload: { sku: mention.sku },
                          });
                        },
                      });
                    }
                  },
                });
              }
            }
          }
        },
        onUISpec: (spec, widget, panelHint, clearPanel) => {
          if (!isPreservePanel && threadId !== this._activeRequestThreadId) return;
          if (widget !== 'chat') return;

          // StreamEventUISpec.clearPanel: hide/clear the assistant panel. Do not use
          // restoreOrClearPanel() here — that restores the pre-loading snapshot while the skeleton
          // is shown and breaks intended panel-dismiss behavior.
          if (clearPanel) {
            this._clearAssistantPanelLikeStreamClearPanel();
            panelLoadingSeen = false;
            pendingConsultingSpec = null;
          }

          const rootElement = spec.elements[spec.root];
          const componentType = rootElement?.type ?? 'unknown';

          // Beauty consulting UISpec components — delegated to feature handler.
          if (
            handleBeautyUISpec(
              componentType,
              rootElement?.props ?? {},
              beautyStreamState,
              {
                drawer: this._drawer,
                ensureRendered: () => this._ensureAssistantMessageRendered(botMsg),
                cancelTypewriter: () => {
                  this._activeTypewriter?.cancel();
                  this._activeTypewriter = null;
                },
                sendSkipMessage: () => this._sendMessage(this._i18n.beautyPhotoStepSkipMessage),
                isStreamDone: () => streamDone,
              },
              botMsg,
            )
          ) {
            if (componentType === 'PhotoAnalysisCard' && botMsg.threadId) {
              this._focusPresentationThread(botMsg.threadId, 'auto');
            }
            return;
          }

          const similarsAppendGrid = isSimilarsAppendGrid(rootElement);
          /** PDP flow with side panel disabled: render details and similar-product grids only in chat. */
          const skipSidePanelForUISpec =
            this.config.productDetailsExtended !== true &&
            (componentType === 'ProductDetailsPanel' || similarsAppendGrid);
          if (skipSidePanelForUISpec && !clearPanel) {
            this._clearAssistantPanelLikeStreamClearPanel();
            panelLoadingSeen = false;
          }
          const effectivePanelHint =
            componentType === 'ProductDetailsPanel' && panelHint !== 'panel' ? ('panel' as const) : panelHint;
          const renderContext = this._buildRenderContext();
          renderContext.isStreaming = true;

          // GA dataLayer: track component-specific events
          if (componentType === 'ComparisonTable') {
            const products = rootElement?.props?.['products'];
            ga.trackCompareReceived(Array.isArray(products) ? products.length : 0);
          }
          if (componentType === 'ProductGrid') {
            const childCount = rootElement?.children?.length ?? 0;
            ga.trackSearch(undefined, childCount);
          }

          const panelSpec = effectivePanelHint === 'panel' && this._panel ? this._panel.toPanelSpec(spec) : spec;

          // Consulting style-picker gate: wait only until at least one variation
          // is not `loading` (fast-first). Further `loading` tabs stream in via
          // `patchConsultingGridDom` without replacing the whole panel. If every
          // variation is still `loading`, keep the skeleton and buffer in
          // `pendingConsultingSpec`. Only the top-level (non-append) panel path
          // is gated — similars-append and pure append paths are unaffected.
          if (
            effectivePanelHint === 'panel' &&
            this._panel &&
            !skipSidePanelForUISpec &&
            componentType === 'ProductGrid' &&
            rootElement
          ) {
            const consultingResult = detectConsultingGrid(rootElement);
            if (consultingResult.isConsulting && !isConsultingGridReady(consultingResult)) {
              pendingConsultingSpec = spec;
              return;
            }
            if (consultingResult.isConsulting) {
              pendingConsultingSpec = null;
            }
          }

          const primaryComponentType = getPrimaryAnswerComponentType(rootElement);
          if (primaryComponentType) {
            markPrimaryAnswerReady();
          }

          if (effectivePanelHint === 'panel' && this._panel && !skipSidePanelForUISpec) {
            const isFirstPanelContentInStream = !panelContentReceived;
            panelContentReceived = true;
            const forceReplacePanel = rootElement?.props?.['replacePanel'] === true;
            // search-revision: when the backend emits a ProductList with
            // rankingState === 'pending' (fast native result) followed by
            // rankingState === 'final' (post-LLM-rank result) — both share a
            // sequenceId — we always REPLACE so the final version swaps in
            // cleanly over the blurred pending one. Non-search-revision flows
            // are unaffected (rankingState is absent).
            const rankingState = rootElement?.props?.['rankingState'];
            const forceReplaceForRanking = rankingState === 'pending' || rankingState === 'final';

            const panelAction =
              forceReplacePanel || forceReplaceForRanking
                ? 'replace'
                : determinePanelUpdateAction({
                    componentType,
                    similarsAppend: rootElement?.props?.['similarsAppend'] === true,
                    currentPanelType: this._panel.currentType,
                    // PDP auto-launch hides the panel on mobile, but its details
                    // must still be present when the following similars grid arrives.
                    hasPanelContent:
                      rootElement?.props?.['similarsAppend'] === true
                        ? this._drawer?.getPanelContentElement() != null
                        : (this._drawer?.hasPanelContent() ?? false),
                    isPanelLoading: this._drawer?.isPanelLoading() ?? false,
                    isFirstPanelContentInStream,
                  });

            renderContext.panelProductListHeading = undefined;
            if (componentType === 'ProductGrid') {
              if (panelAction === 'appendSimilars') {
                renderContext.panelProductListHeading = this._i18n.similarProductsLabel ?? 'Similar Products';
              } else {
                this._applyPanelListHeadingToContext(renderContext, { kind: 'spec', spec: panelSpec });
              }
            }

            if (panelAction === 'appendSimilars') {
              this._appendSimilarsToPanel(panelSpec, renderContext);
            } else if (panelAction === 'append') {
              this._drawer?.appendPanelContent(this._renderUISpec(panelSpec, renderContext));
              if (this._comparisonSelectMode) {
                this._refreshComparisonUI();
              }
            } else {
              replacePanelSpec(panelSpec, renderContext, componentType);
            }
            finalizePanelUpdate(componentType, rootElement, panelAction, true);
          }

          // ProductDetailsPanel goes to the panel, but also render a compact
          // horizontal ProductSummaryCard in chat messages (production parity
          // with the prior engine's LaunchSingleProduct component).
          // Silent PDP auto-prime (`isPdpAutoLaunch`) still uses a silent bot row for
          // history, but users should see the same inline summary + chip row as when
          // they open a product from chat (non-silent launchSingleProduct).
          if (
            componentType === 'ProductDetailsPanel' &&
            effectivePanelHint === 'panel' &&
            (!botMsg.silent || isPdpAutoLaunch)
          ) {
            const product = rootElement?.props?.['product'] as Record<string, unknown> | undefined;
            if (product) {
              this._currentInlineProductSummaryRecord = product;
              const inlineSpec: UISpec = {
                root: 'root',
                elements: {
                  root: {
                    type: 'ProductSummaryCard',
                    props: { product },
                  },
                },
              };
              const messagesContainer = this._shadow?.querySelector('.gengage-chat-messages');
              if (messagesContainer) {
                const inline = this._renderUISpec(inlineSpec, renderContext);
                if (botMsg.threadId) {
                  inline.dataset['threadId'] = botMsg.threadId;
                }
                const bubble = this._shadow?.querySelector(`[data-message-id="${botMsg.id}"]`) as HTMLElement | null;
                if (bubble && bubble.parentNode === messagesContainer) {
                  bubble.after(inline);
                } else {
                  messagesContainer.appendChild(inline);
                }
                this._scrollInlineIntoView(inline, botMsg.threadId);
                this._drawer?.refreshPresentationCollapsed();
                panelContentReceived = true;
              }
            }
          }

          const isAiAnalysisComponent = componentType === 'AITopPicks' || componentType === 'AIGroupingCards';
          const isInlineQuestionButtons = componentType === 'InlineQuestionButtons';
          const actionButtons = componentType === 'ActionButtons' ? rootElement?.props?.['buttons'] : undefined;
          const shouldInlineQuestionActionButtons =
            componentType === 'ActionButtons' &&
            this._modeController.mode !== 'shopping' &&
            Array.isArray(actionButtons) &&
            actionButtons.length > 0 &&
            actionButtons.every((btn) => {
              const action = (btn as Record<string, unknown>)['action'] as Record<string, unknown> | undefined;
              return action?.['type'] === 'inputText';
            });
          let routeAiAnalysisToPanel = false;
          let deferAiPanelUntilGrid = false;
          if (skipSidePanelForUISpec && similarsAppendGrid) {
            renderContext.panelProductListHeading = this._i18n.similarProductsLabel ?? 'Similar Products';
          }
          if (isAiAnalysisComponent && (!botMsg.silent || isContextAutoLaunch)) {
            if (botMsg.threadId) {
              this._rememberPanelAiSpec(botMsg.threadId, spec);
            }
            if (panelListEligibleForAiZone) {
              const aiEl = this._renderUISpec(spec, renderContext);
              aiAnalysisUiReceivedForPanel = true;
              this._drawer?.setPanelAiZoneState('results', { resultEl: aiEl });
              routeAiAnalysisToPanel = true;
              pendingPanelAiSpec = null;
            } else {
              pendingPanelAiSpec = spec;
              deferAiPanelUntilGrid = true;
            }
          }

          if (isInlineQuestionButtons) {
            this._drawer?.setInputAreaChips([]);
            this._drawer?.setPills([]);
          }

          const inlineOkWhenSilentPrime =
            isContextAutoLaunch &&
            (componentType === 'GroundingReviewCard' ||
              isAiAnalysisComponent ||
              componentType === 'InlineQuestionButtons');
          const shouldRenderInline =
            !isAiAnalysisComponent &&
            (!botMsg.silent || inlineOkWhenSilentPrime) &&
            (effectivePanelHint !== 'panel' ||
              componentType === 'ProductCard' ||
              (skipSidePanelForUISpec && componentType === 'ProductGrid' && !similarsAppendGrid)) &&
            (componentType !== 'ActionButtons' || shouldInlineQuestionActionButtons) &&
            !routeAiAnalysisToPanel &&
            !(deferAiPanelUntilGrid && isAiAnalysisComponent);

          if (shouldRenderInline) {
            const messagesContainer = this._shadow?.querySelector('.gengage-chat-messages');
            if (messagesContainer) {
              const inline = this._renderUISpec(spec, renderContext);
              if (botMsg.threadId) {
                inline.dataset['threadId'] = botMsg.threadId;
              }
              messagesContainer.appendChild(inline);
              this._scrollInlineIntoView(inline, botMsg.threadId);
              this._drawer?.refreshPresentationCollapsed();
              if (skipSidePanelForUISpec && componentType === 'ProductGrid') {
                panelContentReceived = true;
              }
            }
          }

          // Track viewed products so comparison affordances can reflect cross-grid history.
          if ((componentType === 'ProductGrid' || componentType === 'ProductCard') && botMsg.threadId) {
            const childIds = rootElement?.children ?? [];
            const products =
              componentType === 'ProductGrid'
                ? (childIds
                    .map((id) => spec.elements[id]?.props?.['product'] as Record<string, unknown> | undefined)
                    .filter(Boolean) as Record<string, unknown>[])
                : ([rootElement?.props?.['product'] as Record<string, unknown> | undefined].filter(Boolean) as Record<
                    string,
                    unknown
                  >[]);

            for (const product of products) {
              const sku = product['sku'] as string | undefined;
              if (sku) {
                this._viewedProductSkus.add(sku);
              }
            }
          }

          // Send preview images to host for launcher thumbnails
          if (componentType === 'ProductGrid' || componentType === 'ProductDetailsPanel') {
            const previewProducts =
              componentType === 'ProductGrid'
                ? ((rootElement?.children ?? [])
                    .map((id) => spec.elements[id]?.props?.['product'] as Record<string, unknown> | undefined)
                    .filter(Boolean) as Record<string, unknown>[])
                : ([
                    (rootElement?.props?.['product'] ?? rootElement?.props) as Record<string, unknown> | undefined,
                  ].filter(Boolean) as Record<string, unknown>[]);
            const previewImageUrls = previewProducts
              .map((p) => p['imageUrl'] as string | undefined)
              .filter((url): url is string => typeof url === 'string')
              .slice(0, 5);
            if (previewImageUrls.length > 0) {
              this._bridge?.send('previewImages', { images: previewImageUrls });
            }
          }

          // ChoicePrompter: panel ProductGrid with 2+ products, comparison mode off
          const productGridChildCount = rootElement?.children?.length ?? 0;
          if (
            componentType === 'ProductGrid' &&
            effectivePanelHint === 'panel' &&
            !skipSidePanelForUISpec &&
            productGridChildCount > 1 &&
            !this._isMobileViewport &&
            !this._modeController.isChoicePrompterHidden &&
            !this._comparisonSelectMode &&
            !isChoicePrompterDismissed(this._currentThreadId ?? '')
          ) {
            this._clearChoicePrompter();
            this._choicePrompterEl = createChoicePrompter({
              heading: this._i18n.choicePrompterHeading,
              suggestion: this._i18n.choicePrompterSuggestion,
              ctaLabel: this._i18n.choicePrompterCta,
              threadId: this._currentThreadId ?? '',
              dismissAriaLabel: this._i18n.dismissAriaLabel,
              onCtaClick: () => {
                this._comparisonSelectMode = true;
                this._choicePrompterEl = null;
                this._refreshComparisonUI();
              },
              onDismiss: () => {
                this._choicePrompterEl = null;
              },
            });
            this._mountChoicePrompter();
          }

          // ActionButtons from the stream render as the suggestion pill row only.
          // CSH/BE already owns the full action set there; do not split review/similar
          // types into the composer input-chip row (that duplicate row is redundant).
          if (componentType === 'ActionButtons') {
            const buttons = rootElement?.props?.['buttons'] as
              | Array<{
                  label: string;
                  action: ActionPayload;
                  icon?: string;
                  image?: string;
                  description?: string;
                }>
              | undefined;
            if (buttons && buttons.length > 0 && !shouldInlineQuestionActionButtons) {
              this._drawer?.setInputAreaChips([]);
              this._rememberThreadPills(threadId, buttons);
              this._applyStoredThreadPills(threadId);
            }
          }

          syncPanelAiAnalysisZone();
          botMsg.uiSpec = spec;
        },
        onAction: (event: StreamEvent) => {
          if (!isPreservePanel && threadId !== this._activeRequestThreadId) return;
          if (event.type === 'action') {
            const routerOpts: ActionRouterOptions = {};
            if (this.config.actionHandling?.unknownActionPolicy !== undefined) {
              routerOpts.unknownActionPolicy = this.config.actionHandling.unknownActionPolicy;
            }
            if (this.config.actionHandling?.allowScriptCall !== undefined) {
              routerOpts.allowScriptCall = this.config.actionHandling.allowScriptCall;
            }
            routeStreamAction(
              event as StreamEventAction,
              {
                openChat: () => this.open(),
                navigate: (params) => {
                  this._navigateFromAction(params);
                },
                saveSession: (params) => this.saveSession(params.sessionId, params.sku),
                addToCart: (params) => {
                  void this._runChatAddToCartFlow(params);
                },
                scriptCall: (params) => {
                  dispatch('gengage:chat:script-call', params);
                  this.config.onScriptCall?.(params);
                },
              },
              routerOpts,
            );
          }
        },
        onMetadata: (event: StreamEvent) => {
          if (!isPreservePanel && threadId !== this._activeRequestThreadId) return;
          if (event.type === 'metadata' && event.meta) {
            // Store backend context for sending with next request
            if (
              event.meta.panel !== undefined ||
              event.meta.messages !== undefined ||
              event.meta.message_id !== undefined
            ) {
              this._lastBackendContext = event.meta as import('../common/types.js').BackendContext;
              const panel = asRecord(event.meta.panel);
              if (panel) {
                this._modeController.updateFromContext(panel);
                this._applyUiHints();
              }
            }

            // Panel loading indicator
            if (event.meta.panelLoading) {
              const pendingType =
                typeof event.meta.panelPendingType === 'string' ? event.meta.panelPendingType : undefined;
              const suppressProductDetailsSkeleton =
                this.config.productDetailsExtended !== true &&
                (pendingType === 'productDetails' || pendingType === 'productDetailsSimilars');
              if (!suppressProductDetailsSkeleton) {
                panelLoadingSeen = true;
                panelContentReceived = false;
                // Snapshot current panel before replacing with skeleton
                capturePanelSourceIfNeeded();
                if (this._panel) this._panel.currentType = null;
                this._drawer?.showPanelLoading(pendingType);
                if (this._isMobileViewport && pendingType === 'productList') {
                  this._ensureMobilePanelOverlayVisible();
                }
                // Set panel topbar title immediately so it's not an empty white bar
                if (pendingType) {
                  this._panel?.updateTopBarForLoading(pendingType);
                }
              }
            }

            // Optional voice payload emitted by backend when voiceEnabled is true.
            if (event.meta.voice) {
              // Dispatch cancelable event — host can call preventDefault() to suppress built-in playback
              const voiceEvent = new CustomEvent('gengage:chat:voice', {
                detail: { payload: event.meta.voice },
                bubbles: false,
                cancelable: true,
              });
              const allowed = window.dispatchEvent(voiceEvent);
              ga.trackVoiceInput();

              // Built-in TTS playback (skipped if host called preventDefault)
              if (allowed) {
                const voicePayload = event.meta.voice as { audio_base64?: string; content_type?: string };
                if (voicePayload.audio_base64) {
                  this._activeTtsHandle?.stop();
                  this._activeTtsHandle = playTtsAudio(
                    voicePayload.audio_base64,
                    voicePayload.content_type ?? 'audio/ogg',
                  );
                }
              }
            }

            if (event.meta.redirectTarget || event.meta.redirect) {
              dispatch('gengage:chat:redirect', {
                target: event.meta.redirectTarget ?? null,
                payload: event.meta.redirect ?? null,
              });
              debugLog('redirect', 'redirect dispatched', {
                target: event.meta.redirectTarget ?? null,
              });
              // A successful redirect switches the assistant mode; re-apply ui_hints
              // so attachment controls / placeholder / choice-prompter reflect the new
              // mode immediately. Defensive: backend normally sends a trailing CONTEXT
              // that re-syncs, but this closes the no-CONTEXT gap.
              if (this._handleRedirectMetadata(event.meta.redirect)) {
                this._applyUiHints();
              }
            }

            // Thinking step messages — backend may send an accumulated array,
            // but loading surfaces show the latest current step only.
            if (event.meta.loading) {
              const thinkingMessages = Array.isArray(event.meta.thinkingMessages)
                ? event.meta.thinkingMessages.filter((item): item is string => typeof item === 'string')
                : [];
              const loadingText = typeof event.meta.loadingText === 'string' ? event.meta.loadingText : undefined;
              if (thinkingMessages.length > 0) {
                // Beauty/account-expert modes: condense long thinking lists to 2 steps + loading text.
                // Standard shopping mode: show all thinking messages unmodified.
                const normalizedThinking =
                  loadingText && this._modeController.shouldCondenseThinking()
                    ? [...thinkingMessages.slice(0, 2), loadingText]
                    : thinkingMessages;
                this._drawer?.setThinkingSteps(normalizedThinking);
              }
              if (typeof loadingText === 'string' && loadingText.length > 0) {
                this._drawer?.addThinkingStep(loadingText);
                if (!primaryAnswerReady) {
                  this._bridge?.send('loadingMessage', { text: loadingText });
                }
              }
              // Multi-round turns keep emitting `loading` after the first
              // answer rendered (grid renders round 1, then top-picks /
              // further planner rounds run for several seconds).
              // `markPrimaryAnswerReady` tore the typing indicator down, so
              // those steps would otherwise update silently. Re-establish it
              // while the stream is still open; it is torn down again on
              // stream completion (onDone/onError).
              if (primaryAnswerReady && !streamDone) {
                this._drawer?.resumeTypingIndicator();
              }
            }

            dispatch('gengage:chat:metadata', { payload: event.meta });
          }
        },
        onError: (err) => {
          if (streamController) this._abortControllers.delete(streamController);
          // Skip error handling for aborted/superseded requests (including when no active request)
          if (!isPreservePanel && threadId !== this._activeRequestThreadId) return;
          streamDone = true;
          this._activeTypewriter?.cancel();
          this._activeTypewriter = null;
          syncPanelAiAnalysisZone();
          pendingPanelAiSpec = null;
          pendingConsultingSpec = null;
          setHostResponding(false);
          this._bridge?.send('loadingMessage', { text: null });
          this._drawer?.removeTypingIndicator();
          this._drawer?.clearInputAreaChips();
          flushBeautyStreamError(beautyStreamState, {
            drawer: this._drawer,
            ensureRendered: () => {},
            cancelTypewriter: () => {},
            sendSkipMessage: () => this._sendMessage(this._i18n.beautyPhotoStepSkipMessage),
            isStreamDone: () => true,
          });
          // Capture panel state before resetting — needed for error gating below
          const hadPanelContent = panelContentReceived;
          if (panelLoadingSeen && !panelContentReceived) restoreOrClearPanel();
          panelLoadingSeen = false;
          this._maybeHidePreservedMobilePanel(action.type, hadPanelContent);
          panelContentReceived = false;
          // When the stream already delivered partial content (bot text, panel, etc.),
          // still show backend error text + recovery pills — not only clear stale chips.
          const hasVisibleContent =
            (botMsg.content != null && botMsg.content.length > 0) || localBotText.length > 0 || hadPanelContent;
          const hasContent = botMsg.silent || hasVisibleContent;
          const shouldSuppressOfflineError =
            typeof navigator !== 'undefined' && navigator.onLine === false && isLikelyConnectivityIssue(err);

          const removeAssistantPlaceholderBubble = (): void => {
            this._shadow?.querySelector(`[data-message-id="${escapeCssIdentifier(botMsg.id)}"]`)?.remove();
            const idx = this._messages.indexOf(botMsg);
            if (idx >= 0) this._messages.splice(idx, 1);
          };

          let placeholderBubbleRemoved = false;

          const applyStreamErrorRecovery = (): void => {
            if (shouldSuppressOfflineError) return;
            this.emit('error', err);
            const errMsg = err.message;
            if (errMsg === this._lastErrorMessage) {
              this._consecutiveErrorCount++;
            } else {
              this._consecutiveErrorCount = 1;
              this._lastErrorMessage = errMsg;
            }
            const connectivityIssue = isLikelyConnectivityIssue(err);
            const backendDetail = err.message.trim();
            // Transport errors ("Failed to fetch") are browser English, not backend copy.
            const displayText = connectivityIssue
              ? getGlobalErrorMessage(this.config.locale, err)
              : backendDetail.length > 0
                ? backendDetail
                : this._i18n.errorMessage;
            // Bind Retry to THIS stream's action via the _sendAction closure params,
            // not this._lastSentAction. A concurrent background like/addToCart (which
            // runs without aborting the in-flight stream) or a silent prime can
            // overwrite _lastSentAction before this error even fires, so reading the
            // shared field — even eagerly here — could replay the wrong action.
            const retryAction = action;
            const retryOptions = options;
            const recoveryActions = {
              onRetry: () => {
                this._sendAction(retryAction, retryOptions);
              },
              onNewQuestion: () => {
                this._drawer?.focusInput();
              },
            };

            // A repeated network failure is still retryable, not an inactive account.
            if (this._consecutiveErrorCount >= 2 && !connectivityIssue) {
              removeAssistantPlaceholderBubble();
              placeholderBubbleRemoved = true;
              this._drawer?.showErrorWithRecovery(this._i18n.accountInactiveMessage, recoveryActions);
              return;
            }

            if (shouldShowStreamErrorAsRedStrip(err, displayText)) {
              removeAssistantPlaceholderBubble();
              placeholderBubbleRemoved = true;
              this._drawer?.showErrorWithRecovery(displayText, recoveryActions);
              return;
            }

            botMsg.content = displayText;
            botMsg.status = 'done';
            const bubbleHtml = sanitizeHtml(displayText.replace(/\r\n/g, '\n').split('\n').join('<br />'));
            this._ensureAssistantMessageRendered(botMsg);
            this._drawer?.updateBotMessage(botMsg.id, bubbleHtml);
            this._drawer?.showRecoveryPillsOnly(recoveryActions);
          };

          if (isContextAutoLaunch && !hasVisibleContent) {
            if (isPdpAutoLaunch || this._hasUnavailableProductContext()) {
              this._drawer?.setPills([]);
              const fallback = this._i18n.productNotFoundMessage;
              botMsg.content = fallback;
              botMsg.status = 'done';
              this._ensureAssistantMessageRendered(botMsg);
              this._drawer?.updateBotMessage(botMsg.id, fallback);
              this._markUnavailableProductContext();
            } else {
              botMsg.status = 'done';
            }
          } else if (!hasContent) {
            applyStreamErrorRecovery();
            if (shouldSuppressOfflineError) {
              return;
            }
          } else {
            this._drawer?.setPills([]);
            if (!botMsg.silent) {
              applyStreamErrorRecovery();
            }
          }
          if (isContextAutoLaunch) {
            this._contextPrimingInFlight = false;
            this._flushQueuedUserMessages();
          }
          if (!placeholderBubbleRemoved && botMsg.status === 'streaming') {
            botMsg.status = 'error';
          }
        },
        onDone: () => {
          if (streamController) this._abortControllers.delete(streamController);
          // Skip cleanup for aborted/superseded requests
          if (!isPreservePanel && threadId !== this._activeRequestThreadId) return;
          streamDone = true;
          // Consulting fallback: the backend never delivered a fully-ready
          // style-picker replacement, so flush the last partial spec now so
          // the shopper isn't left staring at a skeleton. Single render →
          // still no flash.
          if (pendingConsultingSpec && this._panel && this._drawer) {
            const fallbackCtx = this._buildRenderContext();
            fallbackCtx.isStreaming = false;
            const fallbackRoot = pendingConsultingSpec.elements[pendingConsultingSpec.root];
            const fallbackPanelSpec = this._panel.toPanelSpec(pendingConsultingSpec);
            this._applyPanelListHeadingToContext(fallbackCtx, { kind: 'spec', spec: fallbackPanelSpec });
            const fallbackType = fallbackRoot?.type ?? 'ProductGrid';
            replacePanelSpec(fallbackPanelSpec, fallbackCtx, fallbackType);
            finalizePanelUpdate(fallbackType, fallbackRoot, 'replace', false);
            panelContentReceived = true;
          }
          pendingConsultingSpec = null;
          // product_list never arrived but AI analysis widgets were deferred — keep them in the main panel AI zone, not the chat column
          if (pendingPanelAiSpec) {
            flushPendingPanelAiSpecToZone(false);
          }
          syncPanelAiAnalysisZone();
          if (!isPreservePanel && threadId === this._activeRequestThreadId) {
            this._activeRequestThreadId = null;
          }
          // Reset consecutive error counter on successful stream completion
          this._consecutiveErrorCount = 0;
          this._lastErrorMessage = '';
          setHostResponding(false);
          this._bridge?.send('loadingMessage', { text: null });
          this._drawer?.removeTypingIndicator();
          flushBeautyStreamComplete(beautyStreamState, {
            drawer: this._drawer,
            ensureRendered: () => {},
            cancelTypewriter: () => {},
            sendSkipMessage: () => this._sendMessage(this._i18n.beautyPhotoStepSkipMessage),
            isStreamDone: () => true,
          });
          const hadPanelContent = panelContentReceived;
          if (panelLoadingSeen && !panelContentReceived) restoreOrClearPanel();
          panelLoadingSeen = false;
          this._maybeHidePreservedMobilePanel(action.type, hadPanelContent);
          // Detect failed PDP auto-launch: silent launch that produced no visible
          // content. Show a soft fallback so the shopper isn't left with an empty chat.
          if (isPdpAutoLaunch && !localBotText && !hadPanelContent) {
            const fallback = this._i18n.productNotFoundMessage;
            botMsg.content = fallback;
            this._ensureAssistantMessageRendered(botMsg);
            this._drawer?.updateBotMessage(botMsg.id, fallback);
            this._markUnavailableProductContext();
          }
          panelContentReceived = false;
          if (isContextAutoLaunch) {
            this._contextPrimingInFlight = false;
            const hadQueuedMessages = this._queuedUserMessages.length > 0;
            this._flushQueuedUserMessages();
            if (!hadQueuedMessages && isPdpAutoLaunch) {
              this._ensurePdpPrimeSuggestedUiIfNeeded();
            }
          }

          if (botMsg.status === 'streaming') {
            botMsg.status = 'done';
            ga.trackMessageReceived();
          }
          this._presentation.finalizeAssistantGroup(threadId);

          // Reveal the comparison toggle button (hidden during streaming) with fade-in
          const hiddenCompareBtn = this._shadow?.querySelector('.gengage-chat-comparison-toggle-btn--hidden');
          if (hiddenCompareBtn) {
            hiddenCompareBtn.classList.remove('gengage-chat-comparison-toggle-btn--hidden');
            hiddenCompareBtn.classList.add('gengage-chat-comparison-toggle-btn--reveal');
          }

          this.emit('message', botMsg);

          // Snapshot current panel content for this message's history.
          // Pass a rebuild function so restored panels have live event listeners.
          const panelSource = this._currentPanelSource;
          const frozenPanelSource = panelSource ? this._clonePanelSourceForHistory(panelSource) : null;
          this._panel?.snapshotForMessage(
            botMsg.id,
            frozenPanelSource
              ? {
                  source: frozenPanelSource,
                  threadId: botMsg.threadId,
                  rebuild: () => {
                    const ctx = this._buildRenderContext();
                    return this._renderPanelFromSource(frozenPanelSource, ctx);
                  },
                }
              : undefined,
          );
          this._maybeAddPanelRestoreMessage(botMsg);

          this._persistConversationState();
        },
      },
      transport,
    );
    this._abortControllers.add(streamController);

    // Show "Stop generating" button for user-visible streams
    if (!options?.silent && !isPreservePanel) {
      const ctrl = streamController;
      this._drawer?.showStopButton(() => {
        ctrl.abort();
        this._abortControllers.delete(ctrl);
        this._drawer?.removeTypingIndicator();
        setHostResponding(false);
        this._bridge?.send('loadingMessage', { text: null });
        // Mirror onDone/onError cleanup: if a panel skeleton was shown but no
        // panel content ever arrived, restore/clear it so the loading skeleton
        // isn't left stuck after the user stops generation. restoreOrClearPanel()
        // self-guards on isPanelLoading().
        if (panelLoadingSeen && !panelContentReceived) {
          restoreOrClearPanel();
          panelLoadingSeen = false;
        }
        if (botMsg.status === 'streaming') {
          botMsg.status = 'done';
        }
      });
    }
  }

  /** Return messages visible at the current thread cursor. */
  private _getVisibleMessages(): ChatMessage[] {
    const msgs = this._messages.filter((m) => !m.silent);
    if (!this._currentThreadId) return msgs;
    const cutoff = this._currentThreadId;
    return msgs.filter((m) => !m.threadId || m.threadId <= cutoff);
  }

  /** Handle rollback-on-click from a user message bubble. */
  private _appendSimilarsToPanel(spec: UISpec, ctx: import('../chat/types.js').ChatUISpecRenderContext): void {
    if (!this._drawer) return;
    const panelEl = this._drawer.getPanelContentElement();
    if (!panelEl) return;
    ctx.panelProductListHeading = this._i18n.similarProductsLabel ?? 'Similar Products';
    const grid = this._renderUISpec(spec, ctx);
    grid.classList.add('gengage-chat-product-details-similars');
    panelEl.appendChild(grid);
    this._mergePanelSourceWithSimilars(spec);
  }

  /** Normalize product SKU for comparisons (wire may send string or number). */
  private _coerceSkuKey(raw: unknown): string {
    if (typeof raw === 'string') return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw).trim();
    return '';
  }

  private _productSkuKey(product: Record<string, unknown> | undefined): string {
    if (!product) return '';
    return this._coerceSkuKey(product['sku']);
  }

  private _pdpPageContextSkuKey(): string | null {
    const pt = this.config.pageContext?.pageType;
    if (typeof pt !== 'string' || pt.toLowerCase() !== 'pdp') return null;
    const key = this._coerceSkuKey(this.config.pageContext?.sku);
    return key || null;
  }

  private _productDetailsRecordFromSpec(spec: UISpec | null | undefined): Record<string, unknown> | null {
    if (!spec) return null;
    const root = spec.elements[spec.root];
    if (!root || root.type !== 'ProductDetailsPanel') return null;
    const props = asRecord(root.props);
    const product = asRecord(props?.['product']) ?? props;
    return product ?? null;
  }

  private _productGridRecordFromSpec(
    spec: UISpec | null | undefined,
    fallbackSku?: string | null,
  ): Record<string, unknown> | null {
    if (!spec) return null;
    const root = spec.elements[spec.root];
    if (!root || root.type !== 'ProductGrid') return null;

    const products = Object.values(spec.elements)
      .filter((element) => element.type === 'ProductCard')
      .map((element) => asRecord(element.props?.['product']))
      .filter((product): product is Record<string, unknown> => product !== null);
    if (products.length === 0) return null;

    const fallbackKey = this._coerceSkuKey(fallbackSku);
    if (fallbackKey) {
      const matched = products.find((product) => this._productRecordMatchesSku(product, fallbackKey));
      if (matched) return matched;
    }

    return products.length === 1 ? products[0]! : null;
  }

  private _productRecordMatchesSku(product: Record<string, unknown>, sku: string): boolean {
    const directSku = this._coerceSkuKey(product['sku']);
    if (directSku === sku) return true;
    const groupId = this._coerceSkuKey(product['group_id'] ?? product['groupId']);
    if (groupId === sku) return true;
    const extras = asRecord(product['extras']);
    const extraGroupId = this._coerceSkuKey(extras?.['group_id'] ?? extras?.['groupId']);
    return extraGroupId === sku;
  }

  private _getCurrentPanelProductRecord(): Record<string, unknown> | null {
    const src = this._currentPanelSource;
    if (!src) return null;
    if (src.kind === 'spec') return this._productDetailsRecordFromSpec(src.spec);
    if (src.kind === 'productDetailsWithSimilars') return this._productDetailsRecordFromSpec(src.pdpSpec);
    return null;
  }

  private _getCurrentPanelProductGridRecord(fallbackSku?: string | null): Record<string, unknown> | null {
    const src = this._currentPanelSource;
    if (!src) return null;
    if (src.kind === 'spec') return this._productGridRecordFromSpec(src.spec, fallbackSku);
    if (src.kind === 'productDetailsWithSimilars')
      return this._productGridRecordFromSpec(src.similarsSpec, fallbackSku);
    return null;
  }

  private _panelSourceFromMessage(message: ChatMessage | undefined): PanelSource | null {
    const spec = message?.uiSpec;
    if (!spec) return null;
    const root = spec.elements[spec.root];
    if (!root || MESSAGE_UI_SPEC_COMPONENTS.has(root.type)) return null;
    return { kind: 'spec', spec };
  }

  private _getLatestProductDetailsRecord(): Record<string, unknown> | null {
    for (let i = this._messages.length - 1; i >= 0; i -= 1) {
      const product = this._productDetailsRecordFromSpec(this._messages[i]?.uiSpec);
      if (product) return product;
    }
    return null;
  }

  private _getLatestProductGridRecord(fallbackSku?: string | null): Record<string, unknown> | null {
    for (let i = this._messages.length - 1; i >= 0; i -= 1) {
      const product = this._productGridRecordFromSpec(this._messages[i]?.uiSpec, fallbackSku);
      if (product) return product;
    }
    return null;
  }

  private _getInlinePdpProductRecord(): Record<string, unknown> | null {
    const pageSku = this._pdpPageContextSkuKey();
    if (!pageSku) return null;
    const product = this._currentInlineProductSummaryRecord;
    if (!product) return null;
    return this._coerceSkuKey(product['sku']) === pageSku ? product : null;
  }

  /**
   * Returns the SKU of the product currently rendered in the side panel, if any.
   *
   * Used to short-circuit duplicate `ProductSummaryCard` messages when the user
   * re-clicks the product that is already open in the panel. Only returns a SKU
   * when the live panel source is a `ProductDetailsPanel` spec (either standalone
   * or paired with a similars grid).
   */
  private _getCurrentPanelProductSku(): string | null {
    const product = this._getCurrentPanelProductRecord();
    if (!product) return null;
    const key = this._coerceSkuKey(product['sku']);
    return key || null;
  }

  /**
   * “Active” PDP SKU for suppressing redundant `ProductSummaryCard` clicks: prefer
   * the panel spec when `productDetailsExtended` keeps a PDP there; otherwise
   * fall back to host `pageContext` on PDP pages (panel may have been cleared).
   */
  private _activeSkuForProductSummaryClick(): string | null {
    const fromPanel = this._getCurrentPanelProductSku();
    if (fromPanel) return fromPanel;
    return this._pdpPageContextSkuKey();
  }

  /** After similars grid is appended, extend panel source so back/history/snapshot rebuild includes it. */
  private _mergePanelSourceWithSimilars(similarsSpec: UISpec): void {
    const prev = this._currentPanelSource;
    if (prev?.kind === 'spec' && this._panel?.currentType === 'ProductDetailsPanel') {
      this._currentPanelSource = {
        kind: 'productDetailsWithSimilars',
        pdpSpec: prev.spec,
        similarsSpec,
      };
    }
  }

  /** Re-render PDP plus similar-products block (matches `_appendSimilarsToPanel` structure). */
  private _renderProductDetailsWithSimilars(
    pdpSpec: UISpec,
    similarsSpec: UISpec,
    ctx: ChatUISpecRenderContext,
  ): HTMLElement {
    this._applyPanelListHeadingToContext(ctx, {
      kind: 'productDetailsWithSimilars',
      pdpSpec,
      similarsSpec,
    });
    const panelEl = this._renderUISpec(pdpSpec, ctx);
    const grid = this._renderUISpec(similarsSpec, ctx);
    grid.classList.add('gengage-chat-product-details-similars');
    panelEl.appendChild(grid);
    return panelEl;
  }

  /** Sets ctx.panelProductListHeading for ProductGrid / PDP+similars rebuilds. */
  private _applyPanelListHeadingToContext(ctx: ChatUISpecRenderContext, source: PanelSource): void {
    ctx.panelProductListHeading = undefined;
    if (!this._panel) return;
    if (source.kind === 'spec') {
      const root = source.spec.elements[source.spec.root];
      if (root?.type === 'ProductGrid') {
        const n = root.children?.length ?? 0;
        if (n > 0) {
          ctx.panelProductListHeading = this._panel.titleForComponent(
            'ProductGrid',
            (root.props?.['panelTitle'] as string | undefined) ?? undefined,
          );
        }
      }
    } else if (source.kind === 'productDetailsWithSimilars') {
      const simRoot = source.similarsSpec.elements[source.similarsSpec.root];
      if (simRoot?.type === 'ProductGrid' && (simRoot.children?.length ?? 0) > 0) {
        ctx.panelProductListHeading = this._i18n.similarProductsLabel ?? 'Similar Products';
      }
    }
  }

  /**
   * Deep-clone panel history payloads so later streams cannot mutate a prior message's
   * rebuild closure (identical consecutive ComparisonTable / ProductGrid specs shared refs).
   */
  private _clonePanelSourceForHistory(source: PanelSource): PanelSource {
    try {
      return structuredClone(source);
    } catch {
      return source;
    }
  }

  private _renderPanelFromSource(source: PanelSource, ctx: ChatUISpecRenderContext): HTMLElement {
    this._applyPanelListHeadingToContext(ctx, source);
    if (source.kind === 'favorites') {
      return this._buildFavoritesPageEl();
    }
    if (source.kind === 'history') {
      return this._buildHistoryPageEl();
    }
    if (source.kind === 'productDetailsWithSimilars') {
      return this._renderProductDetailsWithSimilars(source.pdpSpec, source.similarsSpec, ctx);
    }
    return this._renderUISpec(source.spec, ctx);
  }

  private _handleRollback(messageId: string): void {
    const msg = this._messages.find((m) => m.id === messageId);
    if (!msg?.threadId) return;
    this._rollbackToThread(msg.threadId);
  }

  private _rememberThreadPills(
    threadId: string,
    buttons: Array<{
      label: string;
      action: ActionPayload;
      icon?: string;
      image?: string;
      description?: string;
    }>,
  ): void {
    if (!threadId || buttons.length === 0) return;
    this._threadPillsByThreadId.set(
      threadId,
      buttons.map((btn) => {
        const stored: StoredThreadPill = { label: btn.label, action: btn.action };
        if (btn.icon) stored.icon = btn.icon;
        if (btn.image) stored.image = btn.image;
        if (btn.description) stored.description = btn.description;
        return stored;
      }),
    );
  }

  private _applyStoredThreadPills(threadId: string): void {
    const buttons = this._threadPillsByThreadId.get(threadId);
    if (!buttons?.length) {
      this._drawer?.setPills([]);
      return;
    }
    this._drawer?.setPills(
      buttons.map((btn) => {
        const pill: {
          label: string;
          onAction: () => void;
          icon?: string;
          image?: string;
          description?: string;
        } = {
          label: btn.label,
          onAction: () => this._sendAction(btn.action),
        };
        if (btn.icon) pill.icon = btn.icon;
        if (btn.image) pill.image = btn.image;
        if (btn.description) pill.description = btn.description;
        return pill;
      }),
    );
  }

  private _pruneThreadPillsAfter(cutoffThreadId: string): void {
    for (const threadId of [...this._threadPillsByThreadId.keys()]) {
      if (threadId > cutoffThreadId) {
        this._threadPillsByThreadId.delete(threadId);
      }
    }
  }

  private _ensureMobileFullSheetForPanel(): void {
    if (!this._drawerVisible || !this._isMobileViewport || this._openState === 'full') return;
    this._openState = 'full';
    this._applyOpenStateClasses();
  }

  private _ensureMobilePanelOverlayVisible(): void {
    if (!this._drawerVisible || !this._isMobileViewport) return;
    this._ensureMobileFullSheetForPanel();
    this._drawer?.showMobilePanel();
  }

  private _ensurePdpPrimeSuggestedUiIfNeeded(): void {
    if (!this._drawer) return;
    if (this._hasUnavailableProductContext()) return;

    // TODO(CSH): To be moved to CSH as FE MUST NOT CONTAIN default business logic;
    // all activities should be streamed by backend/CSH agent.
    const contextKey: OpeningContextKey = 'product';
    const configured = this._resolveContextualOpeningActions(contextKey);
    if (configured.length === 0) return;

    this._drawer.setInputAreaChips(
      configured.map((chip) => ({
        label: chip.title,
        onAction: () => this._sendAction(this._resolveContextualOpeningAction(chip, contextKey)),
        ...(chip.icon ? { icon: chip.icon } : {}),
      })),
    );
  }

  /** Rewind the conversation to the given thread. */
  private _rollbackToThread(threadId: string): void {
    // Validate thread ID exists in known threads
    if (this._panel && this._panel.threads.length > 0 && !this._panel.threads.includes(threadId)) {
      // Check if any message has this threadId as fallback
      if (!this._messages.some((m) => m.threadId === threadId)) {
        return; // Invalid thread ID — silently ignore
      }
    }
    this._currentThreadId = threadId;
    this._extendedModeManager?.setHiddenByUser(false);

    // Presentation collapse (single-thread focus) only at the conversation tip.
    // When navigating back to an older panel thread, show the full transcript up to
    // this thread — same effect as "show earlier messages" without an extra tap.
    // Forward navigation to the tip restores collapse + scroll-to-reveal UX.
    const atConversationTip = this._lastThreadId != null && threadId === this._lastThreadId;
    if (atConversationTip) {
      this._presentation.setFocusedThreadId(threadId);
      this._drawer?.setPresentationFocus(threadId);
    } else {
      this._presentation.releaseFocusedThread();
      this._drawer?.setPresentationFocus(null);
    }
    this._drawer?.setFormerMessagesButtonVisible(false);

    // Toggle visibility of messages after the cutoff
    for (const msg of this._messages) {
      const bubble = this._shadow?.querySelector(`[data-message-id="${escapeCssIdentifier(msg.id)}"]`);
      if (!bubble) continue;
      if (msg.threadId && msg.threadId > threadId) {
        bubble.classList.add('gengage-chat-bubble--hidden');
      } else {
        bubble.classList.remove('gengage-chat-bubble--hidden');
      }
    }

    // Hide inline UISpec elements from future threads
    this._shadow?.querySelectorAll('[data-thread-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset['threadId'] && el.dataset['threadId'] > threadId) {
        el.classList.add('gengage-chat-bubble--hidden');
      } else if (el instanceof HTMLElement) {
        el.classList.remove('gengage-chat-bubble--hidden');
      }
    });

    // Restore panel snapshot from the target thread's bot message
    const targetBot = this._messages.find((m) => m.role === 'assistant' && m.threadId === threadId);
    const threadHadPanel = this._panel?.threads.includes(threadId) ?? false;
    const restoredSource =
      (targetBot ? this._panel?.sourceForMessage(targetBot.id) : null) ??
      this._panel?.sourceForThread(threadId) ??
      (threadHadPanel ? this._panelSourceFromMessage(targetBot) : null);
    let restored = targetBot ? (this._panel?.restoreForMessage(targetBot.id) ?? false) : false;
    if (!restored && restoredSource) {
      const ctx = this._buildRenderContext();
      this._drawer?.setPanelContent(this._renderPanelFromSource(restoredSource, ctx));
      this._currentPanelSource = restoredSource;
      if (this._panel) {
        this._panel.currentType = this._panel.componentTypeForSource(restoredSource);
      }
      restored = true;
    }
    if (!restored) {
      this._drawer?.clearPanel();
      this._currentPanelSource = null;
      this._drawer?.setPanelAiZoneState('hidden');
    }
    if (restored) {
      this._currentPanelSource = restoredSource;
      this._restorePanelAiZoneForThread(threadId);
    }
    // Always update topbar navigation state for the new thread position
    const panelType = this._panel!.currentType ?? '';
    this._panel?.updateTopBar(panelType);

    // Restore suggestion pills for the rewound thread (panel parity).
    this._applyStoredThreadPills(threadId);

    requestAnimationFrame(() => {
      this._drawer?.scrollThreadIntoView(threadId, 'auto');
    });

    // Load context from IndexedDB for the target thread so the next request
    // sends the correct historical context, then prune future entries.
    if (this._session?.db && this.config.session?.sessionId) {
      const sid = this.config.session.sessionId;
      void (async () => {
        try {
          const ctx = await this._session?.db?.loadContext(sid, threadId);
          if (ctx) this._lastBackendContext = ctx.context;
          await this._session?.db?.deleteContextsAfterThread(sid, threadId);
        } catch {
          /* non-fatal */
        }
      })();
    }

    this._persistHistory();
  }

  // ---------------------------------------------------------------------------
  // Chat-history archive persistence (IndexedDB-backed)
  // ---------------------------------------------------------------------------

  private _persistHistory(): void {
    if (!this._historyStorage) return;
    const nextHistoryId =
      this._activeHistorySessionId ??
      this._historyStorage.findSessionId(this.config.session?.sessionId ?? '', this._chatCreatedAt) ??
      this._historyStorage.createHistoryId();
    const saved = this._historyStorage.saveSession({
      historyId: nextHistoryId,
      userId: this.config.session?.userId ?? '',
      appId: this.config.accountId,
      sourceSessionId: this.config.session?.sessionId ?? '',
      messages: this._messages,
      currentThreadId: this._currentThreadId,
      lastThreadId: this._lastThreadId,
      chatCreatedAt: this._chatCreatedAt,
      panelSnapshots: this._panel?.snapshots ?? new Map(),
      panelSnapshotTypes: this._panel?.snapshotTypes ?? new Map(),
      panelSnapshotSources: this._panel?.snapshotSourceMap() ?? new Map(),
      panelThreads: this._panel?.threads ?? [],
      panelAiSpecByThread: this._panelAiSpecByThread,
      lastBackendContext: this._lastBackendContext,
      pageType: this.config.pageContext?.pageType,
      sku: this.config.pageContext?.sku,
      attachmentPlaceholder: this._i18n.historyImageAttachmentLabel,
    });
    if (saved) {
      this._activeHistorySessionId = saved.id;
    }
  }

  private _persistConversationState(): void {
    this._persistHistory();
    this._persistToIndexedDB().catch(() => {
      /* non-fatal */
    });
  }

  private async _persistConversationStateForNavigation(): Promise<void> {
    this._persistHistory();
    // History now persists to IndexedDB asynchronously (write-through). An
    // assistant-driven navigation hard-reloads the page, so the history record
    // MUST be flushed before we navigate or the restore-after-navigation handoff
    // (R1 / PanelRestoreCard) would race the page unload and lose it.
    await Promise.all([this._historyStorage?.flush(), this._persistToIndexedDB()]);
  }

  private _formatHistoryTimestamp(iso: string): string {
    try {
      return new Intl.DateTimeFormat(resolveLocaleTag(this.config.locale), {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  }

  private _resolveHistoryPreviewText(session: ChatHistoryIndexEntry): string {
    const preview = (session.preview || '').trim();
    // derivePreview() already folds the PanelRestoreCard ctaLabel into the stored
    // preview at save time, so listing never needs to load the full record. The
    // only remaining case is an empty/placeholder preview — fall back to the title.
    if (preview && preview !== 'PanelRestoreCard') return preview;
    return session.title;
  }

  private _openHistoryPanel(pushCurrent = true): void {
    if (!this._drawer) return;

    if (pushCurrent && this._currentPanelSource && this._currentPanelSource.kind !== 'history') {
      const currentTitle = this._drawer.getPanelTopBarTitle() ?? '';
      this._localPanelHistory.push({ source: this._currentPanelSource, title: currentTitle });
      if (this._localPanelHistory.length > GengageChat._MAX_PANEL_HISTORY) this._localPanelHistory.shift();
    }

    this._drawer.setPanelContent(this._buildHistoryPageEl());
    this._currentPanelSource = { kind: 'history' };
    this._drawer.updatePanelTopBar(true, false, this._i18n.historyPageTitle);
  }

  private _buildHistoryPageEl(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'gengage-chat-history-page';

    const actions = document.createElement('div');
    actions.className = 'gengage-chat-history-actions';

    const newChatBtn = document.createElement('button');
    newChatBtn.type = 'button';
    newChatBtn.className = 'gengage-chat-history-new-chat';
    newChatBtn.textContent = this._i18n.newChatButton;
    newChatBtn.addEventListener('click', () => this._startNewChat());
    actions.appendChild(newChatBtn);

    root.appendChild(actions);

    const sessions = this._historyStorage?.listSessions() ?? [];
    if (sessions.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'gengage-chat-history-empty';
      empty.innerHTML =
        '<div class="gengage-chat-history-empty-icon" aria-hidden="true">' +
        '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8v5l3 2"/><path d="M3.05 11A9 9 0 1 1 5 17.5"/><path d="M3 8v4h4"/></svg>' +
        '</div>';
      const text = document.createElement('p');
      text.textContent = this._i18n.emptyHistoryMessage;
      empty.appendChild(text);
      root.appendChild(empty);
      return root;
    }

    const list = document.createElement('div');
    list.className = 'gengage-chat-history-list';

    for (const session of sessions) {
      const item = document.createElement('div');
      item.className = 'gengage-chat-history-item';
      if (session.id === this._activeHistorySessionId) {
        item.classList.add('gengage-chat-history-item--active');
      }

      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      openBtn.className = 'gengage-chat-history-open';
      openBtn.title = session.title;
      openBtn.setAttribute('aria-label', `${this._i18n.historyAriaLabel}: ${session.title}`);
      openBtn.addEventListener('click', () => {
        void this._loadHistorySession(session.id);
      });

      const titleRow = document.createElement('div');
      titleRow.className = 'gengage-chat-history-item-title-row';

      const title = document.createElement('span');
      title.className = 'gengage-chat-history-item-title';
      title.textContent = session.title;
      titleRow.appendChild(title);

      const meta = document.createElement('span');
      meta.className = 'gengage-chat-history-item-meta';
      meta.textContent = this._formatHistoryTimestamp(session.updatedAt);
      titleRow.appendChild(meta);
      openBtn.appendChild(titleRow);

      const preview = document.createElement('p');
      preview.className = 'gengage-chat-history-item-preview';
      preview.textContent = this._resolveHistoryPreviewText(session);
      openBtn.appendChild(preview);

      item.appendChild(openBtn);

      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.className = 'gengage-chat-history-delete';
      deleteBtn.setAttribute('aria-label', `${this._i18n.historyDeleteLabel}: ${session.title}`);
      deleteBtn.title = this._i18n.historyDeleteLabel;
      deleteBtn.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';
      deleteBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!window.confirm(this._i18n.historyDeleteConfirmMessage)) return;
        this._deleteHistorySession(session.id);
      });
      item.appendChild(deleteBtn);

      list.appendChild(item);
    }

    root.appendChild(list);
    return root;
  }

  private _deleteHistorySession(historyId: string): void {
    if (!this._historyStorage) return;
    const deletingCurrent = historyId === this._activeHistorySessionId;
    this._historyStorage.deleteSession(historyId);
    if (deletingCurrent) {
      this._activeHistorySessionId = null;
      this._startNewChat();
      return;
    }
    if (this._currentPanelSource?.kind === 'history') {
      this._openHistoryPanel(false);
    }
  }

  private async _loadHistorySession(historyId: string): Promise<void> {
    const record = (await this._historyStorage?.loadSession(historyId)) ?? null;
    if (!record) return;

    this._clearConversationState(false);
    dispatchConversationReset('history_session_changed');
    this._activeHistorySessionId = record.id;
    await this._restoreFromHistoryRecord(record);
  }

  private async _restoreFromHistoryRecord(record: ChatHistorySessionRecord): Promise<boolean> {
    this._drawer?.lockScrollForRestore();

    if (record.messages.length === 0) {
      // Empty history record — nothing to put on the surface. Returning false keeps the
      // normal greeting/auto-launch enabled (regression #1: no blank chat).
      return false;
    }
    this._markCurrentContextLaunchHandled();

    this._currentThreadId = record.currentThreadId;
    this._lastThreadId = record.lastThreadId;
    if (this._currentThreadId && this._lastThreadId && this._currentThreadId > this._lastThreadId) {
      this._currentThreadId = this._lastThreadId;
    }
    this._chatCreatedAt = record.createdAt;
    this._lastBackendContext = record.lastBackendContext;

    if (record.panelThreads) {
      this._panel!.threads = [...record.panelThreads];
    }

    const panelThreadIdByMessageId = this._panelThreadIdByMessageId(record.messages);
    if (record.panelSnapshotHtml) {
      for (const [messageId, html] of Object.entries(record.panelSnapshotHtml)) {
        const container = document.createElement('div');
        container.innerHTML = sanitizeHtml(html);
        this._panel!.snapshots.set(messageId, container);
      }
    }
    if (record.panelSnapshotTypes) {
      for (const [messageId, type] of Object.entries(record.panelSnapshotTypes)) {
        this._panel!.snapshotTypes.set(messageId, type);
      }
    }
    this._restorePanelSnapshotSources(record.panelSnapshotSources, panelThreadIdByMessageId);
    if (record.panelAiSpecByThread) {
      this._restorePanelAiSpecMap(record.panelAiSpecByThread);
    }

    let maxMsgNum = 0;
    for (const message of record.messages) {
      const chatMessage: ChatMessage = {
        id: message.id,
        role: message.role,
        timestamp: message.timestamp,
        status: message.status,
      };
      if (message.threadId !== undefined) chatMessage.threadId = message.threadId;
      if (message.content !== undefined) chatMessage.content = message.content;
      if (message.uiSpec !== undefined) chatMessage.uiSpec = message.uiSpec;
      if (message.frontendOnly) chatMessage.frontendOnly = true;
      if (message.silent) chatMessage.silent = true;
      if (message.renderHint !== undefined) chatMessage.renderHint = message.renderHint;
      if (message.photoAnalysis !== undefined) chatMessage.photoAnalysis = message.photoAnalysis;

      this._messages.push(chatMessage);

      if (!chatMessage.silent) {
        const willRenderAssistantBubble =
          chatMessage.role !== 'assistant' ||
          !!chatMessage.content ||
          !!chatMessage.attachment ||
          this._isMessageHostedUISpec(chatMessage.uiSpec);
        if (
          chatMessage.role === 'assistant' &&
          chatMessage.threadId &&
          willRenderAssistantBubble &&
          !this._threadsWithFirstBot.has(chatMessage.threadId)
        ) {
          this._threadsWithFirstBot.add(chatMessage.threadId);
          this._drawer?.markFirstBotMessage(chatMessage.id);
        }
        this._drawer?.addMessage(chatMessage);
        if (chatMessage.role === 'assistant' && chatMessage.threadId && chatMessage.uiSpec) {
          const isMessageHostedUISpec = this._hydrateRestoredUISpecMetadata(
            chatMessage.id,
            chatMessage.threadId,
            chatMessage.uiSpec,
          );
          if (!isMessageHostedUISpec) {
            this._restoreInlineUISpec(chatMessage);
          }
        }
      }

      const idNum = parseInt(message.id.replace('msg-', ''), 10);
      if (!isNaN(idNum) && idNum > maxMsgNum) maxMsgNum = idNum;
    }

    if (maxMsgNum > this._currentMessageId) {
      this._currentMessageId = maxMsgNum;
    }

    if (this._currentThreadId) {
      const panelBot = [...this._messages]
        .reverse()
        .find(
          (message) => message.role === 'assistant' && message.threadId === this._currentThreadId && !message.silent,
        );
      if (panelBot && (this._panel!.snapshots.has(panelBot.id) || this._panel?.sourceForMessage(panelBot.id))) {
        const restored = this._panel?.restoreForMessage(panelBot.id) ?? false;
        if (restored) {
          this._currentPanelSource =
            this._panel?.sourceForMessage(panelBot.id) ?? this._panelSourceFromMessage(panelBot);
          this._restorePanelAiZoneForThread(this._currentThreadId);
        }
      }
    }

    if (this._currentThreadId) {
      const cutoff = this._currentThreadId;
      for (const message of this._messages) {
        if (message.threadId && message.threadId > cutoff) {
          const bubble = this._shadow?.querySelector(`[data-message-id="${escapeCssIdentifier(message.id)}"]`);
          bubble?.classList.add('gengage-chat-bubble--hidden');
        }
      }
      this._shadow?.querySelectorAll('[data-thread-id]').forEach((element) => {
        if (element instanceof HTMLElement && element.dataset['threadId'] && element.dataset['threadId'] > cutoff) {
          element.classList.add('gengage-chat-bubble--hidden');
        }
      });
    }

    this._presentation.releaseFocusedThread();
    this._drawer?.setPresentationFocus(null);

    if (this._isMobileViewport && this._drawer?.hasPanelContent()) {
      this._drawer.hideMobilePanel();
    }

    setTimeout(() => {
      this._drawer?.scrollToLastThread();
    }, 550);
    // A non-empty history record was replayed — the thread owns the surface.
    return true;
  }

  // ---------------------------------------------------------------------------
  // IndexedDB persistence (delegates to SessionPersistence)
  // ---------------------------------------------------------------------------

  private async _persistToIndexedDB(): Promise<void> {
    if (!this._session || !this.config.session?.sessionId) return;
    await this._session.persist({
      userId: this.config.session.userId ?? '',
      appId: this.config.accountId,
      sessionId: this.config.session.sessionId,
      messages: this._messages,
      currentThreadId: this._currentThreadId,
      lastThreadId: this._lastThreadId,
      chatCreatedAt: this._chatCreatedAt,
      panelSnapshots: this._panel?.snapshots ?? new Map(),
      panelSnapshotTypes: this._panel?.snapshotTypes ?? new Map(),
      panelSnapshotSources: this._panel?.snapshotSourceMap() ?? new Map(),
      panelThreads: this._panel?.threads ?? [],
      panelAiSpecByThread: this._panelAiSpecByThread,
      lastBackendContext: this._lastBackendContext,
      sku: this.config.pageContext?.sku,
    });
  }

  private _isSameOriginUrl(url: string): boolean {
    try {
      if (!url.trim()) return false;
      const parsed = new URL(url, window.location.href);
      return parsed.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private _enrichProductMentionsWithUrls(mentions: ProductMention[]): ProductMention[] {
    return mentions.map((mention) => {
      const directUrl = mention.url?.trim();
      if (directUrl && isSafeUrl(directUrl)) {
        return directUrl === mention.url ? mention : { ...mention, url: directUrl };
      }

      const itemUrl = this._productMentionUrlForSku(mention.sku);
      return itemUrl ? { ...mention, url: itemUrl } : mention;
    });
  }

  private _productMentionUrlForSku(sku: string): string | undefined {
    const trimmedSku = sku.trim();
    const item = this._skuToProductItem[sku] ?? (trimmedSku ? this._skuToProductItem[trimmedSku] : undefined);
    const rawUrl = item?.['url'];
    if (typeof rawUrl !== 'string') return undefined;
    const url = rawUrl.trim();
    return url && isSafeUrl(url) ? url : undefined;
  }

  private _handleProductNavClick(params: { sku: string; url: string; name?: string }): void {
    // Demo mode keeps product drilldown in-chat. Production same-origin persists + navigates;
    // production cross-origin safe URLs use the host callback when supplied.
    const isDemoWebsite = this.config.isDemoWebsite === true;
    const shouldNavigate = !isDemoWebsite && this._isSameOriginUrl(params.url);
    if (!shouldNavigate) {
      const clickSku = this._coerceSkuKey(params.sku);
      const activeSku = this._activeSkuForProductSummaryClick();
      if (clickSku && activeSku && clickSku === activeSku) {
        return;
      }
    }
    if (shouldNavigate) {
      dispatch('gengage:similar:product-click', {
        sku: params.sku,
        url: params.url,
        sessionId: this.config.session?.sessionId ?? null,
        ...(params.name !== undefined && params.name !== '' ? { productName: params.name } : {}),
      });
      void this._saveSessionAndOpenURL(params.url);
      return;
    }
    const urlTrim = params.url.trim();
    if (!isDemoWebsite && this.config.onProductNavigate !== undefined && urlTrim.length > 0 && isSafeUrl(urlTrim)) {
      ga.trackProductDetail(params.sku);
      this.config.onProductNavigate(urlTrim, params.sku, this.config.session?.sessionId ?? null);
      return;
    }
    ga.trackProductDetail(params.sku);
    const displayTitle = params.name?.trim() ? params.name.trim() : params.sku;
    this._sendAction({
      title: displayTitle,
      type: 'launchSingleProduct',
      payload: { sku: params.sku },
    });
  }

  private _markUnavailableProductContext(): void {
    this._productContextUnavailableSku = this.config.pageContext?.sku ?? null;
  }

  private _clearUnavailableProductContext(): void {
    this._productContextUnavailableSku = null;
  }

  private _hasUnavailableProductContext(): boolean {
    const currentSku = this.config.pageContext?.sku;
    return currentSku !== undefined && currentSku.length > 0 && this._productContextUnavailableSku === currentSku;
  }

  private _ensureAssistantMessageRendered(msg: ChatMessage): void {
    const bubble = this._shadow?.querySelector(`[data-message-id="${escapeCssIdentifier(msg.id)}"]`);
    if (bubble || !this._drawer) return;
    if (msg.role === 'assistant' && msg.threadId && !this._threadsWithFirstBot.has(msg.threadId)) {
      this._threadsWithFirstBot.add(msg.threadId);
      this._drawer.addMessage(msg);
      this._drawer.markFirstBotMessage(msg.id);
      return;
    }
    this._drawer.addMessage(msg);
  }

  private async _saveSessionAndOpenURL(url: string): Promise<void> {
    if (!this._session) return;
    await this._session.saveAndOpenURL(url, () => this._persistConversationStateForNavigation(), this._bridge);
  }

  private _navigateFromAction(params: { url: string; newTab?: boolean }): void {
    if (!isSafeUrl(params.url)) return;
    if (params.newTab) {
      // New tab: the current page (and its thread) stays put, so no
      // persist/restore handoff is needed.
      this._bridge?.send('navigate', params);
      window.open(params.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (this._isSameOriginUrl(params.url)) {
      // Assistant-driven in-tab page navigation: persist the thread + stamp the
      // restore marker so the re-mounted widget continues the conversation.
      void this._navigateAssistantDriven(params.url, params);
      return;
    }
    // Cross-origin in-tab: leaving the SPA entirely; no same-origin re-mount
    // will read a restore marker, so keep the raw navigation.
    this._bridge?.send('navigate', params);
    window.location.href = params.url;
  }

  /**
   * Assistant-driven page navigation (R1): persist the thread, stamp the restore
   * handoff marker, then navigate. On the SPA re-mount the widget restores the thread
   * and continues its flow — the homepage/PLP auto-launch greeting is suppressed
   * (see _restorePending / _primeOpenPageContext) so the assistant's answer is not
   * clobbered by a generic welcome. Same-origin only; cross-origin/new-tab callers
   * keep the raw navigation since the current page (and its thread) is not replaced.
   *
   * The restore-key stamp is the durable "the chat initiated this navigation" marker
   * across the navigation boundary — we distinguish assistant-vs-user navigation by
   * WHO called this (an assistant action handler), never by inspecting the URL.
   */
  private async _navigateAssistantDriven(
    url: string,
    bridgeParams: { url: string; newTab?: boolean } = { url },
  ): Promise<void> {
    // Stamp the restore marker BEFORE persisting/navigating so the re-mounted widget
    // sees the handoff. Non-fatal if sessionStorage is unavailable.
    try {
      this.saveSessionForNavigation();
    } catch {
      // sessionStorage unavailable — fall through to plain persist+navigate.
    }
    try {
      await this._persistConversationStateForNavigation();
    } catch {
      // Non-fatal — still navigate.
    }
    this._markAssistantSoftNavigationPending();
    this._bridge?.send('navigate', bridgeParams);
    if (!this._assistantSoftNavigationPending) {
      return;
    }
    const markerIsFresh = this._isRestoreMarkerFresh();
    const navigated = navigateToUrl(url);
    if (!navigated && markerIsFresh) {
      this._scheduleAssistantSoftNavigationExpiry();
    } else if (!navigated) {
      this._clearAssistantSoftNavigationPending();
    }
  }

  private async _loadPayload(threadId: string, messageId: string): Promise<import('../common/types.js').UISpec | null> {
    if (!this._session) return null;
    return this._session.loadPayload(threadId, messageId);
  }

  /**
   * Attempt to restore chat session from IndexedDB.
   * Restores when IDB has non-empty session data for the current sessionId AND an
   * explicit handoff is pending.
   * Best-effort — failures are silently ignored.
   *
   * @returns true ONLY when a non-empty thread was actually replayed onto the surface.
   * Every no-op/early-return path returns false so the caller leaves the normal
   * greeting/auto-launch enabled (regression #1: never suppress launch over nothing).
   */
  private async _restoreFromIndexedDB(shouldRestore: boolean): Promise<boolean> {
    if (!this._session?.db) return false;
    const sessionId = this.config.session?.sessionId;
    if (!sessionId) return false;

    const userId = this.config.session?.userId ?? '';
    const appId = this.config.accountId;

    // Always restore favorites (user preference, not session state)
    await this._session.loadFavorites(userId, appId);
    this._drawer?.updateFavoritesBadge(this._session.favoritedSkus.size);

    // Only restore chat state on explicit handoff (e.g. SimRel product navigation)
    if (!shouldRestore) return false;

    const session = await this._session.db?.loadSession(userId, appId, sessionId);
    if (!session || session.messages.length === 0) return false;

    // Don't restore a session saved for a different SKU
    const currentSku = this.config.pageContext?.sku;
    if (currentSku && session.sku && session.sku !== currentSku) return false;

    // Prevent duplicate auto-launch: session already has messages, so the current page-context launch already happened.
    this._markCurrentContextLaunchHandled();

    // Lock auto-scroll during restore to prevent visual jump
    this._drawer?.lockScrollForRestore();

    // Restore thread cursors and creation timestamp
    this._currentThreadId = session.currentThreadId;
    this._lastThreadId = session.lastThreadId;
    // Validate thread invariants — corrupted IDB data must not break navigation
    if (this._currentThreadId && this._lastThreadId && this._currentThreadId > this._lastThreadId) {
      this._currentThreadId = this._lastThreadId;
    }
    this._chatCreatedAt = session.createdAt;

    // Restore panel threads
    if (session.panelThreads) {
      this._panel!.threads = session.panelThreads;
    }

    const panelThreadIdByMessageId = this._panelThreadIdByMessageId(session.messages);
    // Restore panel snapshots from serialized HTML (sanitize for defense-in-depth)
    if (session.panelSnapshotHtml) {
      for (const [msgId, html] of Object.entries(session.panelSnapshotHtml)) {
        const container = document.createElement('div');
        container.innerHTML = sanitizeHtml(html);
        this._panel!.snapshots.set(msgId, container);
      }
    }
    if (session.panelSnapshotTypes) {
      for (const [msgId, type] of Object.entries(session.panelSnapshotTypes)) {
        this._panel!.snapshotTypes.set(msgId, type);
      }
    }
    this._restorePanelSnapshotSources(session.panelSnapshotSources, panelThreadIdByMessageId);
    if (session.panelAiSpecByThread) {
      this._restorePanelAiSpecMap(session.panelAiSpecByThread);
    }

    // Track highest message ID to avoid collisions with new messages
    let maxMsgNum = 0;

    // Replay messages into DOM
    for (const msg of session.messages) {
      const chatMsg: ChatMessage = {
        id: msg.id,
        role: msg.role,
        timestamp: msg.timestamp,
        status: msg.status,
      };
      if (msg.threadId !== undefined) chatMsg.threadId = msg.threadId;
      if (msg.content !== undefined) chatMsg.content = msg.content;
      if (msg.frontendOnly) chatMsg.frontendOnly = true;
      if (msg.silent) chatMsg.silent = true;

      this._messages.push(chatMsg);

      // Skip silent messages from DOM rendering
      if (chatMsg.silent) continue;

      if (chatMsg.role === 'assistant' && chatMsg.threadId) {
        const uiSpec = await this._loadPayload(chatMsg.threadId, chatMsg.id);
        if (uiSpec) {
          chatMsg.uiSpec = uiSpec;
          this._hydrateRestoredUISpecMetadata(chatMsg.id, chatMsg.threadId, uiSpec);
        }
      }
      const willRenderAssistantBubble =
        chatMsg.role !== 'assistant' ||
        !!chatMsg.content ||
        !!chatMsg.attachment ||
        this._isMessageHostedUISpec(chatMsg.uiSpec);
      if (
        chatMsg.role === 'assistant' &&
        chatMsg.threadId &&
        willRenderAssistantBubble &&
        !this._threadsWithFirstBot.has(chatMsg.threadId)
      ) {
        this._threadsWithFirstBot.add(chatMsg.threadId);
        this._drawer?.markFirstBotMessage(chatMsg.id);
      }
      this._drawer?.addMessage(chatMsg);

      // Track message ID counter
      const idNum = parseInt(msg.id.replace('msg-', ''), 10);
      if (!isNaN(idNum) && idNum > maxMsgNum) maxMsgNum = idNum;

      if (chatMsg.uiSpec) {
        if (!this._isMessageHostedUISpec(chatMsg.uiSpec)) {
          this._restoreInlineUISpec(chatMsg);
        }
        // Clear after render to maintain lean pattern
        delete chatMsg.uiSpec;
      }
    }

    // Advance message ID counter past restored messages
    if (maxMsgNum > this._currentMessageId) {
      this._currentMessageId = maxMsgNum;
    }

    // Restore backend context with fallback chain
    if (this._currentThreadId) {
      let ctx = await this._session.db?.loadContext(sessionId, this._currentThreadId);
      if (!ctx) {
        ctx = await this._session.db?.loadLatestContext(sessionId);
      }
      if (ctx) this._lastBackendContext = ctx.context;
    }

    // Restore panel for the current thread's latest bot message
    if (this._currentThreadId) {
      const panelBot = [...this._messages]
        .reverse()
        .find((m) => m.role === 'assistant' && m.threadId === this._currentThreadId && !m.silent);
      if (panelBot && (this._panel!.snapshots.has(panelBot.id) || this._panel?.sourceForMessage(panelBot.id))) {
        const restored = this._panel?.restoreForMessage(panelBot.id) ?? false;
        if (restored) {
          this._currentPanelSource =
            this._panel?.sourceForMessage(panelBot.id) ?? this._panelSourceFromMessage(panelBot);
          this._restorePanelAiZoneForThread(this._currentThreadId);
        }
      }
    }

    // Apply thread visibility — hide messages from future threads
    if (this._currentThreadId) {
      const cutoff = this._currentThreadId;
      for (const msg of this._messages) {
        if (msg.threadId && msg.threadId > cutoff) {
          const bubble = this._shadow?.querySelector(`[data-message-id="${escapeCssIdentifier(msg.id)}"]`);
          bubble?.classList.add('gengage-chat-bubble--hidden');
        }
      }
      this._shadow?.querySelectorAll('[data-thread-id]').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset['threadId'] && el.dataset['threadId'] > cutoff) {
          el.classList.add('gengage-chat-bubble--hidden');
        }
      });
    }

    // Update panel topbar if we have panel threads
    if (this._panel!.threads.length > 0 && this._currentThreadId) {
      const lastPanelThread = this._panel!.threads[this._panel!.threads.length - 1];
      if (lastPanelThread) {
        const lastPanelBot = [...this._messages]
          .reverse()
          .find(
            (m) =>
              m.role === 'assistant' &&
              m.threadId === lastPanelThread &&
              ((this._panel?.snapshots.has(m.id) ?? false) || this._panel?.sourceForMessage(m.id)),
          );
        const snapshotType = lastPanelBot ? this._panel?.snapshotTypes.get(lastPanelBot.id) : undefined;
        if (snapshotType) {
          this._panel?.updateTopBar(snapshotType);
        }
      }
    }

    this._presentation.releaseFocusedThread();
    this._drawer?.setPresentationFocus(null);

    // After lockout expires, scroll to last thread boundary instead of absolute bottom
    setTimeout(() => {
      this._drawer?.scrollToLastThread();
    }, 550);

    if (!this._activeHistorySessionId) {
      this._persistHistory();
    }
    // A non-empty thread was replayed onto the surface — it now owns the page, so the
    // caller must suppress the page-context greeting (regression #1: true ONLY here).
    return true;
  }

  /**
   * Toggle comparison mode or individual SKU selection, then refresh the DOM.
   * Extracted so both the render-context callback and DOM-created checkboxes
   * share the same state-mutation + refresh path.
   */
  /**
   * Panel back navigation: pop local drilldown history first (e.g. card→detail),
   * then fall back to thread-level history.
   */
  private _navigatePanelBack(): void {
    const prev = this._localPanelHistory.pop();
    if (prev) {
      const ctx = this._buildRenderContext();
      const el = this._renderPanelFromSource(prev.source, ctx);
      this._drawer?.setPanelContent(el);
      this._currentPanelSource = prev.source;
      const canBack = this._localPanelHistory.length > 0 || (this._panel?.threads.length ?? 0) > 1;
      this._drawer?.updatePanelTopBar(canBack, false, prev.title);
      return;
    }
    if (this._currentPanelSource?.kind === 'favorites' || this._currentPanelSource?.kind === 'history') {
      this._drawer?.clearPanel();
      this._currentPanelSource = null;
      return;
    }
    // On mobile, when there is no local history left, back = hide the side panel
    // (content is preserved so it can be reopened via the header button)
    if (this._isMobileViewport) {
      this._drawer?.hideMobilePanel();
      return;
    }
    this._panel?.navigateBack();
  }

  private _toggleComparisonSku(sku: string): void {
    if (sku === '') {
      this._comparisonSelectMode = !this._comparisonSelectMode;
      this._comparisonSelectionWarning = null;
      if (this._comparisonSelectMode) {
        recordChoicePrompterDismissedForThread(this._currentThreadId ?? '');
        this._clearChoicePrompter();
      }
      if (!this._comparisonSelectMode) {
        this._comparisonSelectedSkus = [];
        this._comparisonSelectionWarning = null;
        ga.trackCompareClear();
      }
    } else {
      const idx = this._comparisonSelectedSkus.indexOf(sku);
      if (idx >= 0) {
        this._comparisonSelectedSkus = this._comparisonSelectedSkus.filter((s) => s !== sku);
        this._comparisonSelectionWarning = null;
      } else {
        if (this._comparisonSelectedSkus.length >= GengageChat._MAX_COMPARISON_SELECTION) {
          this._comparisonSelectionWarning =
            this._i18n.compareMaxHint ?? `You can select up to ${GengageChat._MAX_COMPARISON_SELECTION} products`;
          if (this._comparisonRefreshRafId !== null) {
            cancelAnimationFrame(this._comparisonRefreshRafId);
          }
          this._comparisonRefreshRafId = requestAnimationFrame(() => {
            this._comparisonRefreshRafId = null;
            this._refreshComparisonUI();
          });
          return;
        }
        this._comparisonSelectedSkus = [...this._comparisonSelectedSkus, sku];
        this._comparisonSelectionWarning = null;
        ga.trackComparePreselection(sku);
      }
    }
    // Debounce: cancel any pending refresh and schedule a new one to batch rapid toggles
    if (this._comparisonRefreshRafId !== null) {
      cancelAnimationFrame(this._comparisonRefreshRafId);
    }
    this._comparisonRefreshRafId = requestAnimationFrame(() => {
      this._comparisonRefreshRafId = null;
      this._refreshComparisonUI();
    });
  }

  /**
   * Refresh the panel DOM to reflect the current comparison state without
   * full re-render. Updates: toggle button active class, checkbox overlays
   * on product cards, and the floating comparison button.
   */
  private _refreshComparisonUI(): void {
    const panelEl = this._shadow?.querySelector('.gengage-chat-panel');
    if (!panelEl) {
      this._drawer?.setComparisonDockContent(null);
      return;
    }

    const gridWrapper = panelEl.querySelector('.gengage-chat-product-grid-wrapper');
    if (!gridWrapper) {
      this._drawer?.setComparisonDockContent(null);
      return;
    }
    const cards = gridWrapper.querySelectorAll<HTMLElement>('.gengage-chat-product-card[data-sku]');
    if (cards.length === 0) {
      this._drawer?.setComparisonDockContent(null);
      return;
    }

    // 1. Toggle comparison button active state
    const toggleBtn = gridWrapper.querySelector('.gengage-chat-comparison-toggle-btn');
    if (toggleBtn) {
      toggleBtn.classList.toggle('gengage-chat-comparison-toggle-btn--active', this._comparisonSelectMode);
    }

    // 2. Add or remove checkbox overlays on product cards
    if (this._comparisonSelectMode) {
      for (const card of cards) {
        if (card.parentElement?.classList.contains('gengage-chat-comparison-select-wrapper')) {
          // Already wrapped — sync selected state
          const wrapper = card.parentElement;
          const selected = this._comparisonSelectedSkus.includes(card.dataset['sku']!);
          wrapper.classList.toggle('gengage-chat-comparison-select-wrapper--selected', selected);
          const toggle = wrapper.querySelector<HTMLButtonElement>('.gengage-chat-comparison-checkbox');
          if (toggle) {
            toggle.dataset['selected'] = selected ? 'true' : 'false';
            toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
            const icon = toggle.querySelector('.gengage-chat-comparison-checkbox-icon');
            const label = toggle.querySelector('.gengage-chat-comparison-checkbox-label');
            if (icon) {
              icon.innerHTML = selected
                ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>'
                : '<span class="gengage-chat-comparison-checkbox-dot"></span>';
            }
            if (label) {
              label.textContent = selected
                ? (this._i18n.comparisonSelectedLabel ?? 'Selected')
                : (this._i18n.comparisonSelectLabel ?? 'Select to compare');
            }
          }
          continue;
        }
        const sku = card.dataset['sku']!;
        const wrapper = document.createElement('div');
        wrapper.className = 'gengage-chat-comparison-select-wrapper';
        const selected = this._comparisonSelectedSkus.includes(sku);
        if (selected) wrapper.classList.add('gengage-chat-comparison-select-wrapper--selected');
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'gengage-chat-comparison-checkbox';
        toggle.dataset['selected'] = selected ? 'true' : 'false';
        toggle.setAttribute('aria-pressed', selected ? 'true' : 'false');
        const icon = document.createElement('span');
        icon.className = 'gengage-chat-comparison-checkbox-icon';
        icon.innerHTML = selected
          ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>'
          : '<span class="gengage-chat-comparison-checkbox-dot"></span>';
        const label = document.createElement('span');
        label.className = 'gengage-chat-comparison-checkbox-label';
        label.textContent = selected
          ? (this._i18n.comparisonSelectedLabel ?? 'Selected')
          : (this._i18n.comparisonSelectLabel ?? 'Select to compare');
        toggle.appendChild(icon);
        toggle.appendChild(label);
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleComparisonSku(sku);
        });
        card.parentNode!.insertBefore(wrapper, card);
        wrapper.appendChild(toggle);
        wrapper.appendChild(card);
        // Allow clicking anywhere on the card (not just the tiny checkbox) to toggle selection
        wrapper.classList.add('gds-clickable');
        wrapper.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('.gengage-chat-comparison-checkbox')) return;
          e.stopPropagation();
          this._toggleComparisonSku(sku);
        });
      }
    } else {
      // Remove all checkbox wrappers
      const wrappers = gridWrapper.querySelectorAll('.gengage-chat-comparison-select-wrapper');
      for (const wrapper of wrappers) {
        const card = wrapper.querySelector('.gengage-chat-product-card');
        if (card && wrapper.parentNode) {
          wrapper.parentNode.insertBefore(card, wrapper);
          wrapper.remove();
        }
      }
    }

    // 3. Update the slim bottom-docked comparison bar
    const existingFloating = gridWrapper.querySelector('.gengage-chat-comparison-floating-btn');
    existingFloating?.remove();
    if (this._comparisonSelectMode) {
      const dock = renderFloatingComparisonButton(this._comparisonSelectedSkus, this._buildRenderContext());
      if (this._isMobileViewport) {
        this._drawer?.setComparisonDockContent(dock);
        this._ensureMobilePanelOverlayVisible();
      } else {
        this._drawer?.setComparisonDockContent(null);
        gridWrapper.appendChild(dock);
      }
    } else {
      this._drawer?.setComparisonDockContent(null);
    }
  }

  private _clearChoicePrompter(): void {
    this._choicePrompterEl?.remove();
    this._choicePrompterEl = null;
    this._shadow?.querySelectorAll('.gengage-chat-choice-prompter').forEach((el) => el.remove());
  }

  private _mountChoicePrompter(): boolean {
    if (!this._choicePrompterEl) return false;
    if (this._isMobileViewport) {
      this._drawer?.setComparisonDockContent(this._choicePrompterEl);
      this._ensureMobilePanelOverlayVisible();
      return true;
    }
    const mountEl = this._shadow?.querySelector('.gengage-chat-panel-float');
    if (!mountEl) {
      this._choicePrompterEl = null;
      return false;
    }
    mountEl.appendChild(this._choicePrompterEl);
    return true;
  }

  private _parseAddToCartActionPayload(payload: unknown): AddToCartParams | null {
    if (typeof payload !== 'object' || payload === null) return null;
    const rec = payload as Record<string, unknown>;
    const sku = this._coerceAddToCartString(rec['sku']);
    const cartCode = this._coerceAddToCartString(rec['cartCode'] ?? rec['cart_code']);
    let quantity = 1;
    if (typeof rec['quantity'] === 'number' && Number.isFinite(rec['quantity']) && rec['quantity'] > 0) {
      quantity = Math.max(1, Math.floor(rec['quantity']));
    }
    if (!sku || !cartCode) return null;
    return { sku, cartCode, quantity };
  }

  private _coerceAddToCartString(value: unknown): string {
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  }

  private _maybeHandleDirectAddToCartCommand(text: string): boolean {
    const intent = detectFastIntent(text);
    if (intent !== 'directAddToCart') return false;
    const params = this._resolveCurrentProductAddToCartParams();
    if (!params) return false;
    void this._runChatAddToCartFlow(params);
    return true;
  }

  private _resolveCurrentProductAddToCartParams(): AddToCartParams | null {
    const pageExtra = asRecord(this.config.pageContext?.extra);
    const fallbackSku = this._activeSkuForProductSummaryClick() ?? this._coerceSkuKey(this._bridgeContext?.['sku']);
    const isPdpPage = this.config.pageContext?.pageType === 'pdp';
    const currentPanelProduct = this._getCurrentPanelProductRecord();
    const currentPanelGridProduct = isPdpPage ? this._getCurrentPanelProductGridRecord(fallbackSku) : null;
    const inlinePdpProduct = isPdpPage ? this._getInlinePdpProductRecord() : null;
    if (!currentPanelProduct && !currentPanelGridProduct && this._currentPanelSource && !inlinePdpProduct) return null;
    const candidates: Array<Record<string, unknown> | null> = [currentPanelProduct, currentPanelGridProduct];
    if (isPdpPage) {
      candidates.push(inlinePdpProduct);
    }
    if (!this._currentPanelSource) {
      candidates.push(this._getLatestProductDetailsRecord());
      if (isPdpPage) {
        candidates.push(this._getLatestProductGridRecord(fallbackSku));
      }
    }
    if (isPdpPage) {
      candidates.push(
        asRecord(this._bridgeContext?.['product']),
        asRecord(this._bridgeContext?.['productDetails']),
        this._bridgeContext,
        asRecord(pageExtra?.['product']),
        asRecord(pageExtra?.['productDetails']),
        pageExtra,
      );
    }

    for (const candidate of candidates) {
      const params = this._extractAddToCartParamsFromRecord(candidate, fallbackSku);
      if (params) return params;
    }
    return null;
  }

  private _extractAddToCartParamsFromRecord(
    record: Record<string, unknown> | null,
    fallbackSku?: string | null,
  ): AddToCartParams | null {
    if (!record) return null;
    const inStock = record['inStock'] ?? record['in_stock'];
    if (inStock === false) return null;
    const sku = this._coerceSkuKey(record['sku'] ?? fallbackSku);
    const cartCode = this._coerceAddToCartString(record['cartCode'] ?? record['cart_code']);
    if (!sku || !cartCode) return null;
    return { sku, cartCode, quantity: 1 };
  }

  private _hasAddToCartConfirmationHandler(): boolean {
    return this.config.onAddToCart !== undefined || (this._eventCallbacks.get('gengage-cart-add')?.size ?? 0) > 0;
  }

  private _maybeHidePreservedMobilePanel(actionType: string, receivedPanelContent: boolean): void {
    if (!this._isMobileViewport || actionType !== 'addToCart' || receivedPanelContent) return;
    if (!this._drawer?.hasPanelContent()) return;
    this._drawer.hideMobilePanel();
  }

  private _addToCartRequestKey(params: AddToCartParams): string {
    return `${params.sku}\u0000${params.cartCode}\u0000${params.quantity}`;
  }

  private async _runChatAddToCartFlow(params: AddToCartParams): Promise<void> {
    const requestKey = this._addToCartRequestKey(params);
    if (this._pendingAddToCartKeys.has(requestKey)) return;
    this._pendingAddToCartKeys.add(requestKey);

    const detail = {
      ...params,
      sessionId: this.config.session?.sessionId ?? null,
    };

    try {
      if (!this._hasAddToCartConfirmationHandler()) {
        this._handleCallbackFailure('gengage-cart-add', detail as unknown as Record<string, unknown>);
        return;
      }
      const progressToastMsg = this._i18n.addingToCartToast ?? 'Adding to cart...';
      this._drawer?.showCartToast(progressToastMsg, { tone: 'neutral', autoHideMs: null });

      let hostOutcome: ReturnType<typeof normalizeAddToCartHostResult> = 'committed';
      if (this.config.onAddToCart !== undefined) {
        try {
          const hostResult = await Promise.resolve(this.config.onAddToCart(params));
          hostOutcome = normalizeAddToCartHostResult(hostResult, params);
        } catch (err) {
          console.error('[gengage] onAddToCart', err);
          this._drawer?.hideCartToast();
          this._handleCallbackFailure('gengage-cart-add', detail as unknown as Record<string, unknown>);
          return;
        }
      }

      if (hostOutcome === 'rejected') {
        this._drawer?.hideCartToast();
        this._handleCallbackFailure('gengage-cart-add', detail as unknown as Record<string, unknown>);
        return;
      }

      if (hostOutcome === 'partial') {
        // Navigation/handoff started — do not claim cart membership or emit a receipt.
        this._drawer?.hideCartToast();
        return;
      }

      if (hostOutcome === 'uncertain') {
        // Possible effect already happened — surface recoverable error; never blind-retry.
        this._drawer?.hideCartToast();
        this._handleCallbackFailure('gengage-cart-add', detail as unknown as Record<string, unknown>);
        return;
      }

      const callbacksOk = await this._runEventCallbacks(
        'gengage-cart-add',
        detail as unknown as Record<string, unknown>,
      );
      if (!callbacksOk) {
        this._drawer?.hideCartToast();
        return;
      }

      ga.trackCartAdd(params.sku, params.quantity);
      dispatch('gengage:chat:add-to-cart', detail);
      this._bridge?.send('addToCart', params);
      // Normalized sellable identity plus a versioned host-effect receipt. The receipt is
      // acknowledgement/telemetry for receipt-capable injectors; ProcessActionRequest is unchanged.
      // Backend still keys on { sku, cart_code, quantity } (+ optional error_message).
      this._sendAction(
        {
          title: this._i18n.addToCartButton ?? 'Add to Cart',
          type: 'addToCart',
          payload: {
            sku: params.sku,
            cart_code: params.cartCode,
            quantity: params.quantity,
            ...(this.config.hostEffectReceiptVersion === 1
              ? { host_effect_receipt: buildCommittedHostEffectReceipt(params) }
              : {}),
          },
        },
        { preservePanel: true },
      );
      if (this._isMobileViewport) {
        this._localPanelHistory = [];
        this._drawer?.hideMobilePanel();
      }
      const toastMsg = this._i18n.addedToCartToast ?? 'Added to cart';
      this._drawer?.showCartToast(toastMsg, { tone: 'success' });
      this._drawer?.flashCartBadge();
    } finally {
      this._pendingAddToCartKeys.delete(requestKey);
    }
  }

  /**
   * Build a ChatUISpecRenderContext with all callbacks wired up.
   * Used both during streaming and during session restore.
   */
  private _buildRenderContext(): ChatUISpecRenderContext {
    const ctx: ChatUISpecRenderContext = {
      locale: resolveLocaleTag(this.config.locale),
      onAction: (action) => {
        if (action.type === FRONTEND_PANEL_RESTORE_ACTION) {
          this._restorePanelFromFrontendMessage(action.payload);
          return;
        }
        ga.trackSuggestedQuestion(action.title, action.type);
        if (action.type === 'addToCart') {
          const addParams = this._parseAddToCartActionPayload(action.payload);
          if (addParams) {
            void this._runChatAddToCartFlow(addParams);
            return; // handled — skip the generic _sendAction fallthrough below
          }
          // Parse failed (missing sku/cartCode): fall through to _sendAction as a best-effort
        }
        if (action.type === 'launchSingleProduct') {
          const rawSku =
            typeof action.payload === 'object' && action.payload !== null && 'sku' in action.payload
              ? (action.payload as Record<string, unknown>).sku
              : undefined;
          const sku = this._coerceSkuKey(rawSku);
          const activeSku = this._activeSkuForProductSummaryClick();
          if (sku && activeSku && sku === activeSku) {
            return;
          }
          if (sku) ga.trackProductDetail(sku, action.title);
        }
        if (action.type === 'findSimilar') {
          const sku =
            typeof action.payload === 'object' && action.payload !== null && 'sku' in action.payload
              ? String((action.payload as Record<string, unknown>).sku)
              : '';
          ga.trackFindSimilars(sku);
        }
        if (action.type === 'getComparisonTable') {
          const raw = action.payload;
          const rec = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
          const src = rec && typeof rec['gengage_analytics_source'] === 'string' ? rec['gengage_analytics_source'] : '';
          let skuList: string[] = [];
          if (rec && Array.isArray(rec['sku_list'])) {
            skuList = rec['sku_list'].filter((x): x is string => typeof x === 'string');
          }
          if (src === 'floating_compare_dock') {
            ga.trackCompareProduct(skuList);
          } else {
            ga.trackCompareSelected(skuList.length > 0 ? skuList : this._comparisonSelectedSkus);
          }
        }
        // addToCart/like actions should preserve the current panel (product cards stay visible)
        const preservePanel = action.type === 'addToCart' || action.type === 'like';
        this._sendAction(action, preservePanel ? { preservePanel: true } : undefined);
      },
      onProductClick: (params) => {
        this._handleProductNavClick(params);
      },
      onAddToCart: (params) => {
        void this._runChatAddToCartFlow(params);
      },
      onProductSelect: (product) => {
        // No-op when the shopper is already on this PDP (panel and/or host PDP context):
        // no extra summary bubble, no panel churn, and no follow-on launch traffic.
        const newSku = this._productSkuKey(product as Record<string, unknown>);
        const activeSku = this._activeSkuForProductSummaryClick();
        if (newSku && activeSku && newSku === activeSku) {
          return;
        }
        this._currentInlineProductSummaryRecord = product as Record<string, unknown>;
        // Save current panel source to local history so back button can re-render it
        if (this._currentPanelSource) {
          const currentTitle = this._drawer?.getPanelTopBarTitle() ?? '';
          this._localPanelHistory.push({ source: this._currentPanelSource, title: currentTitle });
          if (this._localPanelHistory.length > GengageChat._MAX_PANEL_HISTORY) this._localPanelHistory.shift();
        }
        const summaryCtx = this._buildRenderContext();
        const summarySpec: import('../common/types.js').UISpec = {
          root: 'root',
          elements: {
            root: { type: 'ProductSummaryCard', props: { product } },
          },
        };
        const messagesContainer = this._shadow?.querySelector('.gengage-chat-messages');
        if (messagesContainer) {
          const summaryEl = this._renderUISpec(summarySpec, summaryCtx);
          if (this._currentThreadId) {
            summaryEl.dataset['threadId'] = this._currentThreadId;
          }
          messagesContainer.appendChild(summaryEl);
          this._scrollInlineIntoView(summaryEl, this._currentThreadId);
          this._drawer?.refreshPresentationCollapsed();
        }
        if (this.config.productDetailsExtended !== true) {
          this._clearAssistantPanelLikeStreamClearPanel();
          return;
        }
        const detailSpec: import('../common/types.js').UISpec = {
          root: 'root',
          elements: {
            root: {
              type: 'ProductDetailsPanel',
              props: { product },
            },
          },
        };
        this._drawer?.setPanelContent(this._renderUISpec(detailSpec, ctx));
        this._currentPanelSource = { kind: 'spec', spec: detailSpec };
        if (this._panel) this._panel.currentType = 'ProductDetailsPanel';
        this._drawer?.updatePanelTopBar(true, false, this._i18n.panelTitleProductDetails);
      },
      i18n: this._i18n,
      pricing: this.config.pricing,
      productPriceUi: this.config.productPriceUi,
      hideProductDiscountBadge: this.config.hideProductDiscountBadge,
      hideUserReviews: this.config.hideUserReviews,
      hideStockStatus: this.config.hideStockStatus,
      productSort: this._productSort,
      onSortChange: (sort) => {
        this._productSort = sort;
      },
      comparisonSelectMode: this._comparisonSelectMode,
      comparisonSelectedSkus: this._comparisonSelectedSkus,
      comparisonMaxSelection: GengageChat._MAX_COMPARISON_SELECTION,
      comparisonSelectionWarning: this._comparisonSelectionWarning,
      onToggleComparisonSku: (sku) => {
        this._toggleComparisonSku(sku);
      },
      favoritedSkus: this._session?.favoritedSkus ?? new Set(),
      onFavoriteToggle: (sku, product) => {
        void this._toggleProductFavorite(sku, product);
      },
      isMobile: this._isMobileViewport,
    };
    return ctx;
  }

  private async _toggleFavorite(sku: string, product: Record<string, unknown>): Promise<void> {
    if (!this._session) return;
    const userId = this.config.session?.userId ?? '';
    const appId = this.config.accountId;
    await this._session.toggleFavorite(userId, appId, sku, product);
    this._drawer?.updateFavoritesBadge(this._session.favoritedSkus.size);
  }

  /** Revert optimistic heart UI after a failed host favorite callback. */
  private _revertFavoriteHeartUi(sku: string): void {
    const btns = this._shadow?.querySelectorAll(`[data-gengage-favorite-sku="${escapeCssIdentifier(sku)}"]`);
    if (!btns?.length) return;
    for (const btn of btns) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.classList.toggle('gengage-chat-favorite-btn--active');
      const svg = btn.querySelector('svg');
      if (svg) {
        svg.setAttribute('fill', btn.classList.contains('gengage-chat-favorite-btn--active') ? 'currentColor' : 'none');
      }
    }
  }

  /**
   * Product-card favorite: dispatches window + bridge for the host, then runs `addCallback('gengage-product-favorite')`
   * handlers when registered. If none are registered, falls back to IDB favorites + optional `like` backend action.
   */
  private async _toggleProductFavorite(sku: string, product: Record<string, unknown>): Promise<void> {
    const wasLiked = this._session?.favoritedSkus.has(sku) ?? false;
    const favorited = !wasLiked;
    const detail = {
      sku,
      product,
      favorited,
      sessionId: this.config.session?.sessionId ?? null,
    };

    dispatch('gengage:chat:product-favorite', detail);
    this._bridge?.send('productFavorite', detail);

    const callbacks = this._eventCallbacks.get('gengage-product-favorite');
    if (callbacks && callbacks.size > 0) {
      for (const cb of callbacks) {
        try {
          const result = cb(detail);
          const success = result instanceof Promise ? await result : result;
          if (success === false) {
            this._revertFavoriteHeartUi(sku);
            this._handleCallbackFailure('gengage-product-favorite', detail);
            return;
          }
        } catch {
          this._revertFavoriteHeartUi(sku);
          this._handleCallbackFailure('gengage-product-favorite', detail);
          return;
        }
      }
      // All callbacks succeeded — sync in-memory state so the next click reads the correct direction.
      // IDB is intentionally skipped: the host owns persistence in callback mode.
      if (favorited) ga.trackLikeProduct(sku);
      if (this._session) {
        if (favorited) {
          this._session.favoritedSkus.add(sku);
        } else {
          this._session.favoritedSkus.delete(sku);
        }
        this._drawer?.updateFavoritesBadge(this._session.favoritedSkus.size);
      }
      return;
    }

    await this._toggleFavorite(sku, product);
    if (favorited) {
      ga.trackLikeProduct(sku);
      const productName = (product['name'] as string | undefined) ?? sku;
      this._sendAction(
        {
          title: productName,
          type: 'like',
          payload: { sku },
        },
        { preservePanel: true },
      );
    }
  }

  private _openFavoritesPanel(): void {
    if (!this._drawer) return;

    // Save current panel source to local history so back button can re-render it
    if (this._currentPanelSource) {
      const currentTitle = this._drawer.getPanelTopBarTitle() ?? '';
      this._localPanelHistory.push({ source: this._currentPanelSource, title: currentTitle });
      if (this._localPanelHistory.length > GengageChat._MAX_PANEL_HISTORY) this._localPanelHistory.shift();
    }

    this._drawer.setPanelContent(this._buildFavoritesPageEl());
    this._currentPanelSource = { kind: 'favorites' };
    this._drawer.updatePanelTopBar(true, false, this._i18n.favoritesPageTitle);
  }

  private _buildFavoritesPageEl(): HTMLElement {
    const favorites = this._session?.getFavoriteProducts() ?? [];

    if (favorites.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'gengage-chat-favorites-empty';

      const icon = document.createElement('div');
      icon.className = 'gengage-chat-favorites-empty-icon';
      icon.innerHTML = `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
      empty.appendChild(icon);

      const text = document.createElement('p');
      text.textContent = this._i18n.emptyFavoritesMessage;
      empty.appendChild(text);

      return empty;
    }

    // Convert favorites to product records and render as ProductGrid UISpec
    const elements: import('../common/types.js').UISpec['elements'] = {};
    const childKeys: string[] = [];

    for (const [i, fav] of favorites.entries()) {
      const key = `card_${i}`;
      childKeys.push(key);
      elements[key] = {
        type: 'ProductCard',
        props: {
          product: {
            sku: fav.sku,
            name: fav.name,
            imageUrl: fav.imageUrl,
            price: fav.price,
          } as Record<string, unknown>,
        },
      };
    }

    elements['grid'] = { type: 'ProductGrid', children: childKeys };

    const spec: import('../common/types.js').UISpec = { root: 'grid', elements };
    return this._renderUISpec(spec, this._buildRenderContext());
  }

  private _isMessageHostedUISpec(spec: UISpec | undefined): boolean {
    if (!spec) return false;
    const root = spec.elements[spec.root];
    return !!root && MESSAGE_UI_SPEC_COMPONENTS.has(root.type);
  }

  private _isPanelAiSpec(spec: UISpec | undefined): boolean {
    if (!spec) return false;
    const root = spec.elements[spec.root];
    return root?.type === 'AITopPicks' || root?.type === 'AIGroupingCards';
  }

  private _rememberPanelAiSpec(threadId: string, spec: UISpec): void {
    if (this._isPanelAiSpec(spec)) {
      this._panelAiSpecByThread.set(threadId, spec);
    }
  }

  private _panelThreadIdByMessageId(
    messages: Array<{ id: string; threadId?: string | undefined }>,
  ): Map<string, string> {
    const threadIds = new Map<string, string>();
    for (const message of messages) {
      if (message.threadId) threadIds.set(message.id, message.threadId);
    }
    return threadIds;
  }

  private _restorePanelSnapshotSources(
    panelSnapshotSources: Record<string, PanelSource> | undefined,
    threadIdByMessageId: ReadonlyMap<string, string>,
  ): void {
    if (!panelSnapshotSources || !this._panel) return;
    for (const [messageId, source] of Object.entries(panelSnapshotSources)) {
      const threadId = threadIdByMessageId.get(messageId);
      this._panel.registerSnapshotSource(messageId, {
        source,
        threadId,
        rebuild: () => {
          const ctx = this._buildRenderContext();
          return this._renderPanelFromSource(source, ctx);
        },
      });
    }
  }

  private _restorePanelAiSpecMap(panelAiSpecByThread: Record<string, UISpec>): void {
    for (const [threadId, spec] of Object.entries(panelAiSpecByThread)) {
      this._rememberPanelAiSpec(threadId, spec);
    }
  }

  private _hydrateRestoredUISpecMetadata(messageId: string, threadId: string, spec: UISpec): boolean {
    const isMessageHostedUISpec = this._isMessageHostedUISpec(spec);
    this._rememberPanelAiSpec(threadId, spec);
    const root = spec.elements[spec.root];
    if (root && !isMessageHostedUISpec && !this._panel?.snapshotTypes.has(messageId)) {
      this._panel?.snapshotTypes.set(messageId, root.type);
    }
    return isMessageHostedUISpec;
  }

  private _renderMessageUISpec(message: ChatMessage): HTMLElement | null {
    if (!message.uiSpec || !this._isMessageHostedUISpec(message.uiSpec)) {
      return null;
    }
    return this._renderUISpec(message.uiSpec, this._buildRenderContext());
  }

  private _maybeAddPanelRestoreMessage(botMsg: ChatMessage): void {
    if (!this._panel || !botMsg.threadId) return;
    if (this._panel.snapshotTypes.get(botMsg.id) !== 'ProductGrid') return;

    const source = this._currentPanelSource;
    if (source?.kind !== 'spec') return;
    const root = source.spec.elements[source.spec.root];
    if (!root || root.type !== 'ProductGrid' || isSimilarsAppendGrid(root)) return;

    const previews = (root.children ?? [])
      .map((childId) => {
        const product = source.spec.elements[childId]?.props?.['product'] as Record<string, unknown> | undefined;
        if (!product) return null;
        return {
          imageUrl: typeof product['imageUrl'] === 'string' ? product['imageUrl'] : undefined,
          name: typeof product['name'] === 'string' ? product['name'] : undefined,
        };
      })
      .filter((entry): entry is { imageUrl: string | undefined; name: string | undefined } => entry !== null)
      .slice(0, 3);
    if (previews.length === 0) return;

    const backendTitle = typeof root.props?.['panelTitle'] === 'string' ? root.props['panelTitle'] : undefined;
    const title = this._panel.titleForComponent('ProductGrid', backendTitle);
    const restoreMsg = this._createMessage('assistant', '');
    restoreMsg.threadId = botMsg.threadId;
    restoreMsg.frontendOnly = true;
    restoreMsg.uiSpec = {
      root: 'root',
      elements: {
        root: {
          type: 'PanelRestoreCard',
          props: {
            title,
            showTitle: false,
            ctaLabel: this._i18n.showPanelAriaLabel ?? 'Show Results',
            panelMessageId: restoreMsg.id,
            highlightMessageId: restoreMsg.id,
            threadId: botMsg.threadId,
            products: previews,
          },
        },
      },
    };
    this._messages.push(restoreMsg);
    const frozenSource = this._clonePanelSourceForHistory(source);
    this._panel.snapshotForMessage(restoreMsg.id, {
      source: frozenSource,
      threadId: restoreMsg.threadId,
      rebuild: () => {
        const ctx = this._buildRenderContext();
        return this._renderPanelFromSource(frozenSource, ctx);
      },
    });
    this._ensureAssistantMessageRendered(restoreMsg);
  }

  private _restorePanelFromFrontendMessage(payload: unknown): void {
    const rec = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
    if (!rec) return;
    const panelMessageId = typeof rec['panelMessageId'] === 'string' ? rec['panelMessageId'] : '';
    const highlightMessageId =
      typeof rec['highlightMessageId'] === 'string' ? rec['highlightMessageId'] : panelMessageId;
    const threadId = typeof rec['threadId'] === 'string' ? rec['threadId'] : '';
    if (!panelMessageId) return;

    const restore = (): void => {
      const snapshotType = this._panel?.snapshotTypes.get(panelMessageId);
      if (snapshotType === 'ProductGrid' || snapshotType === 'ComparisonTable') {
        this._ensureMobilePanelOverlayVisible();
      }
      const restored = this._panel?.restoreForMessage(panelMessageId, highlightMessageId) ?? false;
      if (restored) {
        const restoredMessage = this._messages.find((message) => message.id === panelMessageId);
        this._currentPanelSource =
          this._panel?.sourceForMessage(panelMessageId) ?? this._panelSourceFromMessage(restoredMessage);
        const panelType = this._panel?.currentType ?? '';
        this._panel?.updateTopBar(panelType);
        if (threadId) {
          this._restorePanelAiZoneForThread(threadId);
        }
      }
    };

    if (threadId && this._currentThreadId !== threadId) {
      this._rollbackToThread(threadId);
      requestAnimationFrame(restore);
      return;
    }

    restore();
  }

  private _restorePanelAiZoneForThread(threadId: string): void {
    const aiSpec = this._panelAiSpecByThread.get(threadId);
    if (!aiSpec || !this._drawer) {
      this._drawer?.setPanelAiZoneState('hidden');
      return;
    }
    const aiEl = this._renderUISpec(aiSpec, this._buildRenderContext());
    this._drawer.setPanelAiZoneState('results', { resultEl: aiEl });
  }

  /**
   * Run registered callbacks for a GA4 event.
   * If any callback returns false or throws, handle the failure (e.g. show error for cart-add).
   */
  private async _runEventCallbacks(eventName: string, detail: Record<string, unknown>): Promise<boolean> {
    const callbacks = this._eventCallbacks.get(eventName);
    if (!callbacks || callbacks.size === 0) return true;

    for (const cb of callbacks) {
      try {
        const success = await Promise.resolve(cb(detail));
        if (success === false) {
          this._handleCallbackFailure(eventName, detail);
          return false;
        }
      } catch {
        this._handleCallbackFailure(eventName, detail);
        return false;
      }
    }
    return true;
  }

  /**
   * Handle a callback failure — for add-to-cart, show an error message in chat.
   */
  private _handleCallbackFailure(eventName: string, _detail: Record<string, unknown>): void {
    if (eventName === 'gengage-cart-add') {
      const errorText = this._i18n.cartAddErrorMessage;
      const botMsg = this._createMessage('assistant', errorText);
      if (this._currentThreadId) botMsg.threadId = this._currentThreadId;
      this._messages.push(botMsg);
      this._drawer?.addMessage(botMsg);
      // _sendAction is NOT called here: _runChatAddToCartFlow only sends the backend
      // addToCart action after host confirmation, so on failure there is nothing to roll back.
      this._persistConversationState();
    }
    if (eventName === 'gengage-product-favorite') {
      const errorText = this._i18n.favoriteToggleErrorMessage;
      const botMsg = this._createMessage('assistant', errorText);
      if (this._currentThreadId) botMsg.threadId = this._currentThreadId;
      this._messages.push(botMsg);
      this._drawer?.addMessage(botMsg);
      this._persistConversationState();
    }
  }

  /**
   * Re-render inline UISpec elements for a restored bot message.
   * Inserts them into the messages container after the message bubble.
   */
  private _restoreInlineUISpec(chatMsg: ChatMessage): void {
    if (!chatMsg.uiSpec || !this._drawer) return;
    const spec = chatMsg.uiSpec;
    const rootElement = spec.elements[spec.root];
    if (!rootElement) return;

    const componentType = rootElement.type;

    // ActionButtons are rendered as pills/chips, not inline
    if (componentType === 'ActionButtons') return;

    // Panel-only components should not be rendered inline.
    // Note: panelHint is a StreamEvent property not stored in UISpec elements,
    // so we identify panel-only status by component type.
    // ProductDetailsPanel is panel-only but gets a compact ProductSummaryCard below.
    // ComparisonTable is always panel-only.
    // ProductGrid with similarsAppend is panel-only (matches desktop + mobile behavior).
    if (componentType === 'ComparisonTable') return;
    if (componentType === 'ProductGrid' && this._panel?.snapshotTypes.get(chatMsg.id) === 'ProductGrid') return;
    if (isSimilarsAppendGrid(rootElement)) return;

    const renderContext = this._buildRenderContext();
    const messagesContainer = this._shadow?.querySelector('.gengage-chat-messages');
    if (!messagesContainer) return;

    // ProductDetailsPanel: synthesize a compact ProductSummaryCard for inline rendering
    if (componentType === 'ProductDetailsPanel') {
      const product = rootElement.props?.['product'] as Record<string, unknown> | undefined;
      if (!product) return;
      this._currentInlineProductSummaryRecord = product;
      const inlineSpec: UISpec = {
        root: 'root',
        elements: { root: { type: 'ProductSummaryCard', props: { product } } },
      };
      const inline = this._renderUISpec(inlineSpec, renderContext);
      if (chatMsg.threadId) inline.dataset['threadId'] = chatMsg.threadId;
      messagesContainer.appendChild(inline);
      this._drawer?.refreshPresentationCollapsed();
      return;
    }

    const inline = this._renderUISpec(spec, renderContext);
    if (chatMsg.threadId) {
      inline.dataset['threadId'] = chatMsg.threadId;
    }
    messagesContainer.appendChild(inline);
    this._drawer?.refreshPresentationCollapsed();
  }

  private _createMessage(role: 'user' | 'assistant', content: string): ChatMessage {
    this._currentMessageId++;
    return {
      id: `msg-${this._currentMessageId}`,
      role,
      content,
      timestamp: Date.now(),
      status: 'done',
    };
  }

  private _resolveI18n(config: ChatWidgetConfig): ChatI18n {
    const base = resolveChatLocale(config.locale);
    return { ...base, ...config.i18n };
  }

  private _resolveUISpecRegistry(): ChatUISpecRegistry {
    const baseRegistry = createDefaultChatUISpecRegistry();
    return mergeUISpecRegistry(baseRegistry, this.config.renderer?.registry);
  }

  private _renderUISpec(spec: UISpec, context: ChatUISpecRenderContext): HTMLElement {
    const root = spec.elements[spec.root];
    if (root?.type === 'ProductSummaryCard') {
      const props = asRecord(root.props);
      const product = asRecord(props?.['product']) ?? props;
      if (product) {
        this._currentInlineProductSummaryRecord = product;
      }
    }
    const registry = this._resolveUISpecRegistry();
    const unknownRenderer = this.config.renderer?.unknownRenderer ?? defaultChatUnknownUISpecRenderer;
    const defaultRender = (inputSpec: UISpec, inputContext: ChatUISpecRenderContext) =>
      renderUISpec(inputSpec, inputContext, registry, unknownRenderer);

    const override = this.config.renderer?.renderUISpec;
    if (!override) return defaultRender(spec, context);

    const helpers: UISpecRenderHelpers<ChatUISpecRenderContext> = {
      registry,
      unknownRenderer,
      defaultRender,
    };
    return override(spec, context, helpers);
  }
}

// ---------------------------------------------------------------------------
// Convenience factory
// ---------------------------------------------------------------------------

export function createChatWidget(): GengageChat {
  return new GengageChat();
}

export type {
  ChatFooterAttribution,
  ChatWidgetConfig,
  ChatMessage,
  ChatSession,
  ChatUIComponents,
  ChatI18n,
  ChatRendererConfig,
  ChatUISpecRenderContext,
  ChatUISpecRegistry,
  ProductSortState,
  SerializableChatMessage,
} from './types.js';
export {
  renderUISpec,
  createDefaultChatUISpecRegistry,
  defaultChatUnknownUISpecRenderer,
} from './components/renderUISpec.js';
export type { UISpecRenderContext } from './components/renderUISpec.js';
export {
  getChatScrollElement,
  invalidateChatScrollCache,
  CHAT_SCROLL_ELEMENT_ID,
} from './utils/get-chat-scroll-element.js';
export { ChatPresentationState } from './chat-presentation-state.js';
export type { GroupReadState, PresentationGroupMeta, ScrollRequest } from './chat-presentation-state.js';
